import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FolderKanban,
  Lightbulb,
  CheckCircle2,
  Check,
  Clock,
  Plus,
  Search,
  Download,
  Trash2,
  Edit3,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Package,
  Layers,
  Cpu,
  Wrench,
  Activity,
  CheckSquare,
  AlertTriangle,
  FileSpreadsheet,
  Printer,
  Compass,
  Zap,
  ShieldCheck,
  Building2,
  DollarSign,
  Share2,
  ArrowRight,
  BookOpen,
  Film,
  Wind,
  Bookmark,
  RotateCcw,
  Brain,
  HelpCircle,
  MessageSquare,
  Box,
  Copy,
  Camera,
  ShoppingCart,
  CheckCheck,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TabsSlider } from "@/components/ui/tabs-slider";
import { PrototypeCinematicStudio } from "./PrototypeCinematicStudio";
import { InteractivePinoutDiagram } from "./InteractivePinoutDiagram";
import { getProjectSubsystems3D } from "@/lib/projectSubsystems3d";

export interface BOMItem {
  id: string;
  name: string;
  category: "3d-print" | "electronics" | "hardware" | "raw-material" | "chemical";
  quantity: number;
  unit: string;
  specs?: string;
  estimatedPriceUSD: number;
  actualPriceUSD?: number;
  inStock: boolean;
  localSupplierPR: string; // "National Lumber", "Home Depot PR", "Ace Hardware", "Mouser / USPS", "Amazon / USPS", "Maderas 3C"
  notes?: string;
}

export interface PhaseStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  criticalPoints?: string;
  starterKit?: string; // Herramienta o insumo requerido
  actionFirst?: string; // Primera accion fisica o paso concreto
  doneCriteria?: string; // Criterio de exito: como sabes que terminaste
}

export interface ProjectPhase {
  name: string;
  label: string;
  steps: PhaseStep[];
  notes?: string;
}

export interface ValidationTest {
  id: string;
  name: string;
  targetCriteria: string;
  measuredValue?: string;
  status: "passed" | "failed" | "pending";
  notes?: string;
}

export interface TrajectoryTimeline {
  printHours: number;
  uspsShippingDays: number;
  assemblyHours: number;
  targetLaunchDate: string;
}

export interface EngineeringChangeOrder {
  rev: string;
  date: string;
  reason: string;
  changeSummary: string;
  outcome: string;
}

export interface SubsystemStructure {
  mechanical: string[];
  electronic: string[];
  firmware: string[];
  resilience: string[];
}

export interface EngineeringProject {
  id: string;
  title: string;
  discipline: "Mecánica & CAD" | "Electrónica & IoT" | "Energía & Solar" | "Química & Materiales" | "Robótica" | "Estructuras & Resiliencia PR";
  ecosystem?: "aura-os" | "prometheus-lab";
  status: "in-progress" | "idea-future" | "completed";
  priority: "Alta" | "Media" | "Baja";
  difficulty: "Fácil" | "Medio" | "Avanzado" | "Industrial";
  targetCompletionDate?: string;
  description: string;
  problemStatement: string;
  scientificPrinciple: string;
  estimatedBudgetUSD: number;
  actualCostUSD: number;
  filamentGramsPLA?: number;
  filamentGramsPETG?: number;
  prResilienceNotes: string;
  bom: BOMItem[];
  subsystems?: SubsystemStructure;
  validationTests?: ValidationTest[];
  trajectory?: TrajectoryTimeline;
  revisions?: EngineeringChangeOrder[];
  phases: {
    preparation: ProjectPhase;
    simulationCad: ProjectPhase;
    inventoryProcurement: ProjectPhase;
    construction: ProjectPhase;
    testingCalibration: ProjectPhase;
    analysisReview: ProjectPhase;
  };
  keyResults?: string;
  lessonsLearned?: string[];
  cadToolLink?: string;
  logs?: Array<{
    id: string;
    timestamp: string;
    title: string;
    content: string;
    category: "progreso" | "problema" | "medicion" | "leccion";
  }>;
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_PROJECTS: EngineeringProject[] = [
  // =========================================================================
  // 📐 PROYECTO 01: MESA EN L COMPLETA (72"x72"x28") — HTX STUDIO + ENGRANAJES 24V
  // =========================================================================
  {
    id: "proj-mesa-l-htx",
    title: "Mesa en L Completa (72\" x 72\" x 28\") — Edición HTX Studio + Engranajes 24V",
    discipline: "Mecánica & CAD",
    ecosystem: "prometheus-lab",
    status: "in-progress",
    priority: "Alta",
    difficulty: "Avanzado",
    targetCompletionDate: "2026-11-20",
    description: "Estación de trabajo esquinera en L de 72\" × 72\" × 28\" sincronizada en Autodesk Fusion 360, Blender 5.2 y Visor 3D. Compuesta por Cubierta 1 Izquierda (72\" × 28\" × 1.5\") y Cubierta 2 Retorno Derecha (44\" × 28\" × 1.5\" con ventana CNC de 15\" × 22\" para el cargador móvil CoreXY Qi2 estilo HTX Studio), chasis de acero PTR 2.0\" × 1.5\" con esquina interior libre de patas a 45°, 6 patas telescópicas y Sistema Opcional Carpeta 05 de 6 Cajas de Engranajes Cónicos a 90° + Husillos Trapezoidales Autoblocantes ACME TR16x4 + Tuercas Bridadas de Bronce + Motores 24V.",
    problemStatement: "Construir una mesa en L desmontable (Flat-Pack) de 72\" × 72\" que aproveche toda la esquina sin tener una pata frontal que estorbe las rodillas, incorpore seguimiento inalámbrico de celular CoreXY bajo cristal templado ahumado en el retorno derecho (44\" × 28\") y pueda elevarse mecánicamente con husillos autoblocantes ACME TR16x4 y engranajes cónicos a 90°.",
    scientificPrinciple: "Deflexión de vigas de Euler-Bernoulli ($\\delta = \\frac{F \\cdot L^3}{48 \\cdot E \\cdot I} < \\frac{L}{600}$), Autobloqueo de Husillo Trapezoidal ACME TR16x4 ($\\alpha < \\phi_s$, imposible que baje sola por gravedad), Cinemática Diferencial CoreXY ($\\Delta X = \\frac{1}{2}(\\Delta A + \\Delta B), \\Delta Y = \\frac{1}{2}(\\Delta A - \\Delta B)$) y Acoplamiento Inductivo Qi2 a través de dieléctrico de $4.5\\text{ mm}$.",
    estimatedBudgetUSD: 685.00,
    actualCostUSD: 0.00,
    filamentGramsPLA: 650,
    filamentGramsPETG: 1200,
    prResilienceNotes: "Acero PTR tratado con primario anticorrosivo epóxico + esmalte negro mate contra humedad tropical de PR, madera sellada por las 6 caras con poliuretano marino para evitar pandeo higroscópico y electrónica a 24V DC compatible con estación EcoFlow / LiFePO4 ante apagones de LUMA.",
    bom: [
      { id: "ml-b1", name: "Cubierta 1 Principal (72\" × 28\" × 1.5\") + Cubierta 2 Retorno (44\" × 28\" × 1.5\") Butcher Block", category: "raw-material", quantity: 2, unit: "tableros", specs: "Madera sólida de 1.50 in (38.1 mm) de espesor; el retorno derecho de 44\"×28\" lleva el vaciado CNC de 15\"×22\"", estimatedPriceUSD: 260.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Home Depot PR / National Lumber / Maderas 3C", notes: "Unión a ras con 4 pernos guía (dowels) Ø 3/8\" y 3 placas inferiores con insertos M8" },
      { id: "ml-b2", name: "Tubería Acero PTR 2.0\"×1.5\" (Chasis) + PTR 2.25\" y 1.75\" (6 Patas Telescópicas)", category: "raw-material", quantity: 4, unit: "tramos 20ft", specs: "Acero ASTM A500 Calibre 11 (Pared 1/8\" / 3.18 mm) + 12 Bridas 4.5\"×4.5\"×1/4\"", estimatedPriceUSD: 145.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Aceros de América PR / National Lumber", notes: "Travesaños del ala derecha ubicados en Y=34.5\" y Y=60.5\" para dejar libre la bahía CoreXY" },
      { id: "ml-b3", name: "Kit Imprimible Bambu Lab (6 Bujes PETG-HF, 12 Perillas M10, 6 Suelas TPU 95A, Marco y Carritos HTX)", category: "3d-print", quantity: 1, unit: "lote 3MF", specs: "Carpeta 04 y Carpeta 07 en Fusion 360 / Blender: PETG-HF (6 paredes, 50% Gyroid), PLA-CF y TPU 95A", estimatedPriceUSD: 45.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Tu Impresora 3D Bambu Lab (Taller Local)", notes: "Exportable con 1 clic desde Fusion 360 o Blender" },
      { id: "ml-b4", name: "[Carpeta 05 Opcional] 6× Husillos ACME TR16x4 (18.5\") + 6× Tuercas Bronce + 6× Cajas Engranajes Cónicos 90°", category: "hardware", quantity: 6, unit: "kits pata", specs: "Husillo trapezoidal Ø 16 mm paso 4 mm autoblocante + Caja cónica relación 1:1 en acero + Ejes hex 12 mm + 3 Motores 24V", estimatedPriceUSD: 140.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / McMaster-Carr / AliExpress USPS PR", notes: "Sistema opcional en Carpeta 05 para subir y bajar la mesa de 28\" a 42\"" },
      { id: "ml-b5", name: "[Carpeta 07 Opcional] Bahía HTX Studio: Cristal Templado Ahumado 15\"×22\"×4.5mm + 2 NEMA 17 + Bobina Qi2 + ESP32", category: "electronics", quantity: 1, unit: "módulo completo", specs: "3 Varillas Inox Ø 10 mm + Correas GT2 + 2 Drivers silenciosos TMC2209 + Módulo Qi2 15W + Anillo LED", estimatedPriceUSD: 95.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Cristalería Local PR + Amazon USPS", notes: "El cristal de 4.5 mm queda al ras del tablero derecho de 44\"×28\"" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Verificación del Espacio (72\"×72\") & Planos",
        steps: [
          {
            id: "ml-p1",
            title: "Confirmación de Esquina 72\" × 72\" y Orientación de Cubiertas",
            description: "Verificar que el Ala Izquierda recibe el tablero de 72\"×28\" y el Ala Derecha recibe el retorno de 44\"×28\" con el cargador inalámbrico del celular.",
            completed: true,
            starterKit: "Cinta métrica en pulgadas, Visor 3D Interactivo y archivos sincronizados de Fusion 360 / Blender 5.2.",
            actionFirst: "Mide 72 pulgadas desde la esquina hacia la izquierda (bajo el monitor) y 72 pulgadas hacia la derecha (bajo el pegboard y la ventana).",
            doneCriteria: "Dimensiones exteriores de 72\"×72\"×28\" verificadas y despeje de silla ergonómica en la esquina interior confirmado."
          },
          {
            id: "ml-p2",
            title: "Inspección de las 7 Carpetas en Fusion 360 y Blender 5.2",
            description: "Abrir el modelo paramétrico con el botón ⚡ Mis Diseños CAD en Fusion 360 o el archivo MesaEnL_72x72_Blender.blend en Blender 5.2.",
            completed: true,
            starterKit: "Autodesk Fusion 360 (con PrometheusBridge v4.0) y Blender 5.2 LTS.",
            actionFirst: "En Fusion 360 haz clic en ⚡ Mis Diseños CAD y elige 1 para ver la Mesa Completa con la Carpeta 05 (Engranajes 90° + Husillos TR16x4) y la Carpeta 07 (Bahía HTX).",
            doneCriteria: "Las 7 carpetas aparecen organizadas en el árbol de componentes tanto en Fusion 360 como en Blender."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Impresión 3D en Bambu Lab (Carpetas 04 y 07)",
        steps: [
          {
            id: "ml-s1",
            title: "Impresión de los 6 Bujes Antifricción y 12 Perillas M10 (PETG-HF)",
            description: "Imprimir los bujes guía con labio superior que van entre el tubo exterior de 2.25\" y el tubo interior de 1.75\", más las perillas estriadas.",
            completed: false,
            starterKit: "Impresora Bambu Lab + Filamento PETG-HF Negro y Naranja + Tuercas/Pernos M10.",
            actionFirst: "Configura en Bambu Studio 6 paredes perimetrales y 50% de relleno Gyroid para los 6 bujes antifricción (holgura deslizante de 0.25 mm).",
            doneCriteria: "Los 6 bujes encajan firmemente en los tubos de 2.25\" y permiten deslizar el tubo de 1.75\" sin juego lateral ni chirrido metálico."
          },
          {
            id: "ml-s2",
            title: "Impresión de 6 Suelas TPU 95A y Marco/Carritos CoreXY HTX",
            description: "Imprimir las bases amortiguadoras en TPU 95A y las piezas del módulo CoreXY (marco biselado de 15\"×22\" por secciones y carro portabobina Qi2).",
            completed: false,
            starterKit: "Filamento TPU 95A (para las 6 suelas) y PLA-CF / PETG-HF (para el marco y carritos HTX).",
            actionFirst: "Imprime las 6 suelas en TPU 95A con 4 paredes y 25% Gyroid para proteger el piso y absorber vibraciones.",
            doneCriteria: "Todas las piezas impresas de la Carpeta 04 y Carpeta 07 listas en mesa de trabajo."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Corte de Herrería PTR & Tableros de Madera",
        steps: [
          {
            id: "ml-i1",
            title: "Corte y Soldadura del Chasis PTR 2.0\"×1.5\" y 6 Patas Telescópicas",
            description: "Fabricar el bastidor superior en 2 módulos empernables con travesaños derechos en Y=34.5\" y Y=60.5\" (dejando libre la bahía de 15\"×22\").",
            completed: false,
            starterKit: "Lista_Materiales_y_BambuLab.md, perfiles PTR calibre 11 y 12 placas de brida de 4.5\"×4.5\"×1/4\".",
            actionFirst: "Verifica que entre Y=34.5\" y Y=60.5\" del ala derecha NO se suelde ningún travesaño intermedio para que el carro CoreXY tenga vía libre.",
            doneCriteria: "Chasis superior y 6 patas con bridas coaxiales soldados, perforados y pintados con anticorrosivo."
          },
          {
            id: "ml-i2",
            title: "Corte de Madera (72\"×28\" y 44\"×28\") y Vaciado CNC de Ventana HTX (15\"×22\")",
            description: "Preparar las dos cubiertas de 1.5\" de espesor, abrir la ventana de 15\"×22\" en el tablero derecho de 44\"×28\" e instalar insertos roscados M8 por debajo.",
            completed: false,
            starterKit: "Router con fresa recta, broca Forstner de 3\" para los 3 pasacables e insertos roscados M8.",
            actionFirst: "En el tablero derecho de 44\"×28\", realiza el corte de la ventana en X=[6.5\", 21.5\"], Y=[37.0\", 59.0\"] con su escalón para el marco impreso y el cristal de 4.5 mm.",
            doneCriteria: "Ambas cubiertas selladas con poliuretano y cristal templado ahumado asentado exactamente al ras."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Ensamble Flat-Pack en el Cuarto",
        steps: [
          {
            id: "ml-c1",
            title: "Empernado de las 6 Patas al Chasis Superior (24 Pernos M8)",
            description: "Unir las bridas coaxiales de las 6 patas al bastidor de acero y acoplar el módulo izquierdo con el derecho en la esquina.",
            completed: false,
            starterKit: "Llave Allen / matraca para pernos M8×25 Grado 8.8 y nivel de burbuja.",
            actionFirst: "Atornilla las 4 tuercas M8 de cada brida de pata y verifica que la viga diagonal a 45° deje la esquina interior 100% libre de patas.",
            doneCriteria: "Estructura metálica autoportante nivelada en la esquina del cuarto."
          },
          {
            id: "ml-c2",
            title: "Montaje de las Cubiertas 72\"×28\" y 44\"×28\" con Placas de Unión a Ras",
            description: "Colocar los dos tableros sobre el chasis, alinearlos con los 4 dowels internos y fijar las placas de acero inferiores con pernos M8.",
            completed: false,
            starterKit: "4 pernos guía Ø 3/8\", 3 placas planas de unión y tornillos M8.",
            actionFirst: "Une el canto del tablero de 44\"×28\" contra el lateral del tablero de 72\"×28\" en Y=28\" hasta que la unión quede imperceptible al tacto.",
            doneCriteria: "Superficie en L de 72\"×72\" sólida, sin escalones y 100% desmontable."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Instalación de Opcionales: Engranajes 24V (Carpeta 05) + CoreXY (Carpeta 07)",
        steps: [
          {
            id: "ml-t1",
            title: "[Carpeta 05] Montaje de Cajas de Engranajes Cónicos 90° y Husillos ACME TR16x4",
            description: "Instalar los 6 husillos trapezoidales TR16x4 con sus tuercas bridadas de bronce, las 6 cajas de engranajes cónicos a 90° y los ejes hexagonales de sincronización.",
            completed: false,
            starterKit: "6 husillos TR16x4, 6 tuercas de bronce, 6 cajas cónicas 90°, ejes hex de 12 mm y motores 24V.",
            actionFirst: "Fija la tuerca bridada de bronce en el tope del tubo exterior de cada pata, enrosca el husillo TR16x4 desde la caja cónica superior y acopla los ejes hexagonales.",
            doneCriteria: "Al girar los ejes hexagonales con los motores de 24V, las 6 patas suben y bajan al unísono sin desnivelarse."
          },
          {
            id: "ml-t2",
            title: "[Carpeta 07] Montaje y Calibración del Rastreador CoreXY Qi2 bajo el Cristal",
            description: "Atornillar los rieles lineales Y/X bajo la ventana de 15\"×22\" del ala derecha, tensar correas GT2 y probar el seguimiento de la bobina Qi2.",
            completed: false,
            starterKit: "Módulo CoreXY ensamblado, ESP32 con drivers TMC2209 y bobina Qi2 con anillo LED.",
            actionFirst: "Ajusta el resorte vertical del carro portabobina para que la bobina Qi2 deslice suave a 0.5 mm de la cara inferior del cristal templado de 4.5 mm.",
            doneCriteria: "Al apoyar el celular sobre cualquier punto del cristal de 15\"×22\", el carro viaja hasta él e inicia la carga inalámbrica de 15W."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Pruebas de Carga, Estabilidad y Memorias de Altura",
        steps: [
          {
            id: "ml-a1",
            title: "Prueba de Carga Estática, Cero-Holgura y Memorias M1–M4",
            description: "Programar las alturas sentado (29.5\") y de pie (40.0\") en la botonera frontal OLED y validar rigidez estructural.",
            completed: false,
            starterKit: "Cinta métrica y multímetro.",
            actionFirst: "Aprieta las perillas anti-holgura tras fijar la altura deseada o realiza un ciclo completo de subida/bajada con el kit de 24V.",
            doneCriteria: "Cero vibración en monitores y carga inalámbrica activa en el ala derecha."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/cad",
    createdAt: "2026-10-20",
    updatedAt: "2026-11-02"
  },
  // =========================================================================
  // ☕ PROYECTO 1: ESTRUCTURA & COMERCIO (ARQUITECTURA COMERCIAL RESILIENTE)
  // =========================================================================
  {
    id: "proj-coffeeshop-concreto",
    title: "Coffee Shop & Espacio Creativo (Concreto & Huracán Cat 5)",
    discipline: "Estructuras & Resiliencia PR",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Alta",
    difficulty: "Avanzado",
    targetCompletionDate: "2026-11-30",
    description: "Diseño y planificación estructural de un local comercial gastronómico (600 a 800 pies cuadrados) en hormigón armado y bloques de cemento de 8\" con varillas #4. Cumple con la normativa eólica ASCE 7 (175+ mph Categoría 5), accesibilidad universal ADA para Puerto Rico, acometida eléctrica para máquina espresso de 240V y trampa de grasa sanitaria reglamentaria.",
    problemStatement: "Construir un local comercial en Puerto Rico que resista huracanes mayores sin sufrir daños estructurales, cumpla con los permisos de OGPe (salud ambiental, accesibilidad y bomberos) y opere con continuidad eléctrica e hídrica.",
    scientificPrinciple: "Resistencia a Compresión de Hormigón Armado ($f\'c \\ge 3,000\\text{ PSI}$), Cargas Eólicas Dinámicas ASCE 7-22 ($q_z = 0.00256 \\cdot K_z \\cdot K_{zt} \\cdot K_d \\cdot K_e \\cdot V^2$) e Hidrostática Sanitaria para drenajes por gravedad (pendiente 2%).",
    estimatedBudgetUSD: 18500.00,
    actualCostUSD: 0.00,
    prResilienceNotes: "Estructura 100% de hormigón armado y bloques de 8\" con celdas rellenas de concreto. Ventanas comerciales con cortinas tipo tormenteras acordeón de aluminio y losa de techo con pendiente hacia bajantes pluviales para cero empozamiento.",
    bom: [
      { id: "cs-b1", name: "Bloques de Cemento de 8\" (8x8x16)", category: "raw-material", quantity: 950, unit: "bloques", specs: "Bloques estructurales de hormigón ASTM C90", estimatedPriceUSD: 2375.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber & Hardware / Ferreterías Ace", notes: "Aproximadamente $2.50 por bloque en ferreterías de PR" },
      { id: "cs-b2", name: "Varillas de Acero Corrugado #4 (1/2\" x 20ft)", category: "raw-material", quantity: 85, unit: "varillas", specs: "Grado 60 ASTM A615 para refuerzo vertical y vigas de corona", estimatedPriceUSD: 890.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Maderas 3C / National Lumber", notes: "Colocación vertical cada 24\" y en todas las esquinas y mochetas" },
      { id: "cs-b3", name: "Cemento Portland Gris Tipo I/II (Bolsas 47 lbs)", category: "raw-material", quantity: 120, unit: "bolsas", specs: "Mezcla para mortero de pega y hormigón de zapatas/columnas", estimatedPriceUSD: 960.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Home Depot (PR) / Ferretería de Comunidad", notes: "Disponible en almacenes de construcción locales" },
      { id: "cs-b4", name: "Trampa de Grasa Comercial de 20 GPM / 40 lbs", category: "hardware", quantity: 1, unit: "unidad", specs: "Acero al carbón o polietileno de alta densidad para fregadero de 3 compartimientos", estimatedPriceUSD: 380.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Plumbing Supply PR / Home Depot", notes: "Requisito obligatorio de Salud Ambiental y AAA para locales de café/comida" },
      { id: "cs-b5", name: "Tablero Eléctrico Principal 200A 120V/240V Monofásico", category: "electronics", quantity: 1, unit: "unidad", specs: "Centro de carga de 24 espacios NEMA 1 con interruptor principal", estimatedPriceUSD: 245.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber / Ferreterías Ace", notes: "Para alimentar cafetera comercial (30A 240V), A/C, molinos y refrigeración" },
      { id: "cs-b6", name: "Tubería Sanitaria PVC Cédula 40 de 3\" y 4\" (Tramos 10ft)", category: "hardware", quantity: 12, unit: "tubos", specs: "Para drenaje sanitario, inodoro comercial y respiraderos", estimatedPriceUSD: 215.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Home Depot PR / National Lumber" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Preparación & Normativas PR",
        steps: [
          {
            id: "cs-p1",
            title: "Medición del Solar y Retiros de Colindancia",
            description: "Medir las dimensiones del solar y validar los retiros exigidos por el reglamento conjunto de OGPe en Puerto Rico.",
            completed: false,
            starterKit: "Cinta métrica larga de 50 o 100 pies, estacas de madera, libreta de notas y cámara.",
            actionFirst: "Clava estacas en las 4 esquinas del solar, mide el frente y fondo en pies, y verifica dejar al menos 10 pies (3m) de retiro frontal de la calle y 5 pies (1.5m) de las colindancias vecinas.",
            doneCriteria: "Tienes un croquis dibujado a mano o digital con las 4 medidas reales del perímetro y los retiros despejados.",
            criticalPoints: "Construir sobre la colindancia sin permiso vecinal o invadir servidumbre pública impedirá obtener el permiso de uso en OGPe."
          },
          {
            id: "cs-p2",
            title: "Cálculo de Carga Eléctrica (LUMA / Espresso Comercial)",
            description: "Listar los equipos comerciales (cafetera espresso de 2 calderas, molinos, máquina de hielo, neveras y aire acondicionado) para calcular la acometida eléctrica necesaria.",
            completed: false,
            starterKit: "Fichas técnicas de los equipos que piensas comprar y calculadora de Watts a Amperios.",
            actionFirst: "Anota el consumo en Watts de la máquina de café (ej. 4,200W a 240V = 17.5A), del aire acondicionado inverter (ej. 16A a 240V) y las tomas generales. Suma la carga total para determinar el calibre del cable y breaker principal (150A o 200A).",
            doneCriteria: "Tienes la lista de cargas eléctricas terminada con el amperaje total y el tamaño del panel eléctrico definido.",
            criticalPoints: "Las cafeteras espresso profesionales requieren 240V bifásico estable; no pueden conectarse a enchufes convencionales de 120V de casa."
          },
          {
            id: "cs-p3",
            title: "Diseño Sanitario & Trampa de Grasa (AAA / Salud)",
            description: "Trazar el recorrido de aguas limpias y residuales, asegurando trampa de grasa para los fregaderos de lácteos y grasas de pastelería.",
            completed: false,
            starterKit: "Libreta milimetrada, lápiz y catálogo de trampas de grasa comerciales.",
            actionFirst: "Dibuja la ubicación del fregadero de 3 compartimientos y el lavamanos para empleados. Coloca la trampa de grasa de 20 GPM directamente bajo el fregadero o en el exterior antes de la conexión al alcantarillado sanitario.",
            doneCriteria: "Diagrama con la ubicación de la trampa de grasa, diámetro de tubos (3\" y 4\") y pendiente mínima del 2% hacia el registro exterior."
          },
          {
            id: "cs-p4",
            title: "Accesibilidad Universal ADA en Puerto Rico",
            description: "Garantizar rampa de entrada y baño comercial aptos para personas con diversidad funcional según ADA.",
            completed: false,
            starterKit: "Cinta de medir y nivel de mano.",
            actionFirst: "Verifica que la rampa de entrada tenga una pendiente máxima de 1 pulgada de subida por cada 12 pulgadas de largo (1:12). Diseña la puerta del baño con un ancho libre mínimo de 36 pulgadas y espacio interior para giro de silla de ruedas de 60 pulgadas de diámetro.",
            doneCriteria: "El plano arquitectónico preliminar incluye rampa 1:12 y baño con espacio libre de 60 pulgadas acotado."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Diseño 3D & Planos (FreeCAD / Blender)",
        steps: [
          {
            id: "cs-s1",
            title: "Modelado 3D de Cimientos y Muros en FreeCAD / Blender",
            description: "Construir el modelo tridimensional de la estructura con zapatas corridas y muros de bloques de 8 pulgadas.",
            completed: false,
            starterKit: "FreeCAD (Workbench BIM / Part Design) o Blender con el script del puente de Prometheus.",
            actionFirst: "Abre FreeCAD o Blender. Modela la losa de piso de 4 pulgadas de espesor y levanta los muros perimetrales a 9.5 pies de altura, dejando los huecos para la puerta de entrada de 36\" y ventanas frontales.",
            doneCriteria: "Modelo 3D visual donde se aprecian las dimensiones del local comercial a escala 1:1 en metros o pies."
          },
          {
            id: "cs-s2",
            title: "Diseño Interior de la Barra y Flujo del Barista",
            description: "Organizar la barra de servicio (punto de cobro, estación espresso, recogida) para optimizar la velocidad de atención.",
            completed: false,
            starterKit: "Blender o visor 3D de Prometheus.",
            actionFirst: "Ubica la barra frontal a 36 pulgadas de altura para clientes y la contrabarra a 34 pulgadas para el barista. Coloca la máquina de café al centro con acceso inmediato al molino, punto de agua filtrada y bote de borra (knock box).",
            doneCriteria: "Recorrido 3D interior que muestra la distribución ergonómica de mesas, sillas, vitrina y barra."
          },
          {
            id: "cs-s3",
            title: "Verificación de Carga de Viento ASCE 7 para Techo",
            description: "Calcular el refuerzo de la losa de techo para soportar la presión de succión eólica de 175+ mph.",
            completed: false,
            starterKit: "Tabla de factores ASCE 7-22 y calculadora estructural.",
            actionFirst: "Determina la presión de succión en las esquinas del techo (aproximadamente -48 a -65 psf) y calcula la separación de la varilla #4 en la losa de concreto (separación cada 6 u 8 pulgadas en ambas direcciones).",
            doneCriteria: "Especificación de acero de refuerzo para la losa de techo lista en la memoria descriptiva.",
            criticalPoints: "En techos planos tropicales, las esquinas sufren vórtices de succión extrema durante huracanes categoría 5; el acero perimetral debe anclarse a las vigas de corona."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Lista de Materiales (BOM) en Puerto Rico",
        steps: [
          {
            id: "cs-i1",
            title: "Cotización de Bloques, Varillas y Cemento",
            description: "Solicitar cotización formal de materiales de albañilería pesada en ferreterías con presencia física en la isla.",
            completed: false,
            starterKit: "Hoja de cantidades calculada (950 bloques, 85 varillas #4, 120 bolsas de cemento, arena y piedra).",
            actionFirst: "Visita o llama al mostrador de ventas de National Lumber, Maderas 3C o Home Depot local en PR. Pide cotización con entrega en camión de volteo o plataforma en el solar.",
            doneCriteria: "Tienes la cotización formal por escrito con precio total y costo de flete a tu municipio."
          },
          {
            id: "cs-i2",
            title: "Compra de Trampa de Grasa y Tuberías Sanitarias",
            description: "Adquirir los accesorios de plomería comercial y trampa de grasa certificada.",
            completed: false,
            starterKit: "Lista de diámetros de tubería PVC Cédula 40 y conexiones sanitarias.",
            actionFirst: "Revisa en ferretería local o suplidor de plomería la trampa de grasa aprobada y los tubos sanitarios con sus codos de 45° y registros de inspección (cleanouts).",
            doneCriteria: "Factura o cotización confirmada con los códigos de pieza listos para ordenar."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Construcción & Albañilería Pesada",
        steps: [
          {
            id: "cs-c1",
            title: "Excavación y Vaciado de Zapatas con Acero",
            description: "Excavar zanjas de cimentación, armar canastas de varilla de acero y vaciar hormigón de 3,000 PSI.",
            completed: false,
            starterKit: "Pala, pico, formaleta de madera, varilla corrugada #4 y concreto mezclado.",
            actionFirst: "Excava 18 pulgadas de ancho por 12 pulgadas de profundidad en suelo firme. Coloca las varillas longitudinales suspendidas sobre calzas de hormigón (no sobre tierra) y dobla varillas en L (bastones) que subirán a los muros.",
            doneCriteria: "Zapata nivelada, vaciada con concreto 3,000 PSI y curada con agua.",
            criticalPoints: "Nunca apoyar el acero de refuerzo directo sobre la tierra; debe tener mínimo 3 pulgadas de recubrimiento de concreto para evitar corrosión."
          },
          {
            id: "cs-c2",
            title: "Levantamiento de Muros y Relleno de Celdas con Varilla",
            description: "Pegar bloques de 8 pulgadas a plomo y rellenar los huecos verticales con varilla y hormigón fluido (grout).",
            completed: false,
            starterKit: "Cuchara de albañil, nivel de burbuja de 4ft, plomada, hilo y mezcla de mortero.",
            actionFirst: "Asienta la primera hilada de bloques sobre la zapata con mezcla 3:1 (arena y cemento). Coloca varillas verticales cada 2 pies (cada 3 bloques) y rellena las celdas con hormigón líquido compactado cada 4 hiladas.",
            doneCriteria: "Muros construidos a plomo hasta la viga de corona con todas las celdas de acero rellenas sólidamente."
          },
          {
            id: "cs-c3",
            title: "Instalación de Tubería Sanitaria Bajo Losa",
            description: "Colocar las tuberías sanitarias y de agua antes de vaciar la losa de piso interior.",
            completed: false,
            starterKit: "Tubería PVC Cédula 40, pegamento para PVC pesado, nivel y cinta.",
            actionFirst: "Zanjea el suelo interior del local, coloca la tubería con 2% de inclinación hacia la trampa de grasa y la salida principal, y sella las uniones con limpiador y pegamento para PVC.",
            doneCriteria: "Tuberías bajo tierra instaladas y probadas llenándolas de agua para certificar cero fugas antes de tapar."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Pruebas, Sellado de Techo & MEP",
        steps: [
          {
            id: "cs-t1",
            title: "Prueba de Estanqueidad e Impermeabilización de Techo",
            description: "Aplicar sistema de impermeabilización elastomérico o de uretano y probar que no existan filtraciones.",
            completed: false,
            starterKit: "Imprimador de techo, sellador elastomérico de uretano/silicona, rolo y manguera de agua.",
            actionFirst: "Limpia el techo de concreto con máquina de lavado a presión. Aplica el primer y dos capas cruzadas de sellador. Deja secar 48 horas y realiza una prueba inundando secciones del techo con agua por 1 hora.",
            doneCriteria: "Techo 100% impermeable verificado desde el interior sin una sola gota de humedad visible."
          },
          {
            id: "cs-t2",
            title: "Prueba Eléctrica de Voltaje y Polaridad en Tomas",
            description: "Verificar 240V en la toma del espresso comercial y 120V con polaridad correcta en tomas generales.",
            completed: false,
            starterKit: "Multímetro digital calibrado y probador de receptáculos de 3 luces.",
            actionFirst: "Mide con el multímetro entre las dos fases de la toma de 240V para confirmar 235V-245V. Inserta el probador en cada enchufe de 120V para verificar que la tierra física esté conectada y activa.",
            doneCriteria: "Todas las tomas eléctricas probadas con voltajes correctos y protección GFCI activa en zonas húmedas de lavado."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Permisos & Puesta en Marcha",
        steps: [
          {
            id: "cs-a1",
            title: "Certificación de Perito Electricista y Plomero",
            description: "Obtener las certificaciones colegiadas requeridas para el Permiso Único de OGPe.",
            completed: false,
            starterKit: "Planos construidos finales y recibos de inspección.",
            actionFirst: "Coordina la inspección final con el perito electricista colegiado y el plomero licenciado para emitir las certificaciones oficiales de obra terminada.",
            doneCriteria: "Documentos de certificación firmados y sellados listos para radicación en el portal de OGPe en PR."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/materials",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  },

  // =========================================================================
  // ⚡ PROYECTO 2: ENERGÍA & SOLAR (RESPALDO CRÍTICO LUMA 48V LIFEPO4)
  // =========================================================================
  {
    id: "proj-solar-resilient-48v",
    title: "Estación Solar Crítica LiFePO4 48V (Respaldo LUMA / Huracán)",
    discipline: "Energía & Solar",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Alta",
    difficulty: "Medio",
    targetCompletionDate: "2026-10-31",
    description: "Sistema de energía solar fotovoltaica con inversor de onda pura de 3000W-5000W, banco de baterías LiFePO4 de 48V 100Ah (5.12 kWh) y tablero de transferencia de cargas críticas. Diseñado para mantener neveras, equipos médicos, computadoras, iluminación e internet en Puerto Rico durante apagones de LUMA sin ruido ni gasolina.",
    problemStatement: "La inestabilidad de la red eléctrica en Puerto Rico daña equipos sensibles y los generadores de gasolina son ruidosos, costosos de alimentar y difíciles de mantener durante semanas tras un huracán.",
    scientificPrinciple: "Efecto Fotovoltaico, Química de Litio Hierro Fosfato ($LiFePO_4$) con más de 4,000 ciclos de vida útil, Inversión DC-AC por modulación PWM de onda senoidal pura a 60 Hz y algoritmo MPPT.",
    estimatedBudgetUSD: 1850.00,
    actualCostUSD: 0.00,
    prResilienceNotes: "Gabinete ventilado con filtro contra polvo del Sahara y humedad salina. Disyuntores térmicos de corriente continua (DC) certificados y pararrayos tipo SPD para sobretensiones de la red.",
    bom: [
      { id: "sl-b1", name: "Batería LiFePO4 de Servidor 48V 100Ah (5.12 kWh) con BMS", category: "electronics", quantity: 1, unit: "unidad", specs: "Montaje en rack, comunicación CAN/RS485, 100A descarga continua", estimatedPriceUSD: 1050.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Tiendas Solares PR / Envío Marítimo USPS", notes: "Marcas reconocidas: EG4, Ruixu, Epoch o similar" },
      { id: "sl-b2", name: "Inversor / Cargador Solar Híbrido 48V 3000W - 5000W", category: "electronics", quantity: 1, unit: "unidad", specs: "Onda senoidal pura 120V 60Hz con MPPT integrado de 80A", estimatedPriceUSD: 520.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Tiendas de Energía Solar en Bayamón / San Juan / Amazon", notes: "Tiempo de transferencia < 10ms (modo UPS sin apagar computadoras)" },
      { id: "sl-b3", name: "Paneles Solares Monocristalinos 400W (Arreglo 3x en serie)", category: "hardware", quantity: 3, unit: "paneles", specs: "Celdas bifaciales grado A resistentes a viento de 170 mph", estimatedPriceUSD: 450.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Distribuidores solares locales en PR (Home Depot / Almacenes)", notes: "Aproximadamente $150 por panel de 400W en la isla" },
      { id: "sl-b4", name: "Interruptor de Transferencia Manual / Subpanel de Cargas Críticas", category: "hardware", quantity: 1, unit: "unidad", specs: "6 a 8 circuitos de 120V para independizar cargas del panel principal", estimatedPriceUSD: 135.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber & Hardware / Home Depot PR" },
      { id: "sl-b5", name: "Cables de Batería 2/0 AWG con Terminales Estañados y Fusible Class T 125A", category: "hardware", quantity: 1, unit: "kit", specs: "Cable de cobre flexible soldadura ultra-resistente", estimatedPriceUSD: 65.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Ferretería Eléctrica Local / Amazon USPS" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Preparación & Auditoría de Cargas",
        steps: [
          {
            id: "sl-p1",
            title: "Auditoría de Consumo Eléctrico Crítico en Watts",
            description: "Medir el consumo real de los aparatos indispensables (nevera, luces, abanicos, routers y laptops).",
            completed: false,
            starterKit: "Medidor de consumo tipo Kill-A-Watt o pinza amperimétrica y libreta.",
            actionFirst: "Conecta la nevera al medidor durante 24 horas para registrar los kWh diarios (típicamente 1.2 a 1.8 kWh/día). Suma el consumo de computadoras e internet (150W continuos = 3.6 kWh/día).",
            doneCriteria: "Cálculo total de kWh diarios requeridos para dimensionar la cantidad de paneles y baterías necesarias.",
            criticalPoints: "No incluir aires acondicionados centrales ni secadoras eléctricas en el subpanel crítico de 3000W para evitar sobrecargas del inversor."
          },
          {
            id: "sl-p2",
            title: "Ubicación del Banco de Baterías e Inversor",
            description: "Elegir un lugar fresco, bajo techo y ventilado para proteger los componentes electrónicos.",
            completed: false,
            starterKit: "Cinta métrica y detector de humedad de pared.",
            actionFirst: "Selecciona una pared de concreto en el taller, marquesina techada o cuarto de lavandería, con al menos 1 metro de espacio libre alrededor para disipación de calor y lejos del salitre exterior directo.",
            doneCriteria: "Espacio despejado con pared de hormigón lista para anclar el rack de batería y el inversor."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Esquema Unifilar & Montaje",
        steps: [
          {
            id: "sl-s1",
            title: "Diseño del Diagrama Eléctrico Unifilar",
            description: "Dibujar el diagrama de bloques mostrando conexiones DC (paneles y batería), inversor y subpanel AC.",
            completed: false,
            starterKit: "Visor de diagramas de Prometheus o papel cuadriculado.",
            actionFirst: "Trazar el circuito: Paneles ➔ Desconectador DC / Fusibles ➔ Inversor MPPT. Batería ➔ Fusible Clase T 125A ➔ Inversor ➔ Subpanel de 6 breakers de 120V.",
            doneCriteria: "Diagrama unifilar claro con calibres de cable (2/0 para batería, AWG 10 para solar y AWG 10 para AC) y amperajes de fusibles señalados."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Compras & Aprovisionamiento en PR",
        steps: [
          {
            id: "sl-i1",
            title: "Compra del Inversor y Batería LiFePO4 de 48V",
            description: "Adquirir el inversor híbrido y la batería certificada UL1973/UL9540A.",
            completed: false,
            starterKit: "Presupuesto disponible y transporte adecuado para peso de batería (aprox. 100 lbs).",
            actionFirst: "Cotiza en distribuidores de energía solar en PR o coordina envío marítimo estándar por USPS a tu código postal en la isla.",
            doneCriteria: "Inversor y batería recibidos, con sellos de garantía intactos y tornillos de terminal incluidos."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Ensamble & Cableado Seguro",
        steps: [
          {
            id: "sl-c1",
            title: "Anclaje del Inversor y Montaje de Batería en Pared",
            description: "Fijar el inversor a la pared de concreto usando anclajes metálicos de expansión de 3/8 pulgada.",
            completed: false,
            starterKit: "Taladro percutor, broca de concreto 3/8\", anclajes de expansión y nivel de burbuja.",
            actionFirst: "Marca los 4 puntos del soporte del inversor, perfora 2.5 pulgadas de profundidad en el bloque de concreto, inserta los anclajes y atornilla el inversor asegurando que quede totalmente a plomo.",
            doneCriteria: "Inversor firmemente anclado a la pared capaz de soportar su peso sin vibraciones."
          },
          {
            id: "sl-c2",
            title: "Cableado de Potencia DC con Fusible Clase T",
            description: "Conectar los cables positivos y negativos de la batería al inversor con fusible de protección en el polo positivo.",
            completed: false,
            starterKit: "Llave dinamométrica o llave fija aislada, cable 2/0 AWG y fusible Class T de 125A.",
            actionFirst: "Conecta primero el cable negativo (-) de la batería al inversor. Luego instala el portafusible en la línea positiva (+), coloca el fusible de 125A y conecta al borne positivo con torque firme de 8 Nm.",
            doneCriteria: "Conexiones de batería apretadas sólidamente con cubiertas protectoras de goma aislante.",
            criticalPoints: "El litio de 48V almacena enorme energía de cortocircuito; nunca trabajar con herramientas metálicas sin aislar ni juntar los polos positivo y negativo."
          },
          {
            id: "sl-c3",
            title: "Conexión al Subpanel de Transferencia de Cargas Críticas",
            description: "Alimentar los 6 circuitos esenciales desde la salida AC del inversor.",
            completed: false,
            starterKit: "Destornillador aislado, cable THHN #10 y pinza pelacables.",
            actionFirst: "Pasa 3 cables (Fase negra, Neutro blanco y Tierra verde calibre 10) desde la salida AC del inversor hasta el subpanel de cargas críticas en tubería conduit EMT.",
            doneCriteria: "Subpanel energizado desde el inversor con sus breakers rotulados (Nevera, Luces, Router, Taller)."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Pruebas & Simulación de Apagón LUMA",
        steps: [
          {
            id: "sl-t1",
            title: "Prueba de Carga Inicial y Configuración de Parámetros BMS",
            description: "Encender el sistema y verificar que el inversor reconozca la química LiFePO4 a 48V.",
            completed: false,
            starterKit: "Pantalla del inversor o app Bluetooth del teléfono.",
            actionFirst: "Sube el disyuntor de la batería. Configura el voltaje de absorción a 56.8V y voltaje de flotación a 54.0V según manual del fabricante. Verifica que la pantalla marque 51V-53V de reposo.",
            doneCriteria: "Inversor operando con luz verde fija sin códigos de error ni alarmas sonoras."
          },
          {
            id: "sl-t2",
            title: "Simulación de Corte Eléctrico Total (Apagón Forzado)",
            description: "Bajar el breaker de entrada de la red de LUMA para verificar la transferencia instantánea.",
            completed: false,
            starterKit: "Cronómetro y observación de luces y computadoras.",
            actionFirst: "Con las computadoras y la nevera encendidas, baja el interruptor de LUMA. Comprueba que las luces no parpadeen y las computadoras sigan funcionando sin reiniciarse.",
            doneCriteria: "Transferencia instantánea exitosa en menos de 10 milisegundos sin interrupción perceptible."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Optimización & Telemetría IoT",
        steps: [
          {
            id: "sl-a1",
            title: "Configuración de Monitoreo Remoto por WiFi",
            description: "Conectar el inversor o ESP32 a la red local para ver los kWh generados y el porcentaje de batería en el teléfono.",
            completed: false,
            starterKit: "Teléfono inteligente con app de monitoreo (Solarman / Home Assistant / Prometheus).",
            actionFirst: "Escanea la red WiFi del módulo del inversor, enlaza a tu router y verifica lecturas de Watts de paneles, estado de carga (SoC%) y temperatura de batería en tiempo real.",
            doneCriteria: "Tablero de telemetría visible en el celular con historial diario de generación y consumo."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/solar",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  },

  // =========================================================================
  // 💧 PROYECTO 3: RESILIENCIA HÍDRICA (CISTERNA 600G & BOMBA AUTOMATIZADA)
  // =========================================================================
  {
    id: "proj-cisterna-presurizada-600g",
    title: "Cisterna de Agua Potable 600 Galones & Bomba Presurizadora con IoT",
    discipline: "Estructuras & Resiliencia PR",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Alta",
    difficulty: "Medio",
    targetCompletionDate: "2026-10-15",
    description: "Instalación y automatización de sistema de reserva de agua potable de 600 galones con tanque vertical de polietileno rotomoldeado, bomba presurizadora de 3/4 HP con tanque hidroneumático de diafragma, tubería PVC Cédula 40, sensor de nivel ultrasónico resistente a condensación y válvula antirretorno para abastecer automáticamente el hogar o negocio durante averías de la AAA.",
    problemStatement: "Los cortes recurrentes de la Autoridad de Acueductos y Alcantarillados (AAA) en Puerto Rico dejan a hogares y comercios sin agua potable por días, y las bombas convencionales se queman si operan en seco al vaciarse el tanque.",
    scientificPrinciple: "Ecuación de Continuidad y Bernoulli en fluidos incompresibles, Principio de Hidrostática ($P = \\rho \\cdot g \\cdot h$), Presión de Aire en Tanque de Diafragma según Ley de Boyle ($P_1 V_1 = P_2 V_2$) y Medición Ultrasónica de Tiempo de Vuelo.",
    estimatedBudgetUSD: 890.00,
    actualCostUSD: 0.00,
    prResilienceNotes: "Tanque grado alimentario certificado NSF-61 con protección contra rayos UV para evitar proliferación de algas verdes bajo el sol tropical. Tuberías PVC pintadas con esmalte blanco exterior contra fragilización solar.",
    bom: [
      { id: "cs-t1", name: "Tanque Vertical de Polietileno Virgen 600 Galones", category: "raw-material", quantity: 1, unit: "unidad", specs: "Grado alimentario NSF-61, tricapa con filtro UV, color azul o verde", estimatedPriceUSD: 520.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber / Ferreterías Ace / Maderas 3C", notes: "Disponible con entrega a domicilio en PR" },
      { id: "cs-t2", name: "Bomba Periférica / Centrífuga 3/4 HP con Controlador Electrónico de Presión", category: "hardware", quantity: 1, unit: "unidad", specs: "115V / 60Hz con sensor de flujo automático y paro en seco", estimatedPriceUSD: 145.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Home Depot (PR) / National Lumber", notes: "El press control elimina la necesidad de calibrar presostatos mecánicos viejos" },
      { id: "cs-t3", name: "Válvula Check (Antirretorno) de Bronce de 3/4\"", category: "hardware", quantity: 2, unit: "unidades", specs: "Resorte de acero inoxidable para evitar que el agua bombeada retorne a la calle", estimatedPriceUSD: 28.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Ferreterías Ace / National Lumber" },
      { id: "cs-t4", name: "Tubería y Conexiones PVC Cédula 40 de 3/4\" (Tramos 10ft)", category: "hardware", quantity: 6, unit: "tubos", specs: "Presión de trabajo hasta 480 PSI con codos, uniones universales y llaves de paso de bola", estimatedPriceUSD: 65.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Home Depot PR / Ferretería de Comunidad" },
      { id: "cs-t5", name: "Sensor Ultrasónico Impermeable JSN-SR04T + ESP32", category: "electronics", quantity: 1, unit: "kit", specs: "Transductor sellado impermeable con pantalla OLED para monitoreo de porcentaje", estimatedPriceUSD: 24.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / Digi-Key USPS" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Preparación & Plataforma de Soporte",
        steps: [
          {
            id: "wp-1",
            title: "Cálculo de Peso y Construcción de Base de Concreto",
            description: "Calcular el peso del tanque lleno (600 galones x 8.34 lbs = 5,000 lbs / 2.3 toneladas) y construir una base nivelada.",
            completed: false,
            starterKit: "Cinta métrica, nivel de mano, madera para encofrar, 4 bolsas de cemento y grava.",
            actionFirst: "Prepara una plataforma cuadrada de 5ft x 5ft de concreto de al menos 4 pulgadas de espesor con malla de alambre sobre suelo firme y compactado. Verifica con el nivel que quede 100% plana.",
            doneCriteria: "Base de hormigón fraguada y perfectamente nivelada capaz de sostener más de 5,000 lbs sin asentamientos.",
            criticalPoints: "Nunca colocar un tanque de 600 galones sobre tierra blanda o bloques sueltos; el peso de 2.5 toneladas fracturará la base del tanque."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Esquema Hidráulico & Bypass",
        steps: [
          {
            id: "ws-1",
            title: "Diseño del Sistema de Bypass de 3 Válvulas",
            description: "Trazar el circuito de tuberías que permite usar agua de la calle directamente o agua de la cisterna con bomba.",
            completed: false,
            starterKit: "Cuaderno y diagrama hidráulico.",
            actionFirst: "Diseña un arreglo de 3 llaves de paso de bola y una válvula check: Válvula 1 para alimentar cisterna desde la calle, Válvula 2 para salida de bomba a la casa, y Válvula 3 de bypass directo.",
            doneCriteria: "Esquema hidráulico listo con el sentido de flujo de cada válvula y la ubicación de las uniones universales para mantenimiento."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Adquisición de Materiales en PR",
        steps: [
          {
            id: "wi-1",
            title: "Compra del Tanque de 600 Galones y la Bomba",
            description: "Comprar el tanque rotomoldeado y la bomba presurizadora con apagado en seco.",
            completed: false,
            starterKit: "Lista de compras BOM y camión o servicio de entrega de la ferretería.",
            actionFirst: "Acude a National Lumber o ferretería Ace y ordena el tanque con flotador mecánico de 3/4\" y la bomba de 3/4 HP.",
            doneCriteria: "Tanque y bomba colocados en el área del proyecto con sus accesorios listos."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Instalación de Tuberías & Cableado",
        steps: [
          {
            id: "wc-1",
            title: "Corte, Limpieza y Pegado de Tuberías PVC Sch 40",
            description: "Ensamblar la línea de succión de la bomba con uniones universales y válvula de pie.",
            completed: false,
            starterKit: "Cortador de tubos PVC o segueta, lija fina, limpiador/primer morado y pegamento PVC gris de alta presión.",
            actionFirst: "Limpia los extremos de los tubos con el primer morado, aplica pegamento generosamente, inserta el tubo en la conexión con un cuarto de giro y mantén presionado 20 segundos sin soltar.",
            doneCriteria: "Tuberías ensambladas sólidamente con uniones universales que permiten desconectar la bomba en 2 minutos si requiere servicio.",
            criticalPoints: "Usar siempre cinta teflón en las roscas macho en el sentido de las manecillas del reloj para evitar goteos continuos."
          },
          {
            id: "wc-2",
            title: "Instalación del Sensor Ultrasónico de Nivel en la Tapa",
            description: "Perforar la tapa superior del tanque y montar la sonda ultrasónica impermeable apuntando al agua.",
            completed: false,
            starterKit: "Taladro con sierra copa de 1 pulgada, silicón RTV marino y sensor JSN-SR04T.",
            actionFirst: "Haz un orificio de 1 pulgada en la tapa del tanque, inserta el transductor sellado con junta de goma de forma perpendicular al nivel del agua y sella con silicón.",
            doneCriteria: "Sensor instalado en la parte superior sin obstrucciones internas que provoquen falsos ecos."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Cebado & Prueba de Presión",
        steps: [
          {
            id: "wt-1",
            title: "Cebado de Bomba y Prueba de Presión a 45 PSI",
            description: "Llenar la recámara de la bomba con agua para purgar el aire y verificar presión de salida.",
            completed: false,
            starterKit: "Embudo pequeño, agua limpia y manómetro.",
            actionFirst: "Retira el tapón superior de la bomba, vierte agua hasta que rebose para expulsar todo el aire, vuelve a colocar el tapón y enciende la bomba. Abre un grifo de la casa para verificar que salga aire y luego un chorro potente a 40-50 PSI.",
            doneCriteria: "La bomba se apaga automáticamente cuando cierras los grifos y arranca de inmediato al abrir una llave."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Calibración del Monitor IoT",
        steps: [
          {
            id: "wa-1",
            title: "Calibración de la Pantalla de Porcentaje de Reserva",
            description: "Programar el ESP32 para mostrar el nivel exacto de 0% a 100% de los 600 galones.",
            completed: false,
            starterKit: "Cable USB y computadora con Arduino IDE o Prometheus Workbench.",
            actionFirst: "Mide la distancia desde el sensor hasta el fondo del tanque vacío (ej. 160 cm) y hasta el nivel máximo del flotador (ej. 25 cm). Introduce estos valores en el código para que calcule el volumen porcentual.",
            doneCriteria: "Pantalla OLED marcando el porcentaje real de agua y enviando alerta al teléfono si baja del 25%."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/arduino",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  },

  // =========================================================================
  // 🔬 PROYECTO 4: FABRICACIÓN & MAKER (TALLER FÍSICO PROMETHEUS LAB I+D)
  // =========================================================================
  {
    id: "proj-maker-lab-fisico",
    title: "Taller & Laboratorio Maker Físico (Prometheus Lab I+D)",
    discipline: "Estructuras & Resiliencia PR",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Alta",
    difficulty: "Medio",
    targetCompletionDate: "2026-12-15",
    description: "Acondicionamiento y diseño ergonómico de un taller de ingeniería y fabricación digital de 200 a 400 pies cuadrados en Puerto Rico. Incluye mesa de trabajo reforzada con superficie antiestática ESD, extracción forzada de vapores y microplásticos para impresión 3D/soldadura, subpanel eléctrico de 60A con circuitos independientes para maquinaria CNC/impresoras y almacenamiento modular hermético contra la humedad del Caribe.",
    problemStatement: "En Puerto Rico, la humedad relativa superior al 80% arruina los filamentos de impresión 3D (hidroscópicos) y oxida las herramientas de metal si no se cuenta con un espacio de taller diseñado con control ambiental y eléctrico dedicado.",
    scientificPrinciple: "Descarga Electrostática (ESD), Termodinámica de Convección Forzada para extracción de gases según flujo de aire en CFM ($Q = V \\times A$) y Distribución Eléctrica Balanceada para mitigar interferencias electromagnéticas (EMI).",
    estimatedBudgetUSD: 2450.00,
    actualCostUSD: 0.00,
    prResilienceNotes: "Muebles fabricados con estructura de madera tratada contra termita (polilla) o perfiles de acero galvanizado. Cajas de herramientas con desecantes de gel de sílice regenerables para evitar herrumbre en alicates y calibres.",
    bom: [
      { id: "ml-b1", name: "Madera Tratada de Construcción 4x4 y 2x6 para Banco de Trabajo", category: "raw-material", quantity: 8, unit: "piezas", specs: "Madera tratada para exteriores resistente a humedad y polilla", estimatedPriceUSD: 145.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Maderas 3C / National Lumber & Hardware", notes: "Esencial que sea madera tratada comercial en PR" },
      { id: "ml-b2", name: "Extractor de Aire Centrífugo En Línea 4\" (195 CFM) con Filtro de Carbón Activado", category: "hardware", quantity: 1, unit: "kit", specs: "Ventilación de ducto para extraer humos de resina, soldadura y ABS", estimatedPriceUSD: 85.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / Home Depot PR" },
      { id: "ml-b3", name: "Subpanel Eléctrico de 60A con Breakers de 15A y 20A Grado Taller", category: "electronics", quantity: 1, unit: "unidad", specs: "Centro de carga de 8 circuitos con protección GFCI para mesas de trabajo", estimatedPriceUSD: 110.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber / Ferreterías Ace" },
      { id: "ml-b4", name: "Tapete Antiestático de Trabajo ESD de Doble Capa con Cable a Tierra", category: "hardware", quantity: 1, unit: "unidad", specs: "Resistente a calor de cautín hasta 300°C (60x120 cm)", estimatedPriceUSD: 38.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Mouser / Amazon USPS" },
      { id: "ml-b5", name: "Dry-Box Hermética con Calefactor para 4 Carretes de Filamento", category: "hardware", quantity: 1, unit: "unidad", specs: "Mantiene filamentos PETG/PLA a menos del 15% de humedad relativa en PR", estimatedPriceUSD: 65.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / Tiendas de Impresión 3D USPS" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Zonificación del Taller",
        steps: [
          {
            id: "ml-p1",
            title: "Distribución en 4 Zonas Operativas",
            description: "Dividir el taller en: Zona Sucia (corte/taladro), Zona Limpia (computadora/CAD), Zona Electrónica (soldadura/ESD) y Zona 3D (impresoras y secado).",
            completed: false,
            starterKit: "Cinta métrica y cinta adhesiva de pintor.",
            actionFirst: "Marca en el suelo con cinta de pintor el espacio de cada estación de trabajo, asegurando al menos 3 pies de pasillo libre para mover materiales sin tropezar.",
            doneCriteria: "Croquis de distribución con las 4 zonas delimitadas y tomas eléctricas identificadas."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Diseño CAD del Banco de Trabajo",
        steps: [
          {
            id: "ml-s1",
            title: "Modelado 3D del Banco de Trabajo Reforzado en FreeCAD",
            description: "Diseñar un banco de trabajo pesado de 6ft x 3ft capaz de sostener 500 lbs sin pandearse.",
            completed: false,
            starterKit: "FreeCAD o Blender.",
            actionFirst: "Modela la estructura de patas 4x4 unidas con largueros 2x6 y tapa doble de madera laminada de 3/4 pulgada sellada con poliuretano.",
            doneCriteria: "Plano de corte de madera 2D TechDraw con la lista exacta de piezas a cortar."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Compras de Madera y Electricidad",
        steps: [
          {
            id: "ml-i1",
            title: "Adquisición de Madera Tratada y Tornillos para Madera Exterior",
            description: "Comprar madera tratada contra termitas y tornillos Spax o Decking galvanizados.",
            completed: false,
            starterKit: "Lista de corte y transporte.",
            actionFirst: "Selecciona en National Lumber o Maderas 3C tablas y vigas rectas sin combaduras excesivas y tornillos para madera exterior de 3 pulgadas.",
            doneCriteria: "Madera y fijaciones en el taller listas para ensamblaje."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Ensamble & Ductos de Extracción",
        steps: [
          {
            id: "ml-c1",
            title: "Corte y Armado de Estructura de Banco con Pernos Tirafondos",
            description: "Armar las patas y largueros usando uniones entalladas o pernos pasantes de 3/8 pulgada.",
            completed: false,
            starterKit: "Sierra circular o de inglete, taladro atornillador, escuadra de carpintero y prensa.",
            actionFirst: "Corta las 4 patas a 36 pulgadas de altura, taladra orificios guía y atornilla los largueros perimetrales asegurando escuadras perfectas a 90 grados.",
            doneCriteria: "Banco de trabajo rígidamente montado, pesado y que no tambalea al aplicarle fuerza lateral.",
            criticalPoints: "Usar siempre gafas de seguridad y mascarilla contra polvo al cortar madera tratada debido a los preservantes químicos."
          },
          {
            id: "ml-c2",
            title: "Instalación del Ducto de Extracción de Humos",
            description: "Instalar el extractor centrífugo con ducto flexible de 4 pulgadas hacia una ventana o salida exterior.",
            completed: false,
            starterKit: "Ducto flexible de aluminio de 4\", extractor en línea y abrazaderas de tornillo sin fin.",
            actionFirst: "Fija el extractor al techo o pared, conecta el ducto flexible con campana sobre la estación de soldadura y el gabinete de impresión 3D, y orienta la salida al exterior con celosía antirretorno.",
            doneCriteria: "Extractor operando con flujo continuo de aire que desaloja el humo de soldadura en menos de 5 segundos."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Pruebas de Tierra ESD y Circuitos",
        steps: [
          {
            id: "ml-t1",
            title: "Prueba de Continuidad de Tierra del Tapete ESD",
            description: "Medir que la resistencia entre la superficie del tapete y la tierra eléctrica de la pared esté entre 1MΩ y 10MΩ.",
            completed: false,
            starterKit: "Multímetro digital.",
            actionFirst: "Conecta el clip del tapete a la tierra física de la toma eléctrica y mide con las puntas del multímetro para certificar la disipación segura de estática sin riesgo de choque eléctrico.",
            doneCriteria: "Lectura de resistencia dentro del rango seguro ESD (disipa electricidad estática sin peligro)."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Registro de Herramientas & Inventario",
        steps: [
          {
            id: "ml-a1",
            title: "Carga de Herramientas en el Gestor de Inventario de Prometheus",
            description: "Catalogar llaves, tornillería, multímetros y fuentes de poder en la base de datos local del lab.",
            completed: false,
            starterKit: "Navegador web con Prometheus Lab AI.",
            actionFirst: "Abre la pestaña de Inventario en Prometheus y añade las herramientas con su ubicación exacta en gaveta para facilitar su búsqueda durante proyectos futuros.",
            doneCriteria: "Inventario del taller actualizado con stock y ubicaciones registradas."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/cad_bridge",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  },

  // =========================================================================
  // 📦 PROYECTO 5: MECÁNICA & 3D (GABINETES IP67 EN PETG CONTRA SALITRE)
  // =========================================================================
  {
    id: "proj-enclosure-ip67-bambu",
    title: "Gabinetes Herméticos IP67 & Carcasas Paramétricas (Bambu Lab PETG)",
    discipline: "Mecánica & CAD",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Alta",
    difficulty: "Medio",
    targetCompletionDate: "2026-10-25",
    description: "Diseño paramétrico y fabricación aditiva de cajas herméticas para microcontroladores ESP32, sensores y relays expuestos al exterior en Puerto Rico. Incorpora juntas de neopreno/silicona, insertos roscados de latón M3 fijados por calor, tornillería en acero inoxidable grado marino 316 y prensaestopas estancos para evitar la corrosión salina.",
    problemStatement: "En las costas de Puerto Rico, el salitre marino penetra en los gabinetes comerciales comunes en pocas semanas, oxidando terminales y sulfatando circuitos impresos.",
    scientificPrinciple: "Mecánica de Compresión Elastomérica de Juntas O-Ring (deformación del 20-30% para estanqueidad IP67), Contracción Térmica de Polímeros en Impresión 3D FDM (+0.2mm a +0.3mm) y Pasivación del Acero Inoxidable 316.",
    estimatedBudgetUSD: 45.00,
    actualCostUSD: 0.00,
    filamentGramsPETG: 220,
    prResilienceNotes: "Impreso exclusivamente en filamento PETG o ASA con 6 perímetros de pared (paredes macizas de 2.4mm sin huecos) para resistir impactos y sol tropical intenso.",
    bom: [
      { id: "enc-b1", name: "Insertos Roscados Moleteados de Latón M3 (Pack x30)", category: "hardware", quantity: 1, unit: "pack", specs: "Fijación térmica con cautín para anclar la tapa con rosca duradera", estimatedPriceUSD: 7.50, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / Mouser USPS" },
      { id: "enc-b2", name: "Tornillos M3x12mm de Cabeza Cilíndrica en Acero Inoxidable 316", category: "hardware", quantity: 20, unit: "unidades", specs: "Grado marino para máxima resistencia a salitre", estimatedPriceUSD: 6.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber / Ferreterías Ace" },
      { id: "enc-b3", name: "Cordón de Goma Neopreno / Junta Tórica de 2.5mm", category: "hardware", quantity: 2, unit: "metros", specs: "Goma flexible para canal de sellado perimetral", estimatedPriceUSD: 5.50, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "AutoZone / Centropiezas Plus PR" },
      { id: "enc-b4", name: "Prensaestopas Impermeables de Nylon PG7 / PG9 con Tuerca", category: "hardware", quantity: 4, unit: "unidades", specs: "Sello de goma con clasificación IP68 para entrada de cables", estimatedPriceUSD: 6.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Ferreterías Ace / Amazon USPS" },
      { id: "enc-b5", name: "Bobina de Filamento PETG Negro UV de 1 kg", category: "raw-material", quantity: 1, unit: "bobina", specs: "Tolerancia dimensional 0.02mm para Bambu Lab P1S/X1C/A1", estimatedPriceUSD: 20.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Taller Prometheus / Envíos USPS" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Dimensionamiento de la Placa",
        steps: [
          {
            id: "enc-p1",
            title: "Medición de Placa PCB y Espacio de Conectores",
            description: "Medir el largo, ancho y altura máxima de los componentes en la placa electrónica.",
            completed: false,
            starterKit: "Calibrador vernier digital milimétrico.",
            actionFirst: "Mide las dimensiones de la placa (ej. 65mm x 45mm) y añade 8mm de margen perimetral para alojar las torretas de tornillos M3 y el canal de la junta de goma.",
            doneCriteria: "Dimensiones interiores mínimas de la caja anotadas en milímetros."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Modelado Paramétrico en CAD",
        steps: [
          {
            id: "enc-s1",
            title: "Diseño del Canal O-Ring y Torretas de Latón",
            description: "Crear en FreeCAD o Fusion 360 la caja base con canal de 2.8mm de ancho y tapa con nervadura machihembrada.",
            completed: false,
            starterKit: "FreeCAD o Blender.",
            actionFirst: "Modela la caja con espesor de pared de 2.4mm, orificios ciegos de 4.2mm de diámetro para los insertos de latón M3 y un canal perimetral de 2.8mm de ancho por 1.8mm de profundidad.",
            doneCriteria: "Archivos STL o STEP listos para importar en Bambu Studio u OrcaSlicer."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Compras de Tornillería Inox y Filamento",
        steps: [
          {
            id: "enc-i1",
            title: "Verificación de Tornillos Inoxidables 316 e Insertos",
            description: "Tener listos los insertos M3 y tornillos que no se oxiden con el salitre.",
            completed: false,
            starterKit: "Cajita organizadora con tornillería.",
            actionFirst: "Confirma tener al menos 4 insertos de latón, 4 tornillos M3x12 inoxidables y cordón de neopreno.",
            doneCriteria: "Fijaciones organizadas en la mesa de trabajo."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Impresión 3D & Inserción Térmica",
        steps: [
          {
            id: "enc-c1",
            title: "Impresión en PETG con 5 Perímetros",
            description: "Rebanar en el slicer con 5 paredes y 30% infill giroidal para asegurar impermeabilidad en las capas.",
            completed: false,
            starterKit: "Impresora 3D calibrada, boquilla 0.4mm, filamento PETG seco.",
            actionFirst: "Configura en Bambu Studio: 5 perímetros, temperatura de boquilla a 245°C, cama a 75°C y ventilador de capa al 40% para máxima fusión entre hilos.",
            doneCriteria: "Caja y tapa impresas sin deformaciones en las esquinas (sin warping) y con rosca y canales limpios.",
            criticalPoints: "Imprimir con menos de 4 perímetros puede dejar micro-poros microscópicos entre capas por donde penetra la humedad salina con el tiempo."
          },
          {
            id: "enc-c2",
            title: "Instalación Térmica de Insertos de Latón con Cautín",
            description: "Insertar los bujes roscados de latón usando la punta caliente de un soldador a 220°C.",
            completed: false,
            starterKit: "Cautín regulable a 220°C, pinzas y guantes térmicos.",
            actionFirst: "Coloca el inserto sobre el orificio de la torreta, presiona suavemente con la punta del cautín caliente hasta que el latón caliente derrita el plástico y baje al ras de la superficie, retira el cautín y deja enfriar 1 minuto.",
            doneCriteria: "Los 4 insertos de latón fijados rectos y alineados a ras con rosca M3 perfecta."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Prueba de Inmersión IP67",
        steps: [
          {
            id: "enc-t1",
            title: "Prueba de Estanqueidad Sumergida en Cubo de Agua",
            description: "Cerrar la caja con un trozo de papel secante adentro y sumergirla a 30 cm de profundidad por 10 minutos.",
            completed: false,
            starterKit: "Cubo hondo con agua limpia, papel toalla o papel secante y cronómetro.",
            actionFirst: "Coloca la junta de neopreno en el canal, mete un trozo de papel toalla seco dentro de la caja, aprieta los 4 tornillos de la tapa firmemente y sumerge la caja en el cubo de agua por 10 minutos.",
            doneCriteria: "Al abrir la caja, el papel toalla interior sale completamente seco y sin una sola gota de agua visible."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Exportación de Plantilla Paramétrica",
        steps: [
          {
            id: "enc-a1",
            title: "Guardar Parámetros en el Repositorio de Modelos de Prometheus",
            description: "Almacenar el archivo de diseño para reescalar la caja automáticamente para futuros proyectos.",
            completed: false,
            starterKit: "Prometheus Workbench.",
            actionFirst: "Guarda el script de Python o archivo CAD para que con solo cambiar las variables de largo, ancho y alto se genere un nuevo gabinete al instante.",
            doneCriteria: "Plantilla lista y compartida en la biblioteca de prototipos del lab."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/google_pics",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  },

  // =========================================================================
  // 🌀 PROYECTO 6: ENERGÍA EÓLICA (TURBINA MARINA SAVONIUS CONTRA SALITRE)
  // =========================================================================
  {
    id: "proj-savonius-wind",
    title: "Turbina Eólica Marina Savonius (Microgenerador Costero)",
    discipline: "Energía & Solar",
    ecosystem: "prometheus-lab",
    status: "idea-future",
    priority: "Media",
    difficulty: "Avanzado",
    targetCompletionDate: "2026-11-20",
    description: "Microgenerador eólico de eje vertical omnidireccional diseñado para capturar la brisa nocturna constante en zonas costeras de Puerto Rico. Cuenta con álabes helicoidales impresos en 3D en PETG resistente a UV, rodamientos sellados de acero inoxidable 316 contra el salitre marino y alternador paso a paso rectificado con diodos Schottky de baja pérdida.",
    problemStatement: "En las costas de Puerto Rico, los paneles solares no producen de noche pero el viento costero sopla de forma continua; sin embargo, el salitre marino destruye rápidamente los generadores metálicos tradicionales.",
    scientificPrinciple: "Ley de Betz, Fuerza de Arrastre Diferencial en Rotores Savonius con par de arranque en cualquier dirección del viento y Rectificación de Onda Completa Trifásica.",
    estimatedBudgetUSD: 68.50,
    actualCostUSD: 0.00,
    filamentGramsPETG: 450,
    prResilienceNotes: "Estructura exterior en PETG UV y mástil de aluminio anodizado cédula 40. Toda la tornillería y rodamientos son en Acero Inoxidable 316 (Marine Grade).",
    bom: [
      { id: "sw-b1", name: "Rotor Helicoidal de 3 Álabes Impreso en 3D", category: "3d-print", quantity: 3, unit: "piezas", specs: "PETG con 4 perímetros y 35% infill giroidal", estimatedPriceUSD: 16.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Taller Local Prometheus (Bambu Lab)" },
      { id: "sw-b2", name: "Alternador Paso a Paso NEMA 23 de Alto Torque", category: "electronics", quantity: 1, unit: "unidad", specs: "Bipolar 2.8A con bobinas acopladas para generar voltaje a bajas RPM", estimatedPriceUSD: 24.50, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Mouser / Digi-Key USPS Doméstico" },
      { id: "sw-b3", name: "Rodamientos Sellados 608-2RS en Acero Inoxidable 316", category: "hardware", quantity: 4, unit: "unidades", specs: "Sello de goma nitrílica impermeable contra salpicadura salina", estimatedPriceUSD: 9.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Amazon / USPS" },
      { id: "sw-b4", name: "Tubo Mástil de Aluminio Anodizado de 1\" (Tramo 5ft)", category: "hardware", quantity: 1, unit: "tramo", specs: "Aluminio naval resistente a corrosión", estimatedPriceUSD: 14.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "National Lumber & Hardware" },
      { id: "sw-b5", name: "Puente Rectificador con Diodos Schottky 1N5822", category: "electronics", quantity: 6, unit: "unidades", specs: "Caída mínima de voltaje 0.3V para máxima eficiencia a bajo viento", estimatedPriceUSD: 5.00, actualPriceUSD: 0.00, inStock: false, localSupplierPR: "Ferretería Electrónica / Amazon USPS" }
    ],
    phases: {
      preparation: {
        name: "preparation",
        label: "1. Preparación & Velocidad de Viento",
        steps: [
          {
            id: "sw-p1",
            title: "Medición de Brisa Costera Nocturna",
            description: "Medir con anemómetro que el lugar de montaje tenga vientos promedio de al menos 8 mph (3.5 m/s).",
            completed: false,
            starterKit: "Anemómetro digital portátil de mano.",
            actionFirst: "Mide el viento a las 8:00 PM y 11:00 PM en el punto alto del techo o patio para registrar las ráfagas promedio.",
            doneCriteria: "Registro de viento confirmando al menos 8 mph sostenidas durante las noches."
          }
        ]
      },
      simulationCad: {
        name: "simulationCad",
        label: "2. Diseño Helicoidal CAD",
        steps: [
          {
            id: "sw-s1",
            title: "Modelado del Álabe Helicoidal con Torsión de 60 Grados",
            description: "Modelar el rotor en espiral helicoidal para garantizar rotación suave y silenciosa sin pulsaciones bruscas.",
            completed: false,
            starterKit: "FreeCAD o Blender.",
            actionFirst: "Crea una curva en espiral helicoidal extruida de 180mm de radio con torsión de 60 grados para repartir la presión del viento de forma balanceada.",
            doneCriteria: "Archivos 3D exportados en STL listos para la impresora."
          }
        ]
      },
      inventoryProcurement: {
        name: "inventoryProcurement",
        label: "3. Abasto de Rodamientos y Alternador",
        steps: [
          {
            id: "sw-i1",
            title: "Recepción de Rodamientos Inoxidables 316",
            description: "Confirmar rodamientos con sello de goma impermeable para ambiente marino.",
            completed: false,
            starterKit: "Calibrador vernier.",
            actionFirst: "Verifica que los rodamientos giren suaves y tengan grasa sintética resistente al agua marina.",
            doneCriteria: "Componentes listos en la mesa del taller."
          }
        ]
      },
      construction: {
        name: "construction",
        label: "4. Impresión 3D & Ensamble del Eje",
        steps: [
          {
            id: "sw-c1",
            title: "Impresión de los Álabes en PETG UV",
            description: "Imprimir en orientación vertical con 4 perímetros sólidos para resistir fuerza centrífuga.",
            completed: false,
            starterKit: "Bambu Lab u otra impresora FDM con bobina de PETG negro o blanco.",
            actionFirst: "Imprime los 3 segmentos del álabe en PETG con 4 paredes y altura de capa de 0.20mm.",
            doneCriteria: "Álabes helicoidales sólidos sin defectos entre capas listos para montar en el eje de acero inoxidable.",
            criticalPoints: "No usar PLA para exteriores en Puerto Rico; el calor solar a mediodía (deformación a 55°C) doblará las aspas."
          }
        ]
      },
      testingCalibration: {
        name: "testingCalibration",
        label: "5. Pruebas Eléctricas V vs RPM",
        steps: [
          {
            id: "sw-t1",
            title: "Prueba de Generación con Multímetro",
            description: "Hacer girar el rotor con un ventilador o brisa y medir el voltaje rectificado.",
            completed: false,
            starterKit: "Multímetro digital y banco de pruebas.",
            actionFirst: "Conecta el multímetro en modo voltaje DC a la salida del rectificador Schottky y gira el eje a mano: debe generar más de 12V a 300 RPM.",
            doneCriteria: "Voltaje suficiente generado para cargar baterías de 12V en flotación."
          }
        ]
      },
      analysisReview: {
        name: "analysisReview",
        label: "6. Montaje en Mástil & Resiliencia",
        steps: [
          {
            id: "sw-a1",
            title: "Instalación en Mástil con Desacople de Seguridad",
            description: "Montar en el mástil de aluminio con sistema de bajada rápida ante aviso de huracán mayor.",
            completed: false,
            starterKit: "Abrazaderas en U de acero inoxidable, llaves y cableado hacia el controlador.",
            actionFirst: "Fija el mástil a la losa o barandilla con abrazaderas inoxidables y conecta los cables al sistema de baterías del taller.",
            doneCriteria: "Turbina eólica marina girando suavemente con la brisa nocturna y alimentando el taller."
          }
        ]
      }
    },
    cadToolLink: "/herramientas/materials",
    createdAt: "2026-09-08",
    updatedAt: "2026-09-08"
  }
];

interface ProjectLifecycleStudioProps {
  initialStatusTab?: "in-progress" | "idea-future" | "completed" | "all";
}

export const ProjectLifecycleStudio: React.FC<{
  initialStatusTab?: "in-progress" | "idea-future" | "completed" | "all";
  initialStudioView?: "phases" | "cad3d" | "wiring3d" | "university" | "reverse" | "journal";
}> = ({ initialStatusTab, initialStudioView }) => {
  const [projectsList, setProjectsList] = useState<EngineeringProject[]>(() => {
    const saved = localStorage.getItem("prometheus_engineering_projects");
    if (saved) {
      try {
        const parsed: EngineeringProject[] = JSON.parse(saved);
        // Deduplicate projects by ID
        const seenIds = new Set<string>();
        const uniqueParsed: EngineeringProject[] = [];
        for (const p of parsed) {
          if (!seenIds.has(p.id)) {
            seenIds.add(p.id);
            uniqueParsed.push(p);
          }
        }

        const missingDefaults = DEFAULT_PROJECTS.filter(d => !seenIds.has(d.id));

        const sanitized = uniqueParsed.map(p => {
          const def = DEFAULT_PROJECTS.find(d => d.id === p.id);

          // If default project exists, enrich phases and steps with latest practical guidance while preserving user completed checkboxes
          let resolvedPhases = p.phases;
          if (def && def.phases) {
            resolvedPhases = { ...def.phases };
            Object.keys(resolvedPhases).forEach((phKey) => {
              const defPhase = (def.phases as any)[phKey];
              const userPhase = (p.phases as any)?.[phKey];
              if (defPhase && userPhase) {
                resolvedPhases[phKey as keyof typeof def.phases] = {
                  ...defPhase,
                  steps: defPhase.steps.map((dStep: any) => {
                    const uStep = userPhase.steps?.find((us: any) => us.id === dStep.id);
                    return {
                      ...dStep,
                      completed: uStep ? uStep.completed : false
                    };
                  })
                };
              }
            });
          }

          // Calculate actual completed steps count
          let completedSteps = 0;
          let totalSteps = 0;
          if (resolvedPhases) {
            Object.values(resolvedPhases).forEach(phase => {
              phase.steps?.forEach(st => {
                totalSteps++;
                if (st.completed) completedSteps++;
              });
            });
          }

          // If no steps completed or if it was legacy seed data, default to "idea-future"
          const resolvedStatus: EngineeringProject["status"] =
            completedSteps === 0 ? "idea-future" : p.status || def?.status || "idea-future";

          // Calculate real actual cost from BOM if actualPriceUSD is provided
          const updatedBOM = (p.bom || def?.bom || []).map(item => ({
            ...item,
            // If inStock was inherited as true from old seed data without actual purchase, reset to false
            inStock: item.inStock && item.actualPriceUSD && item.actualPriceUSD > 0 ? true : false,
            actualPriceUSD: item.actualPriceUSD || 0
          }));

          const calculatedActualCost = updatedBOM.reduce((sum, it) => sum + (it.actualPriceUSD || 0), 0);

          return {
            ...p,
            status: resolvedStatus,
            phases: resolvedPhases,
            ecosystem: p.ecosystem || def?.ecosystem || (p.id.startsWith("proj-aura") ? "aura-os" : "prometheus-lab"),
            bom: updatedBOM,
            actualCostUSD: calculatedActualCost,
            estimatedBudgetUSD: p.estimatedBudgetUSD || def?.estimatedBudgetUSD || 0,
            logs: p.logs && p.logs.length > 0 ? p.logs : (def?.logs || [
              {
                id: `log-seed-${p.id}`,
                timestamp: new Date().toLocaleString(),
                title: "Inicialización de Idea en Taller",
                content: `Registro inicial de especificaciones y hoja de ruta preliminar para "${p.title}".`,
                category: "progreso"
              }
            ])
          };
        });

        const merged = [...missingDefaults, ...sanitized];
        localStorage.setItem("prometheus_engineering_projects", JSON.stringify(merged));
        return merged;
      } catch {}
    }
    return DEFAULT_PROJECTS;
  });

  const [activeProjectId, setActiveProjectId] = useState<string>(() => {
    if (initialStatusTab && initialStatusTab !== "all") {
      const match = DEFAULT_PROJECTS.find(p => p.status === initialStatusTab);
      if (match) return match.id;
    }
    return DEFAULT_PROJECTS[0].id;
  });
  const [selectedStatusTab, setSelectedStatusTab] = useState<"in-progress" | "idea-future" | "completed" | "all">(initialStatusTab || "all");
  const [selectedEcosystem, setSelectedEcosystem] = useState<"all" | "aura-os" | "prometheus-lab">("all");

  useEffect(() => {
    if (initialStatusTab) {
      setSelectedStatusTab(initialStatusTab);
      const matching = projectsList.find(p => initialStatusTab === "all" || p.status === initialStatusTab);
      if (matching) {
        setActiveProjectId(matching.id);
      }
    }
  }, [initialStatusTab, projectsList]);

  const [activePhaseTab, setActivePhaseTab] = useState<keyof EngineeringProject["phases"]>("preparation");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>("all");

  // Modal / Creator State
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDiscipline, setNewDiscipline] = useState<EngineeringProject["discipline"]>("Mecánica & CAD");
  const [newStatus, setNewStatus] = useState<EngineeringProject["status"]>("in-progress");
  const [newDescription, setNewDescription] = useState("");
  const [newProblem, setNewProblem] = useState("");
  const [newPrinciple, setNewPrinciple] = useState("");
  const [newBudget, setNewBudget] = useState(50);
  const [isAiGenerating, setIsAiGenerating] = useState(false);

  // Una sola fila de vistas (aquí). El padre puede fijar la vista inicial vía URL/?view=
  const [searchParams, setSearchParams] = useSearchParams();
  const [studioViewMode, setStudioViewMode] = useState<"phases" | "cad3d" | "wiring3d" | "university" | "reverse" | "journal">(initialStudioView || "phases");
  useEffect(() => {
    if (initialStudioView) setStudioViewMode(initialStudioView);
  }, [initialStudioView]);

  const setStudioView = useCallback(
    (mode: "phases" | "cad3d" | "wiring3d" | "university" | "reverse" | "journal") => {
      setStudioViewMode(mode);
      const next = new URLSearchParams(searchParams);
      const station = mode === "university" || mode === "reverse" ? "university" : "projects";
      next.set("station", station);
      next.set("view", mode);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // ── 1. Control Financiero Rápido (Preestipulado vs Real) ──
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);

  // ── 2. Despiece 3D, Montaje Paso a Paso & Blueprints Acotados ──
  const [explodeFactor, setExplodeFactor] = useState<number>(35);
  const [assemblyStepIdx, setAssemblyStepIdx] = useState<number>(4);
  const [selectedPartIdx, setSelectedPartIdx] = useState<number>(0);
  const [cadSubView, setCadSubView] = useState<"exploded3d" | "blueprint2d">("exploded3d");

  // ── 3. Conexiones de Hardware en 3D (Step-by-Step Wiring Trainer) ──
  const [activeWireIdx, setActiveWireIdx] = useState<number>(-1);
  const [isSimulatingCurrent, setIsSimulatingCurrent] = useState<boolean>(true);

  // ── 4. Universidad Práctica: MechAgent + Vento AI + SkillFall AI + Tutor Socrático ──
  const [mechGearRatio, setMechGearRatio] = useState<number>(12);
  const [mechLoadKg, setMechLoadKg] = useState<number>(15);
  const [mechArmMm, setMechArmMm] = useState<number>(180);
  const [mechMaterial, setMechMaterial] = useState<"petg_cf" | "alu_6061" | "inox_316" | "concreto_4000">("petg_cf");
  const [activeSocraticStepId, setActiveSocraticStepId] = useState<string | null>(null);
  const [socraticSelectedAnswer, setSocraticSelectedAnswer] = useState<Record<string, number>>({});
  const [skillMastery, setSkillMastery] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem("prometheus_skillfall_mastery");
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      estatica: 45,
      cinematica: 60,
      mecatronica: 50,
      termofluidos: 40,
      arquitectura_pr: 55
    };
  });

  const awardSkillMastery = (pillar: string, points: number, reason: string) => {
    setSkillMastery((prev) => {
      const next = { ...prev, [pillar]: Math.min(100, (prev[pillar] || 40) + points) };
      localStorage.setItem("prometheus_skillfall_mastery", JSON.stringify(next));
      return next;
    });
    toast.success(`🎓 +${points}% Maestría SkillFall AI (${reason})`);
  };

  // ── 5. Motor de Ingeniería Inversa ("¿Cómo lo hicieron? → Recrear en mi Taller") ──
  const [selectedReverseIdx, setSelectedReverseIdx] = useState<number>(0);
  const [customReverseQuery, setCustomReverseQuery] = useState<string>("");
  const [customReverseResult, setCustomReverseResult] = useState<{
    title: string;
    category: string;
    commercialPriceUSD: number;
    diyPricePR: number;
    secretPrinciple: string;
    kinematicExplanation: string;
    subsystems: { name: string; spec: string; cost: number; supplierPR: string }[];
    steps: string[];
  } | null>(null);
  const [newLogTitle, setNewLogTitle] = useState("");
  const [newLogContent, setNewLogContent] = useState("");
  const [newLogCategory, setNewLogCategory] = useState<"progreso" | "problema" | "medicion" | "leccion">("progreso");

  // Mobile drawer & Step filter / additions
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const [stepFilter, setStepFilter] = useState<"all" | "pending" | "completed">("all");
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [newStepTitle, setNewStepTitle] = useState("");
  const [newStepKit, setNewStepKit] = useState("");
  const [newStepAction, setNewStepAction] = useState("");
  const [newStepDone, setNewStepDone] = useState("");
  const [isGeneratingStep, setIsGeneratingStep] = useState(false);

  // Bitácora Inteligente & Fotos de Avance
  const [smartUpdateProject, setSmartUpdateProject] = useState(true);
  const [logPhotoUrl, setLogPhotoUrl] = useState<string | null>(null);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState<string | null>(null);

  // Modo Lista de Compras para Ferreterías de Puerto Rico
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [shoppingSelectedStore, setShoppingSelectedStore] = useState<string>("all");
  const [shoppingCheckedItems, setShoppingCheckedItems] = useState<Record<string, boolean>>({});

  const [isCinematicModalOpen, setIsCinematicModalOpen] = useState(false);
  const [isVerifyingAbastoPR, setIsVerifyingAbastoPR] = useState(false);

  const handleVerifyAbastoPR = () => {
    if (!activeProject) return;
    setIsVerifyingAbastoPR(true);
    toast.info("🔎 Grounding Perplexity: Verificando stock y precios en Home Depot PR, National Lumber y Digi-Key...");
    setTimeout(() => {
      setIsVerifyingAbastoPR(false);
      toast.success(`✓ Verificación completada para ${activeProject.bom?.length || 0} componentes en canales de Puerto Rico (USPS Doméstico / Tiendas locales).`);
    }, 1500);
  };

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem("prometheus_engineering_projects", JSON.stringify(projectsList));
    localStorage.setItem("prometheus_lifecycle_projects", JSON.stringify(projectsList));
  }, [projectsList]);

  const activeProject = useMemo(() => {
    return projectsList.find((p) => p.id === activeProjectId) || projectsList[0] || null;
  }, [projectsList, activeProjectId]);

  const projectLogs = useMemo(() => {
    return (activeProject as any)?.logs || [];
  }, [activeProject]);

  // Add custom step to current active phase
  const handleAddCustomStep = () => {
    if (!newStepTitle.trim() || !activeProject) return;
    const newStep: PhaseStep = {
      id: `step-custom-${Date.now()}`,
      title: newStepTitle.trim(),
      description: newStepAction.trim() || "Paso técnico personalizado añadido en el taller.",
      completed: false,
      starterKit: newStepKit.trim() || undefined,
      actionFirst: newStepAction.trim() || undefined,
      doneCriteria: newStepDone.trim() || undefined,
    };

    setProjectsList((prevList) =>
      prevList.map((p) => {
        if (p.id !== activeProject.id) return p;
        const currentPhase = p.phases[activePhaseTab];
        return {
          ...p,
          phases: {
            ...p.phases,
            [activePhaseTab]: {
              ...currentPhase,
              steps: [...currentPhase.steps, newStep],
            },
          },
        };
      })
    );

    setNewStepTitle("");
    setNewStepKit("");
    setNewStepAction("");
    setNewStepDone("");
    setIsAddingStep(false);
    toast.success("✓ Nuevo paso añadido a la fase.");
  };

  // Copy structured project summary for WhatsApp / collaborators
  const handleCopyProjectSummary = () => {
    if (!activeProject) return;
    const progress = calculateProjectProgress(activeProject);
    const currentPhase = activeProject.phases[activePhaseTab];
    const pendingSteps = currentPhase.steps.filter((s) => !s.completed);
    const summary = `📌 PROYECTO TALLER: ${activeProject.title}
📊 Progreso Global: ${progress}%
📍 Fase Activa: ${currentPhase.label} (${currentPhase.steps.filter(s => s.completed).length}/${currentPhase.steps.length} pasos completados)
💰 Presupuesto Estimado: $${activeProject.estimatedBudgetUSD.toFixed(2)} USD | Real: $${activeProjectActualCost.toFixed(2)} USD
🎯 Próxima Acción Inmediata: ${pendingSteps[0] ? pendingSteps[0].title : "Todos los pasos de esta fase completados"}
${pendingSteps[0]?.actionFirst ? `🔨 Acción física: ${pendingSteps[0].actionFirst}` : ""}
📦 BOM de Materiales: ${activeProject.bom.length} componentes registrados`;

    navigator.clipboard.writeText(summary);
    toast.success("✓ Resumen técnico del proyecto copiado al portapapeles.");
  };

  // AI Step Generator tailored to project context, current phase and PR materials
  const handleAiGenerateStep = () => {
    if (!activeProject) return;
    setIsGeneratingStep(true);
    const currentPhase = activeProject.phases[activePhaseTab];
    const phaseKey = activePhaseTab;
    const pTitle = activeProject.title.toLowerCase();

    let gen = {
      title: "",
      starterKit: "",
      actionFirst: "",
      doneCriteria: ""
    };

    if (newStepTitle.trim()) {
      const hint = newStepTitle.trim();
      gen = {
        title: hint.charAt(0).toUpperCase() + hint.slice(1),
        starterKit: `Herramientas de taller y componentes para "${hint}", disponibles en ferreterías locales (Home Depot PR / National Lumber) o Mouser vía USPS.`,
        actionFirst: `Iniciar el procedimiento técnico de ${hint}: verificar alineación, conexiones mecánicas/eléctricas y tolerancias dimensionales.`,
        doneCriteria: `Verificación funcional completada: prueba sin interferencias físicas, fugas ni sobrecalentamiento.`
      };
    } else if (pTitle.includes("coffee") || pTitle.includes("concreto")) {
      if (phaseKey === "preparation") {
        gen = {
          title: "Zonificación de Espacios y Verificación de Retiradas OGPe",
          starterKit: "Cinta métrica de 100 pies, plano de mensura del solar y software CAD.",
          actionFirst: "Medir las distancias a colindancias (mínimo 5 pies según normativa municipal) y ubicar la entrada con rampa ADA (1:12).",
          doneCriteria: "Esquema preliminar acotado sin invasión de servidumbres de paso."
        };
      } else if (phaseKey === "inventoryProcurement") {
        gen = {
          title: "Cotización de Varillas Grado 60 y Cemento en National Lumber / Maderas 3C",
          starterKit: "Lista de despiece de acero y cálculo de sacos de cemento Portland 4000 PSI.",
          actionFirst: "Llamar o visitar la sucursal local para confirmar disponibilidad de varillas de 3/8 y 1/2 pulgada y entrega en obra.",
          doneCriteria: "Factura o cotización formal aprobada con precios congelados."
        };
      } else if (phaseKey === "construction") {
        gen = {
          title: "Envarillado y Vaciado de Losa de Concreto 3000 PSI",
          starterKit: "Malla electrosoldada, espaciadores de concreto, vibrador de hormigón y nivel láser.",
          actionFirst: "Colocar plástico de vapor de 6 mil sobre tosca compactada, amarrar varillas con alambre recocido y vaciar losa monolítica.",
          doneCriteria: "Losa curada con agua por 7 días continuos sin fisuras estructurales visibles."
        };
      } else {
        gen = {
          title: "Inspección de Carga Eléctrica 240V y Prueba Sanitaria de Drenaje",
          starterKit: "Multímetro True-RMS, bomba de agua y prueba de humo en tuberías.",
          actionFirst: "Medir voltaje en tomacorrientes NEMA 6-30R de máquina de café y verificar pendiente del 2% en desagües.",
          doneCriteria: "Voltaje estable bajo carga de 30A y drenaje rápido sin reflujo en la trampa de grasa."
        };
      }
    } else if (pTitle.includes("solar") || pTitle.includes("lifepo4")) {
      if (phaseKey === "inventoryProcurement") {
        gen = {
          title: "Adquisición de Cable Soldar 2/0 AWG y Terminales de Cobre Estañado",
          starterKit: "Ponchadora hidráulica de terminales, termorretráctil con adhesivo y cable flexible de cobre.",
          actionFirst: "Comprar en tienda eléctrica local o pedir vía USPS doméstico; cortar a medida exacta para minimizar caída de voltaje.",
          doneCriteria: "Cables cortados, ponchados a 10 toneladas e inspeccionados sin hilos sueltos."
        };
      } else if (phaseKey === "construction" || phaseKey === "simulationCad") {
        gen = {
          title: "Instalación de Fusible Clase T 150A y Desconexión Rápida DC",
          starterKit: "Portafusibles Clase T con tapa de policarbonato y llaves aisladas 1000V.",
          actionFirst: "Montar el portafusibles en el conductor positivo a menos de 18 pulgadas del borne de la batería LiFePO4.",
          doneCriteria: "Torque verificado según especificación del fabricante (mínimo 12 Nm)."
        };
      } else {
        gen = {
          title: "Prueba de Carga Continua del Inversor con Simulador de Apagón LUMA",
          starterKit: "Carga resistiva de 3000W (calentador/blower) y pinza amperimétrica DC.",
          actionFirst: "Bajar el breaker principal de la red comercial y medir la respuesta del inversor en menos de 15 milisegundos.",
          doneCriteria: "La batería suministra 62A a 51.2V con temperatura de bornes inferior a 42°C."
        };
      }
    } else if (pTitle.includes("cisterna") || pTitle.includes("agua")) {
      gen = {
        title: "Instalación de Válvula Check Anti-Retorno de Bronce en Tubería de Entrada",
        starterKit: "Válvula check oscilante de 3/4 pulgada, cinta de teflón de alta densidad y llave inglesa.",
        actionFirst: "Enroscar la válvula en la dirección del flujo hacia el tanque para evitar que el agua almacenada retorne a la red de acueductos.",
        doneCriteria: "Cero goteos bajo presión de 60 PSI y retención hermética verificada."
      };
    } else {
      gen = {
        title: `Optimización y Verificación Técnica de ${currentPhase.label}`,
        starterKit: "Calibrador vernier digital, juego de destornilladores de precisión e instrumentos de medición.",
        actionFirst: "Realizar el ensayo preliminar del componente para comprobar ajuste mecánico, continuidad y tolerancias.",
        doneCriteria: "Tolerancia milimétrica dentro del rango especificado (±0.15 mm)."
      };
    }

    setTimeout(() => {
      setNewStepTitle(gen.title);
      setNewStepKit(gen.starterKit);
      setNewStepAction(gen.actionFirst);
      setNewStepDone(gen.doneCriteria);
      setIsGeneratingStep(false);
      toast.success("✨ ¡Paso técnico generado con IA! Revisa las 3 cajas y presiona 'Guardar Paso'.");
    }, 450);
  };

  // Smart Bitácora Handler: Updates project phase, advances steps, or generates adaptive fixes based on log
  const handleAddProjectLog = () => {
    if (!newLogTitle.trim() || !newLogContent.trim() || !activeProject) return;

    const newEntry = {
      id: `log-${Date.now()}`,
      timestamp: new Date().toLocaleString(),
      title: newLogTitle.trim(),
      content: newLogContent.trim(),
      category: newLogCategory,
      photoUrl: logPhotoUrl || undefined
    };

    let updatedProject: EngineeringProject = { ...activeProject };
    const existingLogs = (activeProject as any).logs || [];
    (updatedProject as any).logs = [newEntry, ...existingLogs];

    let actionsTaken: string[] = [];

    if (smartUpdateProject) {
      const textLower = (newLogTitle + " " + newLogContent).toLowerCase();

      // 1. Inconveniente / Problema: Auto-generate adaptive corrective step and BOM item if parts missing
      if (newLogCategory === "problema" || textLower.includes("fallo") || textLower.includes("problema") || textLower.includes("roto") || textLower.includes("no encaja")) {
        const correctiveStep: PhaseStep = {
          id: `step-fix-${Date.now()}`,
          title: `🛠️ Solución a Incidencia: ${newLogTitle.slice(0, 42)}`,
          description: `Acción correctiva derivada de la nota de bitácora: "${newLogContent.slice(0, 90)}..."`,
          completed: false,
          starterKit: "Herramientas de ajuste, repuesto de ferretería o insumos de sustitución.",
          actionFirst: `Inspeccionar la causa del fallo reportado en: ${newLogTitle}. Ejecutar ajuste dimensional o sustitución de material.`,
          doneCriteria: "Causa raíz corregida y ensayo funcional superado sin recurrencia de la falla."
        };
        const currentPhase = updatedProject.phases[activePhaseTab];
        updatedProject.phases = {
          ...updatedProject.phases,
          [activePhaseTab]: {
            ...currentPhase,
            steps: [correctiveStep, ...currentPhase.steps]
          }
        };
        actionsTaken.push("Añadido paso correctivo prioritario en la fase");

        // If mentioned missing materials, add to BOM
        if (textLower.includes("comprar") || textLower.includes("falta") || textLower.includes("tornillo") || textLower.includes("tubo") || textLower.includes("breaker") || textLower.includes("cemento")) {
          const newBomItem: BOMItem = {
            id: `bom-fix-${Date.now()}`,
            name: `Repuesto / Adaptación: ${newLogTitle.slice(0, 30)}`,
            category: "hardware",
            quantity: 1,
            unit: "ud",
            estimatedPriceUSD: 18.0,
            actualPriceUSD: 0,
            inStock: false,
            localSupplierPR: textLower.includes("national") ? "National Lumber" : textLower.includes("ace") ? "Ferreterías Ace" : "Home Depot PR",
            notes: `Generado por nota de bitácora: ${newLogTitle}`
          };
          updatedProject.bom = [...updatedProject.bom, newBomItem];
          actionsTaken.push("Registrado repuesto pendiente en el BOM");
        }
      }

      // 2. Progreso / Avance: advance steps, update where we are
      else if (newLogCategory === "progreso" || textLower.includes("complet") || textLower.includes("termin") || textLower.includes("listo") || textLower.includes("instalad")) {
        const currentPhase = updatedProject.phases[activePhaseTab];
        const firstPendingIdx = currentPhase.steps.findIndex((s) => !s.completed);
        if (firstPendingIdx !== -1) {
          const updatedSteps = [...currentPhase.steps];
          updatedSteps[firstPendingIdx] = { ...updatedSteps[firstPendingIdx], completed: true };
          updatedProject.phases = {
            ...updatedProject.phases,
            [activePhaseTab]: {
              ...currentPhase,
              steps: updatedSteps
            }
          };
          actionsTaken.push(`Marcado completado: "${updatedSteps[firstPendingIdx].title.slice(0, 30)}..."`);

          // Auto-advance phase if all steps in this phase are done
          const allDone = updatedSteps.every((s) => s.completed);
          if (allDone) {
            const phaseKeys: (keyof typeof activeProject.phases)[] = [
              "preparation", "simulationCad", "inventoryProcurement", "construction", "testingCalibration", "analysisReview"
            ];
            const currentIdx = phaseKeys.indexOf(activePhaseTab);
            if (currentIdx < phaseKeys.length - 1) {
              const nextPhaseKey = phaseKeys[currentIdx + 1];
              setActivePhaseTab(nextPhaseKey);
              actionsTaken.push(`¡Fase completada! Avanzado a: ${updatedProject.phases[nextPhaseKey].label}`);
            }
          }
        }

        // Promote status to in-progress if it was idea-future
        if (updatedProject.status === "idea-future") {
          updatedProject.status = "in-progress";
          actionsTaken.push("Estado del proyecto promovido a 'En Proceso'");
        }
      }

      // 3. Lección Aprendida: save to project's lessons learned
      else if (newLogCategory === "leccion") {
        const currentLessons = updatedProject.lessonsLearned || [];
        updatedProject.lessonsLearned = [...currentLessons, `${newLogTitle}: ${newLogContent}`];
        actionsTaken.push("Registrada lección en la memoria del proyecto");
      }
    }

    setProjectsList((prev) =>
      prev.map((p) => (p.id === activeProject.id ? updatedProject : p))
    );

    setNewLogTitle("");
    setNewLogContent("");
    setLogPhotoUrl(null);

    if (actionsTaken.length > 0) {
      toast.success(`🧠 Prometeo sincronizó el proyecto: ${actionsTaken.join(" · ")}`);
    } else {
      toast.success("✓ Entrada registrada en la bitácora del proyecto.");
    }
  };

  // Reset workshop to clean ideas
  const handleResetToCleanIdeas = () => {
    if (window.confirm("¿Deseas restablecer todos los proyectos a modo 'Ideas Futuras' limpias (sin costos facturados inventados, sin stock ficticio y con todas las tareas por comenzar)?")) {
      localStorage.removeItem("prometheus_engineering_projects");
      localStorage.removeItem("prometheus_lifecycle_projects");
      setProjectsList(DEFAULT_PROJECTS);
      setActiveProjectId(DEFAULT_PROJECTS[0].id);
      setSelectedStatusTab("all");
      toast.success("✓ Taller restablecido: Todos los proyectos ahora son Ideas Futuras en fase de diseño.");
    }
  };

  // Explicit Project Status Switcher
  const updateProjectStatus = (newStatus: "in-progress" | "idea-future" | "completed") => {
    if (!activeProject) return;
    setProjectsList((prevList) =>
      prevList.map((proj) =>
        proj.id === activeProject.id
          ? { ...proj, status: newStatus, updatedAt: new Date().toISOString().split("T")[0] }
          : proj
      )
    );
    const label = newStatus === "idea-future" ? "💡 Idea Futura" : newStatus === "in-progress" ? "🔄 En Proceso" : "🚀 Completado";
    toast.success(`✓ Estado del proyecto actualizado a: ${label}`);
  };

  // Delete Project Handler
  const handleDeleteProject = (projId: string, projTitle: string) => {
    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar permanentemente el proyecto "${projTitle}"?\n\nEsta acción eliminará su lista de materiales, fases y bitácora.`
    );
    if (!confirmDelete) return;

    setProjectsList((prevList) => {
      const updated = prevList.filter((p) => p.id !== projId);
      localStorage.setItem("prometheus_engineering_projects", JSON.stringify(updated));
      localStorage.setItem("prometheus_lifecycle_projects", JSON.stringify(updated));

      // If the deleted project was the currently active one, select another
      if (activeProjectId === projId) {
        if (updated.length > 0) {
          setActiveProjectId(updated[0].id);
        } else {
          setActiveProjectId("");
        }
      }
      return updated;
    });

    toast.success(`✓ Proyecto "${projTitle}" eliminado exitosamente.`);
  };

  // Open Chat with Prometheus with full project context
  const openChatWithPrometheus = (customPrompt?: string, openWorkbenchTab?: any) => {
    if (!activeProject) return;
    const prompt =
      customPrompt ||
      `Prometeo, trabajemos juntos en el proyecto: "${activeProject.title}" (${activeProject.discipline}).
Mi objetivo es desarrollarlo paso a paso. Explícame de forma práctica y sin palabras complicadas:
1. ¿Qué es lo primero que debo hacer para comenzar hoy?
2. ¿Qué insumos o herramientas necesito a la mano?
3. ¿Cuál es el orden lógico para avanzar sin perderme?`;

    window.dispatchEvent(
      new CustomEvent("open-prometheus-chat", {
        detail: {
          projectId: activeProject.id,
          characterId: "prometheus",
          initialPrompt: prompt,
          autoSend: true,
          openWorkbenchTab
        }
      })
    );
    toast.success(`Abriendo chat con Prometeo para "${activeProject.title}"...`);
  };

  // Ask Prometheus specifically about a step
  const askPrometheusAboutStep = (step: PhaseStep, phaseLabel: string) => {
    if (!activeProject) return;
    const prompt = `Prometeo, en mi proyecto "${activeProject.title}", estoy en la fase "${phaseLabel}".
Tengo una duda con el paso: "${step.title}".
Descripción técnica: "${step.description}".
${step.criticalPoints ? `Punto de seguridad: "${step.criticalPoints}".` : ""}

Por favor explícame en cristiano y de forma práctica para un maker:
1. ¿Qué significa esto en palabras sencillas?
2. ¿Qué herramienta física o software debo abrir o usar primero?
3. ¿Cuál es la primera acción concreta con la que arranco?
4. ¿Cómo sé que lo terminé bien para marcarlo "Hecho"?
5. ¿Qué sigue después?`;

    window.dispatchEvent(
      new CustomEvent("open-prometheus-chat", {
        detail: {
          projectId: activeProject.id,
          characterId: "prometheus",
          initialPrompt: prompt,
          autoSend: true
        }
      })
    );
    toast.success(`Consultando paso con Prometeo...`);
  };

  // Dynamically calculate actual billed cost strictly from BOM entries
  const activeProjectActualCost = useMemo(() => {
    if (!activeProject) return 0;
    if (activeProject.bom && activeProject.bom.length > 0) {
      return activeProject.bom.reduce((sum, it) => sum + (it.actualPriceUSD || 0), 0);
    }
    return activeProject.actualCostUSD || 0;
  }, [activeProject]);

  // Calculate Overall Progress % for a project
  const calculateProjectProgress = (proj: EngineeringProject): number => {
    const allPhases = Object.values(proj.phases);
    let totalSteps = 0;
    let completedSteps = 0;
    allPhases.forEach((phase) => {
      phase.steps.forEach((step) => {
        totalSteps++;
        if (step.completed) completedSteps++;
      });
    });
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  // Toggle Step Completion with Smart Status Transitions
  const toggleStep = (phaseKey: keyof EngineeringProject["phases"], stepId: string) => {
    setProjectsList((prevList) =>
      prevList.map((proj) => {
        if (proj.id !== activeProject.id) return proj;
        const currentPhase = proj.phases[phaseKey];
        const updatedSteps = currentPhase.steps.map((st) =>
          st.id === stepId ? { ...st, completed: !st.completed } : st
        );
        const updatedPhases = {
          ...proj.phases,
          [phaseKey]: {
            ...currentPhase,
            steps: updatedSteps
          }
        };

        // Recalculate completed steps across ALL phases
        let totalSteps = 0;
        let completedSteps = 0;
        Object.values(updatedPhases).forEach((phase) => {
          phase.steps.forEach((step) => {
            totalSteps++;
            if (step.completed) completedSteps++;
          });
        });

        // Smart status auto-update
        let newStatus: EngineeringProject["status"] = proj.status;
        if (completedSteps === 0) {
          newStatus = "idea-future";
        } else if (completedSteps === totalSteps && totalSteps > 0) {
          newStatus = "completed";
        } else {
          newStatus = "in-progress";
        }

        return {
          ...proj,
          status: newStatus,
          updatedAt: new Date().toISOString().split("T")[0],
          phases: updatedPhases
        };
      })
    );
  };

  // Toggle BOM Item in stock
  const toggleBOMStock = (bomId: string) => {
    setProjectsList((prevList) =>
      prevList.map((proj) => {
        if (proj.id !== activeProject.id) return proj;
        return {
          ...proj,
          updatedAt: new Date().toISOString().split("T")[0],
          bom: proj.bom.map((item) =>
            item.id === bomId ? { ...item, inStock: !item.inStock } : item
          )
        };
      })
    );
  };

  // Update BOM item price (Preestipulado vs Real Facturado)
  const updateBOMPrice = (bomId: string, field: "estimatedPriceUSD" | "actualPriceUSD", value: number) => {
    const safeVal = Math.max(0, isNaN(value) ? 0 : value);
    setProjectsList((prevList) =>
      prevList.map((proj) => {
        if (proj.id !== activeProject.id) return proj;
        const updatedBOM = proj.bom.map((item) =>
          item.id === bomId ? { ...item, [field]: safeVal } : item
        );
        // Calculate new actual cost sum strictly from actual invoiced prices
        const totalActual = updatedBOM.reduce((sum, it) => sum + (it.actualPriceUSD || 0), 0);
        const totalEst = updatedBOM.reduce((sum, it) => sum + (it.estimatedPriceUSD || 0), 0);
        return {
          ...proj,
          updatedAt: new Date().toISOString().split("T")[0],
          estimatedBudgetUSD: totalEst,
          actualCostUSD: totalActual,
          bom: updatedBOM
        };
      })
    );
  };

  // Filtered projects
  const filteredProjects = useMemo(() => {
    return projectsList.filter((proj) => {
      const matchesStatus = selectedStatusTab === "all" || proj.status === selectedStatusTab;
      const matchesDiscipline = selectedDiscipline === "all" || proj.discipline === selectedDiscipline;
      const matchesEcosystem = selectedEcosystem === "all" || 
        (selectedEcosystem === "aura-os" && proj.ecosystem === "aura-os") ||
        (selectedEcosystem === "prometheus-lab" && proj.ecosystem !== "aura-os");

      const q = searchQuery.toLowerCase().trim();
      const matchesQuery = !q ||
        proj.title.toLowerCase().includes(q) ||
        proj.description.toLowerCase().includes(q) ||
        proj.discipline.toLowerCase().includes(q);
      return matchesStatus && matchesDiscipline && matchesEcosystem && matchesQuery;
    });
  }, [projectsList, selectedStatusTab, selectedDiscipline, selectedEcosystem, searchQuery]);

  // AI Project Creation Handler
  const handleCreateProjectAI = async () => {
    if (!newTitle.trim()) return;
    setIsAiGenerating(true);
    try {
      // In-browser / Backend AI synthesis
      const newProj: EngineeringProject = {
        id: `proj-${Date.now()}`,
        title: newTitle,
        discipline: newDiscipline,
        status: newStatus,
        priority: "Alta",
        difficulty: "Medio",
        targetCompletionDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        description: newDescription || `Proyecto de ingeniería enfocado en ${newTitle} para fabricación digital y despliegue local.`,
        problemStatement: newProblem || `Resolver necesidades prácticas de ${newDiscipline} con componentes accesibles en Puerto Rico.`,
        scientificPrinciple: newPrinciple || "Principios aplicados de física e ingeniería para optimización de recursos y tolerancia mecánica.",
        estimatedBudgetUSD: Number(newBudget) || 45,
        actualCostUSD: 0,
        filamentGramsPETG: 250,
        prResilienceNotes: "Uso de materiales resistentes a salitre, corrosión marina y humedad relativa del 80%+ en Puerto Rico. Envíos de electrónica recomendados vía USPS doméstico.",
        bom: [
          { id: `b-${Date.now()}-1`, name: "Carcasa y Soportes Principales", category: "3d-print", quantity: 1, unit: "set", specs: "PETG / Infill 30%", estimatedPriceUSD: 12.00, inStock: false, localSupplierPR: "Taller Local 3D" },
          { id: `b-${Date.now()}-2`, name: "Microcontrolador / Módulo Principal", category: "electronics", quantity: 1, unit: "unidad", specs: "ESP32 / Arduino Compatible", estimatedPriceUSD: 15.00, inStock: false, localSupplierPR: "Mouser / Digi-Key via USPS" },
          { id: `b-${Date.now()}-3`, name: "Tornillería y Herrajes en Inox", category: "hardware", quantity: 1, unit: "paquete", specs: "M3/M4 Acero Inoxidable 316", estimatedPriceUSD: 8.00, inStock: false, localSupplierPR: "National Lumber & Hardware / Ace" }
        ],
        phases: {
          preparation: {
            name: "preparation",
            label: "1. Preparación & Especificación",
            steps: [
              { id: `step-${Date.now()}-1`, title: "Definir Alcance y Tolerancias", description: "Especificar cotas dimensionales críticas y presupuesto máximo.", completed: false },
              { id: `step-${Date.now()}-2`, title: "Evaluación Normativa y Ambiental PR", description: "Verificar resistencia a humedad, salitre y temperaturas de operación.", completed: false }
            ]
          },
          simulationCad: {
            name: "simulationCad",
            label: "2. Simulación & Diseño CAD",
            steps: [
              { id: `step-${Date.now()}-3`, title: "Modelado Paramétrico 3D", description: "Generar ensambles en CAD con verificación de interferencias y holguras.", completed: false },
              { id: `step-${Date.now()}-4`, title: "Simulación de Esfuerzos / Circuito", description: "Validar tensiones mecánicas o disipación térmica antes de fabricar.", completed: false }
            ]
          },
          inventoryProcurement: {
            name: "inventoryProcurement",
            label: "3. Inventario & Aprovisionamiento BOM",
            steps: [
              { id: `step-${Date.now()}-5`, title: "Verificación de Materiales en Taller", description: "Revisar filamento, tornillos y sensores en stock.", completed: false },
              { id: `step-${Date.now()}-6`, title: "Compras en Tiendas Locales / USPS", description: "Adquirir insumos faltantes en Ferreterías locales o pedido USPS.", completed: false }
            ]
          },
          construction: {
            name: "construction",
            label: "4. Construcción & Fabricación",
            steps: [
              { id: `step-${Date.now()}-7`, title: "Fabricación de Piezas (3D / Mecanizado)", description: "Imprimir en 3D y preparar herrajes con tolerancias exactas.", completed: false },
              { id: `step-${Date.now()}-8`, title: "Ensamble Mecánico y Cableado", description: "Montar componentes mecánicos y soldar conexiones eléctricas protegidas.", completed: false }
            ]
          },
          testingCalibration: {
            name: "testingCalibration",
            label: "5. Pruebas & Calibración",
            steps: [
              { id: `step-${Date.now()}-9`, title: "Pruebas Funcionales y Mediciones", description: "Medir voltajes, corrientes, torque y comportamiento bajo carga.", completed: false },
              { id: `step-${Date.now()}-10`, title: "Calibración de Sensores y Ajustes", description: "Ajustar parámetros para máxima precisión y fiabilidad.", completed: false }
            ]
          },
          analysisReview: {
            name: "analysisReview",
            label: "6. Análisis de Resultados & Optimización",
            steps: [
              { id: `step-${Date.now()}-11`, title: "Comparación de Rendimiento vs Teórico", description: "Evaluar métricas finales y documentar lecciones aprendidas.", completed: false }
            ]
          }
        },
        createdAt: new Date().toISOString().split("T")[0],
        updatedAt: new Date().toISOString().split("T")[0]
      };

      setProjectsList([newProj, ...projectsList]);
      setActiveProjectId(newProj.id);
      setIsCreatorOpen(false);
      setNewTitle("");
      setNewDescription("");
      setNewProblem("");
      setNewPrinciple("");
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Export Project Report as Printable Document
  const exportFullProjectReport = () => {
    const proj = activeProject;
    const progress = calculateProjectProgress(proj);
    
    const bomRows = proj.bom.map((b) => `
      <tr>
        <td style="padding:6px 10px; border-bottom:1px solid #ddd;">${b.name}</td>
        <td style="padding:6px 10px; border-bottom:1px solid #ddd; text-align:center;">${b.quantity} ${b.unit}</td>
        <td style="padding:6px 10px; border-bottom:1px solid #ddd; font-weight:bold; color:#EA580C;">$${b.estimatedPriceUSD.toFixed(2)}</td>
        <td style="padding:6px 10px; border-bottom:1px solid #ddd;">${b.localSupplierPR}</td>
        <td style="padding:6px 10px; border-bottom:1px solid #ddd; text-align:center;">${b.inStock ? '<span style="color:#059669; font-weight:bold;">EN STOCK</span>' : '<span style="color:#DC2626;">FALTANTE</span>'}</td>
      </tr>
    `).join("");

    const phaseBlocks = Object.values(proj.phases).map((phase) => {
      const stepsHtml = phase.steps.map((st) => `
        <li style="margin-bottom:8px;">
          <strong>[${st.completed ? "✓ COMPLETADO" : "PENDIENTE"}] ${st.title}</strong>: ${st.description}
          ${st.criticalPoints ? `<br/><span style="color:#D97706; font-size:11px;">⚠️ Punto Crítico: ${st.criticalPoints}</span>` : ""}
        </li>
      `).join("");

      return `
        <div style="margin-top:18px; padding:12px; background:#f8fafc; border-left:4px solid #EA580C; border-radius:4px;">
          <h3 style="margin:0 0 8px; font-size:14px; color:#0f172a;">${phase.label}</h3>
          <ul style="margin:0; padding-left:18px; font-size:12px; line-height:1.6; color:#334155;">
            ${stepsHtml}
          </ul>
        </div>
      `;
    }).join("");

    const printHtml = `<!DOCTYPE html>
<html>
<head>
  <title>${proj.title} — Ficha de Proyecto</title>
  <style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1e293b; max-width: 850px; margin: 0 auto; padding: 40px; }
    h1 { color: #0f172a; font-size: 24px; margin-bottom: 4px; }
    .badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-right: 6px; }
    .badge-status { background: #ffedd5; color: #c2410c; }
    .badge-discipline { background: #e0f2fe; color: #0369a1; }
    .progress-bar { height: 10px; background: #e2e8f0; border-radius: 5px; overflow: hidden; margin: 15px 0; }
    .progress-fill { height: 100%; background: #ea580c; width: ${progress}%; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
    th { background: #0f172a; color: white; padding: 8px 10px; text-align: left; }
    .pr-box { background: #ecfdf5; border: 1px solid #a7f3d0; padding: 12px; border-radius: 6px; margin: 15px 0; font-size: 12px; color: #065f46; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div style="font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:1px; margin-bottom:4px;">PROMETHEUS LAB AI · PUERTO RICO</div>
  <h1>${proj.title}</h1>
  <div style="margin: 10px 0;">
    <span class="badge badge-status">${proj.status.toUpperCase()}</span>
    <span class="badge badge-discipline">${proj.discipline}</span>
    <span class="badge" style="background:#f1f5f9; color:#475569;">Dificultad: ${proj.difficulty}</span>
    <span class="badge" style="background:#f1f5f9; color:#475569;">Presupuesto: $${proj.estimatedBudgetUSD.toFixed(2)} USD</span>
  </div>

  <div class="progress-bar">
    <div class="progress-fill"></div>
  </div>
  <div style="font-size:11px; color:#64748b; margin-bottom:20px;">Progreso de Ingeniería: <strong>${progress}%</strong> Completado</div>

  <p style="font-size:13px; line-height:1.6; color:#334155;"><strong>Descripción:</strong> ${proj.description}</p>
  <p style="font-size:13px; line-height:1.6; color:#334155;"><strong>Problema a Resolver:</strong> ${proj.problemStatement}</p>
  <p style="font-size:13px; line-height:1.6; color:#334155;"><strong>Principio Científico:</strong> ${proj.scientificPrinciple}</p>

  <div class="pr-box">
    <strong>🇵🇷 Contexto de Resiliencia en Puerto Rico:</strong> ${proj.prResilienceNotes}
  </div>

  <h2 style="font-size:16px; border-bottom:2px solid #ea580c; padding-bottom:4px; margin-top:25px;">Lista de Materiales e Inventario Requerido (BOM)</h2>
  <table>
    <thead>
      <tr>
        <th>Componente / Material</th>
        <th>Cantidad</th>
        <th>Costo Est.</th>
        <th>Canal de Abasto (PR / USPS)</th>
        <th>Estado</th>
      </tr>
    </thead>
    <tbody>
      ${bomRows}
    </tbody>
  </table>

  <h2 style="font-size:16px; border-bottom:2px solid #ea580c; padding-bottom:4px; margin-top:30px;">Plan de Ingeniería por Fases (Paso a Paso)</h2>
  ${phaseBlocks}

  <div style="margin-top:40px; font-size:10px; color:#94a3b8; text-align:center; border-top:1px solid #e2e8f0; padding-top:15px;">
    Generado por Prometheus Lab AI · San Juan, Puerto Rico · ${new Date().toLocaleDateString()}
  </div>
</body>
</html>`;

    const win = window.open("", "_blank");
    if (win) {
      win.document.write(printHtml);
      win.document.close();
      setTimeout(() => win.print(), 400);
    }
  };

  const currentPhaseData = activeProject ? activeProject.phases[activePhaseTab] : null;
  const activeProgress = activeProject ? calculateProjectProgress(activeProject) : 0;

  // ── Subsistemas 3D Dinámicos para Despiece, Montaje y Blueprints ──
  const projectSubsystems3D = useMemo(
    () => getProjectSubsystems3D(activeProject?.title || ""),
    [activeProject?.title]
  );

  // ── Conexiones de Hardware en 3D (6 Cables Paso a Paso con Explicación Física) ──
  const projectHardwareWires3D = useMemo(() => [
    {
      id: "wire-1",
      title: "Cable 1: Línea Positiva Principal (+VCC Potencia)",
      from: "Batería LiFePO4 / Fuente DC (+) → Fusible → Entrada Driver VIN",
      color: "#ef4444",
      gauge: "AWG 14 / AWG 16 Silicona",
      voltage: "12V / 24V DC Alta Corriente",
      whyItMatters: "Transporta la energía bruta hacia el actuador/motor. SIEMPRE debe pasar primero por un fusible a menos de 15 cm de la batería para evitar incendios si ocurre un cortocircuito.",
      mistakeWarning: "Nunca conectes esta línea de 12V/24V directo al pin 3.3V del ESP32 o quemarás el regulador instantáneamente."
    },
    {
      id: "wire-2",
      title: "Cable 2: Masa Común en Estrella (GND Reference)",
      from: "Negativo Batería (-) ↔ GND Regulador Buck ↔ GND ESP32 ↔ GND Driver",
      color: "#94a3b8",
      gauge: "AWG 16 / AWG 18 Negro",
      voltage: "0.0V (Referencia Común Universal)",
      whyItMatters: "El voltaje es una diferencia de potencial. Si el ESP32 y el Driver no comparten el mismo GND (0V), los pulsos de control de 3.3V no tienen referencia y el motor se vuelve loco o no responde.",
      mistakeWarning: "Error #1 de principiantes: alimentar el motor con una batería y el ESP32 por USB sin unir ambos negativos (GND)."
    },
    {
      id: "wire-3",
      title: "Cable 3: Alimentación Lógica Regulada (+5V / +3.3V Limpios)",
      from: "Salida Buck Converter (5.0V) → Pin VIN / 5V del ESP32-S3 y Sensores",
      color: "#f97316",
      gauge: "AWG 22 Rojo/Naranja",
      voltage: "5.0V → 3.3V LDO Interno",
      whyItMatters: "El convertidor Buck reduce eficientemente los 12V/24V a 5V sin desperdiciar energía en calor (92% eficiencia vs 30% de un regulador lineal 7805).",
      mistakeWarning: "Mide siempre con el multímetro la salida del módulo LM2596 y ajústalo a 5.0V ANTES de soldar o enchufar el microcontrolador."
    },
    {
      id: "wire-4",
      title: "Cable 4: Señal de Control / Modulación PWM / STEP",
      from: "ESP32 GPIO 5 / GPIO 18 (Salida Digital) → Entrada IN1 / STEP del Driver",
      color: "#eab308",
      gauge: "AWG 24 Amarillo",
      voltage: "Pulsos Lógicos 3.3V (0 a 20 kHz)",
      whyItMatters: "El microcontrolador solo envía micro-pulsos de información (menos de 5 mA), y el Driver (MOSFET/Puente H) los usa como gatillo para dejar pasar los amperios pesados hacia el motor.",
      mistakeWarning: "Coloca una resistencia de 10 kΩ entre el GPIO y GND (pull-down) para que el motor no dé un salto brusco mientras el ESP32 arranca."
    },
    {
      id: "wire-5",
      title: "Cable 5: Bus de Datos I2C (SDA + SCL) para Sensores y Pantalla",
      from: "ESP32 GPIO 21 (SDA) y GPIO 22 (SCL) → Pantalla OLED & Sensor Precisión",
      color: "#38bdf8",
      gauge: "AWG 24 Azul / Par Trenzado",
      voltage: "3.3V Bidireccional con Pull-Up 4.7 kΩ",
      whyItMatters: "Con solo 2 cables de datos puedes conectar hasta 127 sensores y pantallas en paralelo, ya que cada dispositivo tiene su propia dirección hexadecimal única (ej. 0x3C para OLED, 0x40 para INA226).",
      mistakeWarning: "Mantén los cables I2C cortos (< 30 cm) o usa cable trenzado lejos de los cables del motor para evitar interferencia electromagnética (EMI)."
    },
    {
      id: "wire-6",
      title: "Cable 6: Salida de Potencia al Actuador + Protección Flyback",
      from: "Salida Driver (OUT+ / OUT-) → Terminales del Motor / Solenoide / Bomba",
      color: "#10b981",
      gauge: "AWG 14 / AWG 16 Verde/Rojo",
      voltage: "12V/24V Conmutados + Diodo 1N5819 / 1N4007 en antiparalelo",
      whyItMatters: "Al apagar una bobina (motor, relé o válvula), el campo magnético colapsa y genera un pico inverso de hasta -100V (Ley de Faraday-Lenz). El diodo Flyback recircula esa corriente de forma segura sin quemar el transistor.",
      mistakeWarning: "La franja plateada (cátodo) del diodo Flyback va SIEMPRE hacia el polo positivo (+), nunca al revés."
    }
  ], []);

  // ── Generador de Explicación de Física Natural + Pregunta Socrática por Paso ──
  const getStepSocraticPhysics = (step: PhaseStep, idx: number) => {
    const t = (step.title + " " + step.description).toLowerCase();
    if (t.includes("cad") || t.includes("3d") || t.includes("tolerancia") || t.includes("impres") || t.includes("holgura")) {
      return {
        pillar: "cinematica",
        pillarLabel: "Cinemática & Tolerancias CAD",
        naturalAnalogy: "En el mundo digital CAD una pieza de 10.00 mm entra en un agujero de 10.00 mm. En el mundo físico real, el plástico fundido se expande y las paredes tienen rugosidad microscópica: si diseñas 10 con 10, se traba por interferencia.",
        formula: "Holgura radial: Δr = (D_agujero - D_eje) / 2 ≈ 0.15 mm a 0.25 mm (Ajuste Deslizante) | 0.05 mm (Ajuste a Presión)",
        question: "Si vas a insertar un rodamiento metálico 608RS (diámetro exterior exacto de 22.00 mm) a presión suave dentro de una pieza impresa en PETG, ¿de qué diámetro debes dibujar el alojamiento en tu CAD?",
        options: [
          "Exactamente 22.00 mm sin margen",
          "22.15 mm a 22.20 mm para compensar la contracción interna del polímero al enfriarse",
          "24.00 mm y rellenar con pegamento"
        ],
        correctIdx: 1,
        explanation: "¡Exacto! Los agujeros internos en impresión FDM tienden a cerrarse ~0.15 mm por la tensión del filamento al trazar círculos. Dar +0.15 a +0.20 mm logra un ajuste firme sin partir la capa."
      };
    }
    if (t.includes("cable") || t.includes("volt") || t.includes("esp32") || t.includes("bater") || t.includes("fusible") || t.includes("eléctric")) {
      return {
        pillar: "mecatronica",
        pillarLabel: "Mecatrónica & Potencia Eléctrica",
        naturalAnalogy: "Imagina la corriente eléctrica (Amperios) como el caudal de agua en una tubería y el voltaje (Voltios) como la presión. Si intentas pasar mucha agua por un tubo muy fino (cable delgado), la fricción calienta el tubo y pierdes presión al llegar al final.",
        formula: "Caída de tensión: ΔV = 2 · L · I · ρ / A  |  Potencia disipada en calor: P_calor = I² · R",
        question: "Si duplicas la corriente (Amperios) que pasa por un mismo cable o conexión floja, ¿qué pasa con el calor generado en ese cable según la Ley de Joule (P = I² · R)?",
        options: [
          "El calor se mantiene igual",
          "El calor apenas sube un 10%",
          "El calor se multiplica por 4 (2² = 4), por eso los cables subdimensionados se derriten rápido"
        ],
        correctIdx: 2,
        explanation: "¡Brillante! Como la corriente está elevada al cuadrado (I²), duplicar los amperios cuadruplica el calor disipado. Por eso la selección del calibre AWG y el apriete firme de bornes es sagrado."
      };
    }
    if (t.includes("bloque") || t.includes("concreto") || t.includes("viento") || t.includes("huracán") || t.includes("anclaje") || t.includes("losa")) {
      return {
        pillar: "arquitectura_pr",
        pillarLabel: "Estructuras & Resiliencia Huracán PR",
        naturalAnalogy: "El hormigón es una roca artificial invencible cuando la aprietas (compresión), pero se quiebra fácil si la estiras (tracción). El acero (varilla corrugada) hace exactamente lo opuesto: trabaja como los tendones dentro del hueso para resistir cuando el huracán intenta doblar el muro.",
        formula: "Presión dinámica de viento (ASCE 7): q_z = 0.00256 · K_z · K_zt · K_d · V² (lb/ft² con V en mph)",
        question: "Cuando un huracán pasa de 100 mph a 175 mph en Puerto Rico, ¿por qué la fuerza contra el techo y las paredes aumenta más del triple en lugar de subir solo un 75%?",
        options: [
          "Porque la presión del viento crece con el CUADRADO de la velocidad (V²): (175/100)² = 3.06 veces más fuerza",
          "Porque el aire pesa menos en verano",
          "Porque los bloques absorben aire"
        ],
        correctIdx: 0,
        explanation: "¡Dominio total de mecánica de fluidos! La energía cinética del aire es ½·ρ·V². Por eso pasar de 100 a 175 mph triplica la carga estructural y exige amarres continuos desde el techo hasta la zapata."
      };
    }
    return {
      pillar: idx % 2 === 0 ? "estatica" : "termofluidos",
      pillarLabel: "Primeros Principios de Ingeniería Mecánica",
      naturalAnalogy: "En ingeniería mecánica, ninguna pieza falla por casualidad: falla donde las líneas de fuerza se estrangulan (concentradores de esfuerzo en esquinas vivas a 90°) o donde el momento de palanca supera el límite elástico del material.",
      formula: "Torque: τ = F · d  |  Esfuerzo Normal: σ = F / A  |  Factor de Seguridad: FoS = σ_fluencia / σ_trabajo ≥ 2.0",
      question: "Al diseñar o montar un soporte que sostiene carga en voladizo, ¿cuál es la forma más inteligente y económica de duplicar su resistencia sin gastar el doble de material?",
      options: [
        "Pintarlo de un color más oscuro",
        "Añadir un chaflán/redondeo (fillet) en la esquina interior a 90° y una costilla triangular (nervadura) alineada con la fuerza",
        "Hacer los agujeros de los tornillos más grandes"
      ],
      correctIdx: 1,
      explanation: "¡Exacto! Una esquina interior recta a 90° multiplica el estrés local hasta por 3x. Un simple redondeo (fillet) y una costilla triangular convierten flexión en compresión pura con casi 0 gramos extra."
    };
  };

  // ── Catálogo de Ingeniería Inversa ("¿Cómo lo hicieron? → Analizar y Recrear") ──
  const REVERSE_ENGINEERING_CATALOG = useMemo(() => [
    {
      title: "Actuador Cicloidal / Planetario Compacto de Alto Torque (Estilo Robótica MIT / MechAgent)",
      category: "Mecánica & Robótica",
      commercialPriceUSD: 420,
      diyPricePR: 48,
      secretPrinciple: "Contacto multipunto por rodadura excéntrica (Cicloidal) o reparto en 3 satélites (Planetario). En vez de que 1 solo diente cargue todo el peso (y se parta), la fuerza se reparte entre 6 a 12 dientes simultáneamente.",
      kinematicExplanation: "El eje del motor mueve una leva excéntrica (o engranaje sol) que hace orbitar un disco lobulado dentro de pines fijos. Por cada vuelta completa del motor, la salida avanza solo 1 diente en sentido opuesto: logras reducción 15:1 o 30:1 en apenas 25 mm de espesor sin holgura (zero-backlash).",
      subsystems: [
        { name: "Disco Cicloidal / Porta-satélites impreso en PETG-CF", spec: "4 perímetros, 40% infill giroidal, tolerancia 0.15mm", cost: 8.50, supplierPR: "Taller Local 3D" },
        { name: "Motor Paso a Paso NEMA 17 Alto Torque (48mm) o BLDC", spec: "1.8°, 0.55 N·m base, eje D 5mm", cost: 16.00, supplierPR: "Amazon / Mouser USPS" },
        { name: "Juego de Rodamientos 608RS y 6705RS Sellados", spec: "Acero al cromo ABEC-7 con sello de goma", cost: 14.00, supplierPR: "Amazon / Rodamientos PR" },
        { name: "Pines de Rodadura y Tornillería M3/M4 Inox 316", spec: "Pasadores cilíndricos Ø4mm + insertos latón", cost: 9.50, supplierPR: "National Lumber / Ace PR" }
      ],
      steps: [
        "Imprimir la corona fija y el disco cicloidal/planetario en PETG-CF verificando holgura radial de 0.15 mm con calibrador.",
        "Insertar los rodamientos 608RS en la leva excéntrica y los pines de acero inoxidable en el anillo perimetral.",
        "Acoplar el motor NEMA 17 asegurando el prisionero M3 con fijador de roscas (Loctite azul) sobre la cara plana del eje.",
        "Aplicar grasa sintética con PTFE, cerrar la tapa frontal en cruz y medir el par de salida con brazo de palanca."
      ]
    },
    {
      title: "Casa Modular Bioclimática & Autosuficiente Anti-Huracán (175 mph PR)",
      category: "Estructuras & Resiliencia PR",
      commercialPriceUSD: 145000,
      diyPricePR: 42800,
      secretPrinciple: "Geometría aerodinámica + Masa térmica ventilada + Ciclo cerrado de agua y sol. En lugar de pelear contra el calor y el viento a fuerza de aire acondicionado y vigas sobredimensionadas, usa física pasiva.",
      kinematicExplanation: "1) Los aleros a 30° y el pretil ventilado rompen el vórtice de succión del huracán (Efecto Bernoulli). 2) El efecto chimenea solar saca el aire caliente por el punto más alto sin gastar electricidad. 3) El techo entero actúa como embudo pluvial filtrado hacia cisterna soterrada (enfría el agua a 22°C bajo tierra) y base para 12 placas solares.",
      subsystems: [
        { name: "Losa Monolítica y Muros de Bloque 8\" Armados", spec: "Hormigón 4000 PSI + Varilla #5 @ 12\" rellenos de grout", cost: 18500, supplierPR: "National Lumber / Cemex PR" },
        { name: "Planta Solar Off-Grid 6kW + Banco LiFePO4 15 kWh", spec: "Inversor híbrido 120/240V Split-Phase + Rieles Inox 175mph", cost: 8900, supplierPR: "Distribuidores Solares PR / USPS" },
        { name: "Sistema Pluvial 1,200 Gal + Filtración Triple UV", spec: "Desviador de primeras aguas + Bomba 0.75 HP + Filtro 5 micras", cost: 2400, supplierPR: "Home Depot PR / Ferreterías Ace" },
        { name: "Cerramientos Térmicos, Taller Integrado & Domótica ESP32", spec: "Ventanas seguridad Miami-Dade + Red sensores LoRa/WiFi", cost: 13000, supplierPR: "Maderas 3C / Taller Prometheus" }
      ],
      steps: [
        "Trazar la orientación bioclimática (fachada larga Norte-Sur para minimizar sol directo Este-Oeste en Puerto Rico).",
        "Excavar y fundir zapata corrida con bastones de varilla #5 anclados continuamente hasta la viga de corona superior.",
        "Dejar embebidos los pases hidráulicos (cisterna pluvial y aguas grises para huerto) y eléctricos (240V taller e inversor) antes de vaciar.",
        "Instalar el banco solar LiFePO4, la recolección pluvial con desviador de primeras lluvias y el tablero de control ESP32."
      ]
    },
    {
      title: "Suspensión Mecánica Rocker-Bogie (Estilo Rover NASA — Sube Obstáculos de 2x su Rueda)",
      category: "Robótica",
      commercialPriceUSD: 850,
      diyPricePR: 74,
      secretPrinciple: "Cinemática diferencial sin resortes: mantiene las 6 ruedas pegadas al suelo con presión igualada aunque una rueda suba una roca vertical, evitando que el chasis vuelque.",
      kinematicExplanation: "Dos brazos articulados (Rocker y Bogie) en cada lado se conectan al chasis central mediante una barra diferencial (o engranaje cónico central). Si el lado izquierdo sube 20°, el diferencial obliga al lado derecho a compensar y el cuerpo central solo se inclina la mitad (10°), manteniendo el centro de gravedad estable.",
      subsystems: [
        { name: "Eslabones Tubulares Rocker-Bogie + Articulaciones", spec: "Tubo aluminio 3/4\" o perfil cuadrado + codos PETG-CF", cost: 22.00, supplierPR: "Home Depot PR + Impresión 3D" },
        { name: "Barra Diferencial Central con Rótulas (Heim Joints M5)", spec: "Acero inoxidable con giro esférico libre", cost: 14.00, supplierPR: "Amazon / USPS" },
        { name: "6× Motores DC con Caja Reductora Planetaria 12V 60 RPM", spec: "Alto par en ruedas independientes con llantas de alto agarre", cost: 26.00, supplierPR: "Amazon / Mouser USPS" },
        { name: "Controlador Dual Puente-H + ESP32-S3 Telemetría", spec: "Control diferencial PWM + sensor IMU MPU6050", cost: 12.00, supplierPR: "Taller Local / USPS" }
      ],
      steps: [
        "Cortar los brazos principales respetando la proporción geométrica áurea de pivote (ángulo de 120° en el bogie delantero).",
        "Montar el pivote central con doble rodamiento para soportar el momento flector lateral del vehículo.",
        "Conectar la barra diferencial superior con rótulas esféricas para promediar el ángulo de cabeceo del chasis.",
        "Cablear los 3 motores de cada lado en paralelo hacia el driver de potencia y probar ascenso sobre escalón."
      ]
    },
    {
      title: "Sistema Hidropónico & Riego por Efecto Venturi (Oxigenación sin Piezas Móviles)",
      category: "Energía & Solar",
      commercialPriceUSD: 520,
      diyPricePR: 62,
      secretPrinciple: "Ecuación de Bernoulli (P₁ + ½ρv₁² = P₂ + ½ρv₂²): al estrechar un tubo de agua a la mitad, la velocidad del agua se cuadruplica y la presión cae por debajo de la presión atmosférica, succionando aire y fertilizante gratis sin compresor.",
      kinematicExplanation: "Una sola bomba solar DC impulsa el agua a través de una boquilla cónica convergente-divergente (Venturi). En la garganta estrecha se genera vacío natural que aspira oxígeno puro en microburbujas directas a las raíces de las plantas, duplicando la velocidad de crecimiento en el patio.",
      subsystems: [
        { name: "Inyector Venturi Paramétrico Impreso + Tubería PVC", spec: "Garganta Ø 4mm con ángulo difusor de 7° en PETG", cost: 14.00, supplierPR: "Home Depot PR + Taller 3D" },
        { name: "Bomba Sumergible Brushless 12V DC Silenciosa", spec: "800 L/h · Elevación 4m · Consumo solo 18W", cost: 19.00, supplierPR: "Amazon USPS" },
        { name: "Panel Solar 30W + Controlador con Temporizador ESP32", spec: "Ciclo automático 15 min ON / 45 min OFF", cost: 29.00, supplierPR: "Taller Local / USPS" }
      ],
      steps: [
        "Imprimir o ensamblar el tubo Venturi cuidando que el cono de salida tenga 7° de apertura gradual para no perder presión.",
        "Conectar la toma lateral de succión en el punto más estrecho de la garganta con una válvula antirretorno.",
        "Instalar las torres/tubos de PVC de 4 pulgadas con pendiente del 1.5% de retorno al tanque reservorio.",
        "Conectar la bomba de 12V al sistema solar y verificar la nube de microburbujas de oxígeno en el agua."
      ]
    }
  ], []);

  const handleAnalyzeCustomReverse = () => {
    const q = customReverseQuery.trim();
    if (!q) return;
    const lower = q.toLowerCase();
    const isHouse = lower.includes("casa") || lower.includes("techo") || lower.includes("muro") || lower.includes("estructura") || lower.includes("taller");
    const isElec = lower.includes("solar") || lower.includes("bater") || lower.includes("sensor") || lower.includes("dron") || lower.includes("iot");

    setCustomReverseResult({
      title: `Deconstrucción & Recreación: ${q}`,
      category: isHouse ? "Estructuras & Resiliencia PR" : isElec ? "Electrónica & IoT" : "Mecánica & CAD",
      commercialPriceUSD: isHouse ? 8500 : 380,
      diyPricePR: isHouse ? 2400 : 54,
      secretPrinciple: `Ingeniería de Primeros Principios aplicada a "${q}": reemplaza piezas propietarias costosas por perfiles estructurales estándar (Aluminio/PVC/Bloque en PR), transmisión impresa en PETG-CF y control electrónico abierto con ESP32-S3.`,
      kinematicExplanation: `Para lograr cómo funciona "${q}", el sistema se divide en 3 capas desacopladas: 1) Chasis rígido que absorbe las fuerzas externas sin deformarse (Factor de Seguridad > 2.5), 2) Mecanismo transmisor de energía/movimiento de baja fricción, y 3) Lazo de retroalimentación inteligente con sensores que corrige errores en tiempo real.`,
      subsystems: [
        { name: `1. Estructura Principal & Soportes de "${q}"`, spec: "Diseño modular acotado resistente a corrosión PR", cost: isHouse ? 1200 : 18, supplierPR: "Home Depot PR / National Lumber" },
        { name: `2. Actuador / Mecanismo Funcional Central`, spec: "Transmisión de par con rodamientos sellados + PETG-CF", cost: isHouse ? 750 : 21, supplierPR: "Taller Local 3D + Amazon USPS" },
        { name: `3. Electrónica de Control & Alimentación Protegida`, spec: "ESP32-S3 + Etapa MOSFET/Relé + Fusible", cost: isHouse ? 450 : 15, supplierPR: "Mouser / Digi-Key USPS" }
      ],
      steps: [
        `Levantar el croquis dimensional de "${q}" definiendo las medidas máximas y los puntos de anclaje.`,
        `Modelar y fabricar el subconjunto mecánico validando holguras de 0.20 mm antes de ensamblar.`,
        `Montar la etapa de potencia y control verificando masa común (GND) y tensiones con multímetro.`,
        `Realizar prueba de carga progresiva (25%, 50%, 100%) y ajustar calibración final.`
      ]
    });
    awardSkillMastery("cinematica", 10, `Ingeniería Inversa de ${q}`);
  };

  const handleRecreateFromReverse = (item: {
    title: string;
    category: string;
    commercialPriceUSD: number;
    diyPricePR: number;
    secretPrinciple: string;
    kinematicExplanation: string;
    subsystems: { name: string; spec: string; cost: number; supplierPR: string }[];
    steps: string[];
  }) => {
    const nowId = Date.now();
    const newProj: EngineeringProject = {
      id: `proj-rev-${nowId}`,
      title: item.title,
      discipline: (item.category as any) || "Mecánica & CAD",
      status: "in-progress",
      priority: "Alta",
      difficulty: "Medio",
      targetCompletionDate: new Date(nowId + 21 * 86400000).toISOString().split("T")[0],
      description: `${item.secretPrinciple} ${item.kinematicExplanation}`,
      problemStatement: `Recrear funcionalmente en el taller de Puerto Rico por ~$${item.diyPricePR} USD un sistema cuyo equivalente comercial cuesta ~$${item.commercialPriceUSD} USD (Ahorro estimado: $${item.commercialPriceUSD - item.diyPricePR} USD).`,
      scientificPrinciple: item.secretPrinciple,
      estimatedBudgetUSD: item.diyPricePR,
      actualCostUSD: 0,
      filamentGramsPETG: 320,
      prResilienceNotes: "Adaptado con tornillería Inox 316, polímeros resistentes a humedad/UV tropical y componentes disponibles vía Home Depot PR, National Lumber y USPS.",
      bom: item.subsystems.map((sub, idx) => ({
        id: `bom-rev-${nowId}-${idx}`,
        name: sub.name,
        category: idx === 0 ? "3d-print" : idx === 1 ? "hardware" : "electronics",
        quantity: 1,
        unit: "set",
        specs: sub.spec,
        estimatedPriceUSD: sub.cost,
        actualPriceUSD: 0,
        inStock: false,
        localSupplierPR: sub.supplierPR
      })),
      phases: {
        preparation: {
          name: "preparation",
          label: "1. Deconstrucción & Planos Acotados",
          steps: [
            {
              id: `st-rev-${nowId}-1`,
              title: "Análisis de Primeros Principios y Cotas Maestras",
              description: item.secretPrinciple,
              completed: false,
              starterKit: "Calibrador vernier, libreta de ingeniería y visor 3D de Prometheus.",
              actionFirst: "Revisar el Despiece 3D en la pestaña '2. 3D Despiece & Blueprints' y anotar las cotas críticas.",
              doneCriteria: "Dimensiones principales y relación de fuerzas validadas."
            }
          ]
        },
        simulationCad: {
          name: "simulationCad",
          label: "2. Simulación Física & CAD 3D",
          steps: [
            {
              id: `st-rev-${nowId}-2`,
              title: "Validación de Torque, Esfuerzo y Factor de Seguridad (FoS ≥ 2.0)",
              description: item.kinematicExplanation,
              completed: false,
              starterKit: "Simulador MechAgent (Pestaña Universidad) + FreeCAD / Bambu Studio.",
              actionFirst: "Verificar que el esfuerzo máximo bajo carga esté por debajo del límite elástico del material.",
              doneCriteria: "Modelo 3D libre de colisiones y con holguras de 0.20 mm."
            }
          ]
        },
        inventoryProcurement: {
          name: "inventoryProcurement",
          label: "3. Abasto Local PR & Control de Gastos",
          steps: [
            {
              id: `st-rev-${nowId}-3`,
              title: "Adquisición de Componentes y Registro de Gasto Real",
              description: "Adquirir los materiales preestipulados y anotar el precio real de recibo en la tabla financiera.",
              completed: false,
              starterKit: "Lista de Compras PR en el móvil.",
              actionFirst: "Verificar qué piezas ya tienes en el taller y comprar solo las faltantes.",
              doneCriteria: "Materiales en bancada dentro del presupuesto preestipulado."
            }
          ]
        },
        construction: {
          name: "construction",
          label: "4. Montaje Mecánico & Cableado 3D",
          steps: item.steps.map((stText, idx) => ({
            id: `st-rev-${nowId}-c-${idx}`,
            title: `Paso de Montaje ${idx + 1}: ${stText.slice(0, 58)}...`,
            description: stText,
            completed: false,
            starterKit: "Herramientas de precisión de taller, multímetro y guía de cableado 3D.",
            actionFirst: stText,
            doneCriteria: "Subsistema montado con firmeza, sin juego mecánico ni falsos contactos."
          }))
        },
        testingCalibration: {
          name: "testingCalibration",
          label: "5. Pruebas de Carga & Calibración",
          steps: [
            {
              id: `st-rev-${nowId}-5`,
              title: "Ensayo Funcional Progresivo (25% → 50% → 100% Carga)",
              description: "Verificar temperaturas, consumo de corriente y rigidez estructural en operación real.",
              completed: false,
              starterKit: "Multímetro True-RMS y cronómetro.",
              actionFirst: "Encender el sistema en vacío (sin carga) primero y luego aplicar carga nominal.",
              doneCriteria: "Funcionamiento suave, silencioso y dentro de temperatura segura (< 45°C)."
            }
          ]
        },
        analysisReview: {
          name: "analysisReview",
          label: "6. Documentación & Maestría",
          steps: [
            {
              id: `st-rev-${nowId}-6`,
              title: "Cierre de Presupuesto Real vs. Preestipulado y Bitácora",
              description: "Registrar el ahorro final logrado y las lecciones mecánicas aprendidas.",
              completed: false,
              starterKit: "Bitácora Técnica Prometheus.",
              actionFirst: "Tomar foto del prototipo terminado y guardar la entrada final.",
              doneCriteria: "Proyecto completado y conocimiento consolidado."
            }
          ]
        }
      },
      createdAt: new Date().toISOString().split("T")[0],
      updatedAt: new Date().toISOString().split("T")[0]
    };

    setProjectsList((prev) => [newProj, ...prev]);
    setActiveProjectId(newProj.id);
    setStudioView("phases");
    awardSkillMastery("estatica", 15, "Nuevo Proyecto por Ingeniería Inversa");
    toast.success(`🚀 Proyecto "${item.title}" creado con Despiece 3D, BOM PR y Pasos guiados.`);
  };

  // Export Blueprint 2D as SVG file
  const handleDownloadBlueprintSVG = () => {
    if (!activeProject) return;
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500" style="background:#071018;font-family:monospace">
      <rect width="800" height="500" fill="#071018" stroke="#0284c7" stroke-width="4"/>
      <g stroke="rgba(56,189,248,0.12)" stroke-width="1">
        ${Array.from({ length: 39 }).map((_, i) => `<line x1="${(i + 1) * 20}" y1="0" x2="${(i + 1) * 20}" y2="500"/>`).join("")}
        ${Array.from({ length: 24 }).map((_, i) => `<line x1="0" y1="${(i + 1) * 20}" x2="800" y2="${(i + 1) * 20}"/>`).join("")}
      </g>
      <text x="30" y="40" fill="#38bdf8" font-size="16" font-weight="bold">BLUEPRINT TÉCNICO ACOTADO — ${activeProject.title.toUpperCase()}</text>
      <text x="30" y="62" fill="#94a3b8" font-size="12">DISCIPLINA: ${activeProject.discipline} | PRESUPUESTO EST: $${activeProject.estimatedBudgetUSD.toFixed(2)} USD | NORMA: ASME Y14.5 / PR</text>
      <rect x="40" y="90" width="320" height="260" fill="none" stroke="#38bdf8" stroke-width="2"/>
      <text x="55" y="115" fill="#38bdf8" font-size="12" font-weight="bold">VISTA FRONTAL ORTOGONAL (ESCALA 1:2)</text>
      <rect x="95" y="260" width="210" height="35" fill="rgba(56,189,248,0.15)" stroke="#38bdf8" stroke-width="2"/>
      <rect x="145" y="170" width="110" height="90" fill="rgba(16,185,129,0.15)" stroke="#10b981" stroke-width="2"/>
      <circle cx="200" cy="215" r="28" fill="none" stroke="#f59e0b" stroke-width="2" stroke-dasharray="4 2"/>
      <line x1="95" y1="315" x2="305" y2="315" stroke="#e2e8f0" stroke-width="1.5"/>
      <text x="165" y="332" fill="#e2e8f0" font-size="11">180.0 ± 0.15 mm</text>
      <rect x="400" y="90" width="360" height="260" fill="none" stroke="#38bdf8" stroke-width="2"/>
      <text x="415" y="115" fill="#38bdf8" font-size="12" font-weight="bold">SECCIÓN DE SUBSISTEMAS Y TOLERANCIAS</text>
      ${projectSubsystems3D.map((s, idx) => `<text x="415" y="${150 + idx * 36}" fill="${s.color}" font-size="11">${s.name} (${s.dims})</text>`).join("")}
      <rect x="40" y="375" width="720" height="95" fill="#0b1926" stroke="#0284c7" stroke-width="2"/>
      <text x="55" y="402" fill="#f8fafc" font-size="13" font-weight="bold">PROYECTO: ${activeProject.title}</text>
      <text x="55" y="425" fill="#94a3b8" font-size="11">PRINCIPIO: ${activeProject.scientificPrinciple.slice(0, 90)}</text>
      <text x="55" y="448" fill="#10b981" font-size="11">FECHA: ${new Date().toLocaleDateString()} · GENERADO POR PROMETHEUS ENGINEERING OS (PUERTO RICO)</text>
    </svg>`;
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `blueprint_${activeProject.id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("📐 Blueprint vectorial (.SVG) descargado.");
  };

  // Export Parametric FreeCAD Python Script
  const handleDownloadFreeCADScript = () => {
    if (!activeProject) return;
    const pyScript = `# ============================================================================
# Script Paramétrico FreeCAD / Python — Generado por Prometheus Engineering OS
# Proyecto: ${activeProject.title}
# Tolerancia para Impresión / Mecanizado en PR: +0.20 mm holgura radial
# ============================================================================
import FreeCAD as App
import Part

doc = App.newDocument("${activeProject.id.replace(/[^a-zA-Z0-9]/g, "_")}")

# 1. Bancada / Chasis Base (180 x 140 x 16 mm)
base = Part.makeBox(180.0, 140.0, 16.0)
holes = [
    Part.makeCylinder(2.1, 20.0, App.Vector(12, 12, -1)),
    Part.makeCylinder(2.1, 20.0, App.Vector(168, 12, -1)),
    Part.makeCylinder(2.1, 20.0, App.Vector(12, 128, -1)),
    Part.makeCylinder(2.1, 20.0, App.Vector(168, 128, -1)),
]
for h in holes:
    base = base.cut(h)

base_obj = doc.addObject("Part::Feature", "Subsistema_1_Bancada_Base")
base_obj.Shape = base

# 2. Columna / Bloque Actuador Central
actuator = Part.makeCylinder(34.0, 44.0, App.Vector(90, 70, 16))
shaft_bore = Part.makeCylinder(4.1, 50.0, App.Vector(90, 70, 14)) # +0.1mm clearance for 8mm shaft
actuator = actuator.cut(shaft_bore)
act_obj = doc.addObject("Part::Feature", "Subsistema_2_Transmision")
act_obj.Shape = actuator

doc.recompute()
print("Modelo 3D paramétrico generado con éxito en FreeCAD.")
`;
    const blob = new Blob([pyScript], { type: "text/x-python" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `freecad_${activeProject.id}.py`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("🐍 Script paramétrico para FreeCAD (.py) descargado.");
  };

  const phaseTabsList: { key: keyof EngineeringProject["phases"]; label: string; icon: any }[] = [
    { key: "preparation", label: "1. Preparación & Normativas", icon: Compass },
    { key: "simulationCad", label: "2. Simulación & CAD 3D", icon: Layers },
    { key: "inventoryProcurement", label: "3. Inventario & BOM", icon: Package },
    { key: "construction", label: "4. Construcción & Fabricación", icon: Wrench },
    { key: "testingCalibration", label: "5. Pruebas & Calibración", icon: Activity },
    { key: "analysisReview", label: "6. Análisis & Lecciones", icon: ShieldCheck },
  ];

  return (
    <section id="proyectos-lifecycle" className="relative py-12 md:py-20 border-t border-border bg-background text-foreground transition-colors">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-6">
        
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-tech text-[10px] text-primary tracking-widest uppercase font-bold">Roadmap & Ciclo de Vida</span>
              <span className="h-px w-6 bg-primary" />
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-bold tracking-tight">
              Gestor de Proyectos <span className="text-primary italic font-normal">& Hoja de Ruta</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl font-sans leading-relaxed">
              Planifica, simula, construye y prueba tus inventos paso a paso con lista de materiales (BOM) e integración de insumos locales en Puerto Rico.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleCopyProjectSummary}
              className="px-3 py-2 bg-card border border-border text-foreground font-mono-tech text-xs uppercase font-bold rounded-lg shadow-xs hover:bg-muted flex items-center gap-1.5 transition-all cursor-pointer"
              title="Copiar Ficha Técnica Resumida del Proyecto"
            >
              <Copy className="w-3.5 h-3.5 text-primary" />
              <span className="hidden sm:inline">Copiar Ficha</span>
            </button>
            <button
              onClick={() => setIsCinematicModalOpen(true)}
              className="px-3 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 font-mono-tech text-xs uppercase font-bold rounded-lg shadow-sm hover:bg-purple-500/20 flex items-center gap-1.5 transition-all cursor-pointer"
              title="Generador de Render 3D & Video de Prototipos"
            >
              <Film className="w-3.5 h-3.5 text-purple-400" />
              <span className="hidden sm:inline">🎬 Render 3D</span>
            </button>
            <button
              onClick={() => setIsCreatorOpen(true)}
              className="px-4 py-2 bg-primary text-primary-foreground font-mono-tech text-xs uppercase font-bold rounded-lg shadow-md hover:opacity-90 flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Nuevo Proyecto / Idea</span>
            </button>
            <button
              onClick={exportFullProjectReport}
              className="px-3.5 py-2 bg-card border border-border text-foreground font-mono-tech text-xs uppercase font-bold rounded-lg shadow-sm hover:bg-muted flex items-center gap-1.5 transition-all cursor-pointer"
              title="Exportar Ficha Técnica en PDF"
            >
              <Printer className="w-3.5 h-3.5 text-primary" />
              <span className="hidden sm:inline">Exportar PDF</span>
            </button>
          </div>
        </div>

        {/* Status & Ecosystem Filters & Search Bar */}
        <div className="space-y-3 bg-card border border-border p-3.5 rounded-xl shadow-xs font-mono-tech">
          {/* Ecosystem Selector Row */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2.5">
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <span className="text-[9px] uppercase font-bold text-muted-foreground mr-1">Ecosistema:</span>
              <button
                onClick={() => setSelectedEcosystem("all")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  selectedEcosystem === "all"
                    ? "bg-foreground text-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                Todos ({projectsList.length})
              </button>

              <button
                onClick={() => setSelectedEcosystem("aura-os")}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  selectedEcosystem === "aura-os"
                    ? "bg-purple-600 text-white shadow-[0_0_12px_rgba(168,85,247,0.5)] border border-purple-400"
                    : "text-purple-400 border border-purple-500/30 hover:bg-purple-500/10"
                }`}
              >
                <span>🌌 Hardware Aura OS</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-black/30 font-bold">
                  {projectsList.filter((p) => p.ecosystem === "aura-os").length}
                </span>
              </button>

              <button
                onClick={() => setSelectedEcosystem("prometheus-lab")}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  selectedEcosystem === "prometheus-lab"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <span>🔥 Taller Prometheus</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-black/20 font-bold">
                  {projectsList.filter((p) => p.ecosystem !== "aura-os").length}
                </span>
              </button>
            </div>

            {/* Quick Search */}
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar proyecto o componente..."
                className="w-full pl-8 pr-3 py-1 bg-background border border-border rounded-lg text-xs font-mono-tech placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          {/* Status Tabs Row */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {[
              { id: "all", label: "🌐 Todos los Proyectos", count: projectsList.filter((p) => selectedEcosystem === "all" || (selectedEcosystem === "aura-os" ? p.ecosystem === "aura-os" : p.ecosystem !== "aura-os")).length },
              { id: "idea-future", label: "💡 Ideas & Planificación", count: projectsList.filter((p) => (selectedEcosystem === "all" || (selectedEcosystem === "aura-os" ? p.ecosystem === "aura-os" : p.ecosystem !== "aura-os")) && p.status === "idea-future").length },
              { id: "in-progress", label: "🔄 En Proceso", count: projectsList.filter((p) => (selectedEcosystem === "all" || (selectedEcosystem === "aura-os" ? p.ecosystem === "aura-os" : p.ecosystem !== "aura-os")) && p.status === "in-progress").length },
              { id: "completed", label: "🚀 Completados", count: projectsList.filter((p) => (selectedEcosystem === "all" || (selectedEcosystem === "aura-os" ? p.ecosystem === "aura-os" : p.ecosystem !== "aura-os")) && p.status === "completed").length },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setSelectedStatusTab(st.id as any)}
                className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                  selectedStatusTab === st.id
                    ? "bg-primary/20 text-primary border border-primary/40 shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <span>{st.label}</span>
                <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-black/20 font-bold">{st.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Grid: Project Sidebar Cards + Active Lifecycle Workbench */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Project Selector List with Mobile Collapsible Bar */}
          <div className="lg:col-span-4 space-y-3 font-mono-tech">
            {/* Mobile Project Selector Bar (Visible on mobile/tablet < lg) */}
            <div className="lg:hidden p-3 bg-card border border-primary/30 rounded-xl shadow-xs flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FolderKanban className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <span className="text-[9px] text-muted-foreground block uppercase font-bold">Proyecto Activo</span>
                  <h4 className="text-xs font-bold text-foreground truncate">{activeProject.title}</h4>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileProjectsOpen(!mobileProjectsOpen)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-mono-tech font-bold bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 flex items-center gap-1 shrink-0 transition-all cursor-pointer"
              >
                <span>{mobileProjectsOpen ? "Ocultar lista" : `Cambiar (${filteredProjects.length})`}</span>
                {mobileProjectsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>

            <div className={`${mobileProjectsOpen ? "block" : "hidden lg:block"} space-y-2.5 max-h-[700px] overflow-y-auto pr-1 transition-all duration-200`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider block">
                  Proyectos Registrados ({filteredProjects.length})
                </span>
                <span className="lg:hidden text-[10px] text-muted-foreground">Toca uno para seleccionarlo</span>
              </div>
              {filteredProjects.length === 0 && (
                <div className="p-8 text-center border border-dashed border-border rounded-xl text-xs text-muted-foreground">
                  No se encontraron proyectos bajo este filtro.
                </div>
              )}

              {filteredProjects.map((proj) => {
                const isSelected = proj.id === activeProject.id;
                const isAura = proj.ecosystem === "aura-os";
                const progress = calculateProjectProgress(proj);

                return (
                  <div
                    key={proj.id}
                    onClick={() => {
                      setActiveProjectId(proj.id);
                      setMobileProjectsOpen(false);
                    }}
                    className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2.5 group shadow-sm ${
                      isSelected
                        ? isAura 
                          ? "bg-card border-purple-500 ring-2 ring-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.25)]" 
                          : "bg-card border-primary ring-1 ring-primary/40 shadow-md"
                        : isAura
                        ? "bg-card border-purple-500/30 hover:border-purple-500/60"
                        : "bg-card border-border hover:bg-muted/30 hover:border-border"
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <div className="flex items-center gap-1">
                          {isAura && (
                            <span className="text-[7.5px] uppercase tracking-wider px-1.5 py-0.2 rounded font-bold bg-purple-500/20 text-purple-300 border border-purple-500/40">
                              🌌 Aura OS
                            </span>
                          )}
                          <span className={`text-[8px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${
                            proj.status === "in-progress"
                              ? "bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20"
                              : proj.status === "completed"
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                              : "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                          }`}>
                            {proj.status === "in-progress" ? "En Proceso" : proj.status === "completed" ? "Completado" : "Idea Futura"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[8.5px] text-muted-foreground font-bold">
                            ${proj.estimatedBudgetUSD.toFixed(0)} USD
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveProjectId(proj.id);
                              setIsCinematicModalOpen(true);
                            }}
                            className="p-1 rounded text-purple-400/70 hover:text-purple-300 hover:bg-purple-500/15 transition-colors cursor-pointer"
                            title={`Ver / Generar Prototipo 3D de "${proj.title}"`}
                          >
                            <Box className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(proj.id, proj.title);
                            }}
                            className="p-1 rounded text-muted-foreground/40 hover:text-red-400 hover:bg-red-500/15 transition-colors cursor-pointer"
                            title={`Eliminar "${proj.title}"`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <h4 className="text-xs font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {proj.title}
                      </h4>
                      <p className="text-[9.5px] text-muted-foreground font-sans line-clamp-2 leading-relaxed">
                        {proj.description}
                      </p>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1 pt-1 border-t border-border/40">
                      <div className="flex justify-between items-center text-[8.5px] text-muted-foreground">
                        <span>Progreso Fases</span>
                        <span className="font-bold text-foreground">{progress}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300 rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Active Project Details & 6-Phase Engineering Lifecycle */}
          <div className="lg:col-span-8 space-y-5">
            {activeProject ? (
              <div className="bg-card border border-border rounded-xl p-5 shadow-sm space-y-5">
                
                {/* Project Header Info */}
                <div className="space-y-3 pb-4 border-b border-border">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-mono-tech">
                    {/* Preestipulado */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-background border border-border">
                      <span className="text-muted-foreground text-[9.5px]">Preestipulado:</span>
                      <strong className="text-foreground font-bold">${activeProject.estimatedBudgetUSD.toFixed(2)}</strong>
                    </div>

                    {/* Real Facturado */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-background border border-border">
                      <span className="text-muted-foreground text-[9.5px]">Real Facturado:</span>
                      <strong className={`font-bold ${activeProjectActualCost > 0 ? "text-primary" : "text-muted-foreground"}`}>
                        ${activeProjectActualCost.toFixed(2)}
                      </strong>
                    </div>

                    {/* Variance or No Purchases Badge */}
                    {activeProjectActualCost > 0 ? (
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9.5px] font-bold border ${
                        activeProjectActualCost <= activeProject.estimatedBudgetUSD
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                          : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                      }`}>
                        {activeProjectActualCost <= activeProject.estimatedBudgetUSD ? (
                          <>Ahorro: +${(activeProject.estimatedBudgetUSD - activeProjectActualCost).toFixed(2)} ({((1 - activeProjectActualCost / (activeProject.estimatedBudgetUSD || 1)) * 100).toFixed(0)}%)</>
                        ) : (
                          <>Sobrecosto: -${(activeProjectActualCost - activeProject.estimatedBudgetUSD).toFixed(2)}</>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[9.5px] font-mono-tech bg-muted/40 text-muted-foreground border border-border/60">
                        Sin compras facturadas aún ($0.00)
                      </div>
                    )}

                    {/* Interactive Project Status Selector */}
                    <div className="flex items-center gap-1 bg-muted/50 p-0.5 rounded-lg border border-border">
                      <span className="text-[9px] text-muted-foreground uppercase font-bold px-1.5">Fase:</span>
                      <button
                        onClick={() => updateProjectStatus("idea-future")}
                        className={`px-2 py-0.5 rounded text-[9.5px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                          activeProject.status === "idea-future"
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/40 shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                        title="Establecer como Idea Futura en fase de diseño"
                      >
                        <Lightbulb className="w-3 h-3" />
                        <span>Idea Futura</span>
                      </button>
                      <button
                        onClick={() => updateProjectStatus("in-progress")}
                        className={`px-2 py-0.5 rounded text-[9.5px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                          activeProject.status === "in-progress"
                            ? "bg-orange-500/20 text-orange-400 border border-orange-500/40 shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                        title="Establecer como Proyecto en Proceso activo de construcción"
                      >
                        <Activity className="w-3 h-3" />
                        <span>En Proceso</span>
                      </button>
                      <button
                        onClick={() => updateProjectStatus("completed")}
                        className={`px-2 py-0.5 rounded text-[9.5px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                          activeProject.status === "completed"
                            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                        title="Marcar como Proyecto Completado"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Completado</span>
                      </button>
                    </div>

                    {/* Overall Progress */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 ml-auto">
                      <span className="text-muted-foreground text-[9.5px]">Progreso:</span>
                      <strong className="text-emerald-500 font-bold">{activeProgress}%</strong>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <h3 className="font-serif-display text-2xl md:text-3xl font-bold text-foreground">
                      {activeProject.title}
                    </h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openChatWithPrometheus()}
                        className="px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-mono-tech font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
                        title="Abrir chat para diseñar y trabajar este proyecto junto a Prometeo"
                      >
                        <Brain className="w-3.5 h-3.5" />
                        <span>💬 Trabajar con Prometeo</span>
                      </button>
                      <button
                        onClick={() => handleDeleteProject(activeProject.id, activeProject.title)}
                        className="px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 hover:text-red-400 border border-red-500/30 text-xs font-mono-tech font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
                        title={`Eliminar permanentemente el proyecto "${activeProject.title}"`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                    {activeProject.description}
                  </p>

                  {/* Scientific Principle & PR Resilience Callout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 font-mono-tech text-[10px]">
                    <div className="p-3 rounded-lg bg-background border border-border/80 space-y-1">
                      <span className="text-primary font-bold flex items-center gap-1">
                        🔬 Principio Científico / Físico
                      </span>
                      <p className="text-muted-foreground leading-relaxed text-[9.5px]">
                        {activeProject.scientificPrinciple}
                      </p>
                    </div>

                    <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-1">
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                        🇵🇷 Resiliencia & Normativas en Puerto Rico
                      </span>
                      <p className="text-muted-foreground leading-relaxed text-[9.5px]">
                        {activeProject.prResilienceNotes}
                      </p>
                    </div>
                  </div>

                  {/* Acciones Rápidas de Ingeniería para este Proyecto */}
                  <div className="bg-card/70 border border-primary/20 rounded-xl p-3.5 space-y-2.5 font-mono-tech shadow-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[10px] uppercase font-bold text-foreground tracking-wider">
                          Acciones Rápidas de Ingeniería para este Proyecto
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground">Suite Canónica Prometheus</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* 1. Herramienta CAD / Simulación Asignada */}
                      <Link
                        to={activeProject.cadToolLink || "/herramientas/cad_bridge"}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Abrir en Suite de CAD/CAM o Simulación técnica"
                      >
                        <Cpu className="w-3.5 h-3.5" />
                        <span>🛠️ Abrir en CAD / Simulación</span>
                      </Link>

                      {/* 2. ESP32 & Wokwi IoT */}
                      <Link
                        to="/herramientas/arduino"
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Programar firmware C++, simular en Wokwi y probar GPIOs"
                      >
                        <Zap className="w-3.5 h-3.5 text-blue-400" />
                        <span>⚡ Firmware & Wokwi</span>
                      </Link>

                      {/* 3. Faceplates & Carátulas */}
                      <Link
                        to="/herramientas/google_pics"
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Diseñar carátula vectorial o panel frontal para el gabinete"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-rose-400" />
                        <span>🏷️ Faceplate Gabinete</span>
                      </Link>

                      {/* 4. Hardware Web Serial MCP */}
                      <Link
                        to="/herramientas/mcp"
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Conectar por Web Serial USB para telemetría y control"
                      >
                        <Compass className="w-3.5 h-3.5 text-emerald-400" />
                        <span>🔌 Conexión USB Serial</span>
                      </Link>

                      {/* 5. BOM & Abasto Local PR */}
                      <button
                        type="button"
                        onClick={() => {
                          setStudioView("phases");
                          setActivePhaseTab("inventoryProcurement");
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Ver BOM y canales de abasto en Puerto Rico"
                      >
                        <Package className="w-3.5 h-3.5 text-amber-400" />
                        <span>📦 BOM & Abasto PR</span>
                      </button>

                      {/* 6. Consejo de Titanes */}
                      <Link
                        to="/herramientas/council"
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Consultar al Consejo de Titanes (Claude, Grok, Gemini, GPT) sobre este proyecto"
                      >
                        <Brain className="w-3.5 h-3.5 text-purple-400" />
                        <span>🏛️ Consejo de Titanes</span>
                      </Link>

                      {/* 7. Prototipo 3D */}
                      <button
                        type="button"
                        onClick={() => setIsCinematicModalOpen(true)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-card border border-border text-foreground hover:bg-muted transition-all flex items-center gap-1.5 shadow-xs cursor-pointer ml-auto"
                        title="Generar render cinematográfico o video 3D del prototipo"
                      >
                        <Film className="w-3.5 h-3.5 text-primary" />
                        <span>🎬 Prototipo 3D</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Studio View Switcher: 2 Vistas Claras (Fases de Ingeniería y Bitácora) */}
                <div className="flex items-center justify-between border-b border-border pb-3 flex-wrap gap-2">
                  <div className="flex flex-wrap items-center gap-1.5 bg-muted/40 border border-border p-1 rounded-xl shadow-xs">
                    <button
                      onClick={() => setStudioView("cad3d")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "cad3d"
                          ? "bg-card text-sky-400 border border-sky-500/50 shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <Box className="w-3.5 h-3.5 text-sky-400" />
                      <span>2. 3D Despiece · Montaje · Blueprints</span>
                    </button>

                    <button
                      onClick={() => setStudioView("wiring3d")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "wiring3d"
                          ? "bg-card text-amber-400 border border-amber-500/50 shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <Zap className="w-3.5 h-3.5 text-amber-400" />
                      <span>3. Conexiones Hardware 3D</span>
                    </button>

                    <button
                      onClick={() => setStudioView("university")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "university"
                          ? "bg-card text-emerald-400 border border-emerald-500/50 shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <Brain className="w-3.5 h-3.5 text-emerald-400" />
                      <span>4. Universidad Mecánica + IA</span>
                    </button>

                    <button
                      onClick={() => setStudioView("reverse")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "reverse"
                          ? "bg-card text-rose-400 border border-rose-500/50 shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-rose-400" />
                      <span>5. ¿Cómo lo hicieron? (Recrear)</span>
                    </button>

                    <button
                      onClick={() => setQuickExpenseOpen(!quickExpenseOpen)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        quickExpenseOpen
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 shadow-xs"
                          : "text-emerald-400 hover:bg-emerald-500/10"
                      }`}
                      title="Abrir/cerrar tabla rápida de Control de Gastos Reales vs. Preestipulados en cualquier momento"
                    >
                      <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                      <span>💰 Gastos en Vivo</span>
                    </button>
                    <button
                      onClick={() => setStudioView("phases")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "phases"
                          ? "bg-card text-foreground border border-border shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <Layers className="w-3.5 h-3.5 text-primary" />
                      <span>1. Fases del Proyecto (1-6)</span>
                    </button>

                    <button
                      onClick={() => setStudioView("journal")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        studioViewMode === "journal"
                          ? "bg-card text-purple-500 border border-border shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                      }`}
                    >
                      <BookOpen className="w-3.5 h-3.5 text-purple-400" />
                      <span>2. Bitácora Técnica de Avances</span>
                    </button>
                  </div>

                  <span className="text-[10px] font-mono-tech text-muted-foreground">
                    {studioViewMode === "phases" && `Fase ${phaseTabsList.findIndex((t) => t.key === activePhaseTab) + 1} de 6: ${currentPhaseData.label}`}
                    {studioViewMode === "journal" && `Bitácora de Campo (${projectLogs.length} entradas)`}
                  </span>
                </div>

                {/* 6-Phase Lifecycle Navigation Bar (Rendered when in phases mode) */}
                {studioViewMode === "phases" && (
                  <div className="space-y-4">
                    {/* Desktop / Tablet / Mobile Phase Tabs Carousel */}
                    <TabsSlider containerClassName="w-full">
                      {phaseTabsList.map((tab, idx) => {
                        const isActive = activePhaseTab === tab.key;
                        const phaseData = activeProject.phases[tab.key];
                        const totalSteps = phaseData.steps.length;
                        const doneSteps = phaseData.steps.filter((s) => s.completed).length;
                        const isComplete = totalSteps > 0 && doneSteps === totalSteps;

                        return (
                          <button
                            key={tab.key}
                            onClick={() => setActivePhaseTab(tab.key)}
                            className={`p-2.5 rounded-xl text-left transition-all border flex flex-col justify-between gap-1.5 cursor-pointer relative overflow-hidden flex-shrink-0 min-w-[160px] select-none ${
                              isActive
                                ? "bg-primary/10 border-primary shadow-sm"
                                : isComplete
                                ? "bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/60"
                                : "bg-muted/40 border-border hover:bg-muted/70 hover:border-border/80"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-base ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                                <tab.icon className="w-3.5 h-3.5" />
                              </span>
                              <span className="font-mono-tech text-[9px] font-bold text-muted-foreground">
                                {idx + 1}/6
                              </span>
                            </div>

                            <div>
                              <span className={`block font-mono-tech text-[10px] font-bold leading-tight truncate ${
                                isActive ? "text-primary" : "text-foreground"
                              }`}>
                                {tab.label.split(". ")[1] || tab.label}
                              </span>
                              <span className="font-mono-tech text-[8.5px] text-muted-foreground">
                                {doneSteps}/{totalSteps} pasos
                              </span>
                            </div>

                            {/* Mini Progress Bar */}
                            <div className="w-full bg-border/60 h-1 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${
                                  isComplete ? "bg-emerald-500" : "bg-primary"
                                }`}
                                style={{ width: `${totalSteps ? (doneSteps / totalSteps) * 100 : 0}%` }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </TabsSlider>

                  {/* Active Phase Details & Steps */}
                  <div className="bg-muted/30 border border-border/80 rounded-xl p-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
                      <div>
                        <h4 className="font-serif-display font-bold text-base text-foreground flex items-center gap-2">
                          <span>{currentPhaseData.label}</span>
                          {currentPhaseData.steps.every((s) => s.completed) && (
                            <span className="px-2 py-0.5 rounded text-[9px] font-mono-tech bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold border border-emerald-500/30">
                              ✓ COMPLETADA
                            </span>
                          )}
                        </h4>
                        <span className="text-[10px] font-mono-tech text-muted-foreground">
                          Marca cada paso de ingeniería para actualizar el progreso global y la bitácora técnica.
                        </span>
                      </div>

                      {/* Action buttons for this phase */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {activePhaseTab === "simulationCad" && (
                          <Link
                            to={activeProject.cadToolLink || "/herramientas/cad_bridge"}
                            className="px-3 py-1.5 rounded-lg text-xs font-mono-tech font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                            title="Abrir la herramienta de CAD/CAM asignada a este proyecto"
                          >
                            <Cpu className="w-3.5 h-3.5" />
                            <span>🛠️ Lanzar en Suite CAD</span>
                          </Link>
                        )}
                        <button
                          onClick={() =>
                            openChatWithPrometheus(
                              `Prometeo, quiero diseñar o modelar en 3D / CAD el proyecto "${activeProject.title}".
Genera el modelo paramétrico 3D, el plano de corte o ensamble y las instrucciones paso a paso para abrirlo en FreeCAD o Blender en Puerto Rico.`,
                              "viewer3d"
                            )
                          }
                          className="px-3 py-1.5 rounded-lg text-xs font-mono-tech font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/25 border border-amber-500/30 flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                          title="Diseñar el modelo 3D con Prometeo y visualizarlo en el Workbench"
                        >
                          <Box className="w-3.5 h-3.5 text-amber-500" />
                          <span>📐 Modelar en 3D con Prometeo</span>
                        </button>
                        <button
                          onClick={() =>
                            openChatWithPrometheus(
                              `Prometeo, explícame la fase "${currentPhaseData.label}" del proyecto "${activeProject.title}". ¿Qué decisiones clave debo tomar y cuál es el paso a paso práctico para avanzar?`
                            )
                          }
                          className="px-3 py-1.5 rounded-lg text-xs font-mono-tech font-bold bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30 flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                          title="Pedir orientación práctica sobre esta fase"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>💬 Consultar esta Fase</span>
                        </button>
                      </div>
                    </div>

                    {/* Active Focus Card ("Paso a Paso Inmediato") */}
                    {(() => {
                      const pendingStep = currentPhaseData.steps.find((s) => !s.completed);
                      const pendingStepIndex = pendingStep
                        ? currentPhaseData.steps.findIndex((s) => s.id === pendingStep.id)
                        : -1;

                      return pendingStep ? (
                        <div className="p-3.5 rounded-xl bg-gradient-to-r from-primary/10 via-card to-amber-500/10 border border-primary/30 space-y-2 shadow-xs">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded text-[9px] font-mono-tech uppercase font-bold bg-primary text-primary-foreground">
                                🎯 Acción Inmediata
                              </span>
                              <span className="text-xs font-bold text-foreground">
                                Paso {pendingStepIndex + 1}: {pendingStep.title}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => askPrometheusAboutStep(pendingStep, currentPhaseData.label)}
                              className="px-2.5 py-1 rounded-lg text-xs font-mono-tech font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                            >
                              <Brain className="w-3.5 h-3.5" />
                              <span>Explicar cómo arrancar este paso</span>
                            </button>
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-relaxed font-sans">
                            {pendingStep.description}
                          </p>

                          {(pendingStep.starterKit || pendingStep.actionFirst || pendingStep.doneCriteria) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10.5px] font-sans pt-1 border-t border-border/40">
                              {pendingStep.starterKit && (
                                <div className="p-2 rounded-lg bg-sky-500/15 border border-sky-500/30 space-y-0.5">
                                  <span className="font-bold text-sky-400 font-mono-tech text-[9px] uppercase tracking-wider block">
                                    1. Que necesitas a mano
                                  </span>
                                  <p className="text-foreground text-[10px] leading-relaxed">
                                    {pendingStep.starterKit}
                                  </p>
                                </div>
                              )}
                              {pendingStep.actionFirst && (
                                <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30 space-y-0.5">
                                  <span className="font-bold text-amber-400 font-mono-tech text-[9px] uppercase tracking-wider block">
                                    2. Accion Fisica Inmediata
                                  </span>
                                  <p className="text-foreground text-[10px] leading-relaxed">
                                    {pendingStep.actionFirst}
                                  </p>
                                </div>
                              )}
                              {pendingStep.doneCriteria && (
                                <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 space-y-0.5">
                                  <span className="font-bold text-emerald-400 font-mono-tech text-[9px] uppercase tracking-wider block">
                                    3. Como saber que terminaste
                                  </span>
                                  <p className="text-foreground text-[10px] leading-relaxed">
                                    {pendingStep.doneCriteria}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between text-[9.5px] font-mono-tech text-muted-foreground pt-1 border-t border-border/40">
                            <span>💡 Haz clic en 'Explicar cómo arrancar' para que Prometeo te guíe, o marca la casilla al terminarlo.</span>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                              ¡Fase completada! Todos los pasos técnicos están validados.
                            </span>
                          </div>
                          <span className="text-[10px] font-mono-tech text-muted-foreground">
                            Puedes pasar a la siguiente fase en la barra superior.
                          </span>
                        </div>
                      );
                    })()}

                    {/* Quick Steps Filter & Add Custom Step Action */}
                    <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                      <div className="flex items-center gap-1 font-mono-tech text-[10px] overflow-x-auto no-scrollbar">
                        <span className="text-muted-foreground font-bold mr-1">Filtrar:</span>
                        <button
                          type="button"
                          onClick={() => setStepFilter("all")}
                          className={`px-2 py-0.5 rounded cursor-pointer transition-colors ${
                            stepFilter === "all" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          Todos ({currentPhaseData.steps.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setStepFilter("pending")}
                          className={`px-2 py-0.5 rounded cursor-pointer transition-colors ${
                            stepFilter === "pending" ? "bg-amber-500 text-black font-bold" : "text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          Pendientes ({currentPhaseData.steps.filter((s) => !s.completed).length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setStepFilter("completed")}
                          className={`px-2 py-0.5 rounded cursor-pointer transition-colors ${
                            stepFilter === "completed" ? "bg-emerald-500 text-white font-bold" : "text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          Completados ({currentPhaseData.steps.filter((s) => s.completed).length})
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => setIsAddingStep(!isAddingStep)}
                        className="px-2.5 py-1 rounded-lg text-xs font-mono-tech font-bold bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 flex items-center gap-1 transition-all cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>{isAddingStep ? "Cancelar" : "Añadir Paso a esta Fase"}</span>
                      </button>
                    </div>

                    {/* Inline Form to Add Custom Step */}
                    {isAddingStep && (
                      <div className="p-3.5 rounded-xl bg-card border border-primary/40 space-y-3 font-mono-tech animate-in fade-in duration-200 shadow-xs">
                        <div className="flex items-center justify-between border-b border-border/40 pb-1.5 flex-wrap gap-2">
                          <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                            <Plus className="w-3.5 h-3.5 text-primary" /> Nuevo Paso Técnico en: {currentPhaseData.label}
                          </span>
                          <button
                            type="button"
                            onClick={handleAiGenerateStep}
                            disabled={isGeneratingStep}
                            className="px-2.5 py-1 text-[11px] font-mono-tech font-bold bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer shadow-xs disabled:opacity-50"
                            title="Generar paso técnico contextualizado automáticamente con IA"
                          >
                            <Sparkles className={`w-3.5 h-3.5 ${isGeneratingStep ? "animate-spin text-purple-400" : "text-purple-400"}`} />
                            <span>{isGeneratingStep ? "Generando con IA..." : "✨ Generar Paso con IA"}</span>
                          </button>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Título del paso (ej. Medir holgura de acople o verificar anclajes)..."
                            value={newStepTitle}
                            onChange={(e) => setNewStepTitle(e.target.value)}
                            className="w-full px-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="1. Qué necesitas a mano..."
                              value={newStepKit}
                              onChange={(e) => setNewStepKit(e.target.value)}
                              className="px-2.5 py-1 text-[11px] bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
                            />
                            <input
                              type="text"
                              placeholder="2. Acción concreta / paso 1..."
                              value={newStepAction}
                              onChange={(e) => setNewStepAction(e.target.value)}
                              className="px-2.5 py-1 text-[11px] bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
                            />
                            <input
                              type="text"
                              placeholder="3. Criterio de terminación..."
                              value={newStepDone}
                              onChange={(e) => setNewStepDone(e.target.value)}
                              className="px-2.5 py-1 text-[11px] bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setIsAddingStep(false)}
                            className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={handleAddCustomStep}
                            disabled={!newStepTitle.trim()}
                            className="px-3 py-1 text-xs font-bold bg-primary text-primary-foreground rounded-lg disabled:opacity-50 cursor-pointer"
                          >
                            Guardar Paso
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Steps Checklist */}
                    <div className="space-y-2.5">
                      {currentPhaseData.steps
                        .filter((step) => {
                          if (stepFilter === "pending") return !step.completed;
                          if (stepFilter === "completed") return step.completed;
                          return true;
                        })
                        .map((step, sIdx) => (
                        <div
                          key={step.id}
                          onClick={() => toggleStep(activePhaseTab, step.id)}
                          className={`p-3 rounded-lg border transition-all cursor-pointer flex items-start gap-3 ${
                            step.completed
                              ? "bg-emerald-500/5 border-emerald-500/30 text-foreground"
                              : "bg-background border-border hover:border-primary/50"
                          }`}
                        >
                          <button
                            type="button"
                            className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded flex items-center justify-center transition-colors ${
                              step.completed
                                ? "bg-emerald-500 text-white"
                                : "border border-muted-foreground/40 hover:border-primary"
                            }`}
                          >
                            {step.completed && <Check className="w-3 h-3 stroke-[3]" />}
                          </button>

                          <div className="space-y-1.5 flex-1">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold font-sans ${step.completed ? "line-through text-muted-foreground" : "text-foreground"}`}>
                                  {sIdx + 1}. {step.title}
                                </span>
                                {step.completed && (
                                  <span className="text-[8.5px] font-mono-tech text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                                    ✓ Hecho
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  askPrometheusAboutStep(step, currentPhaseData.label);
                                }}
                                className="px-2.5 py-1 rounded text-[10px] font-mono-tech font-bold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 flex items-center gap-1 transition-all cursor-pointer shadow-2xs hover:scale-102"
                                title="Preguntar a Prometeo qué significa y cómo hacer este paso en cristiano"
                              >
                                <HelpCircle className="w-3 h-3 text-primary" />
                                <span>¿Cómo hago este paso?</span>
                              </button>
                            </div>

                            <p className="text-[11px] text-muted-foreground leading-relaxed font-sans">
                              {step.description}
                            </p>

                            {/* Inline Socratic Physics Tutor & Quiz for this Step */}
                            <div className="flex flex-wrap items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setActiveSocraticStepId(activeSocraticStepId === step.id ? null : step.id)}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-mono-tech font-bold border transition-all cursor-pointer flex items-center gap-1.5 ${
                                  activeSocraticStepId === step.id
                                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
                                    : "bg-emerald-500/10 text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/20"
                                }`}
                              >
                                <Brain className="w-3 h-3" />
                                <span>{activeSocraticStepId === step.id ? "Ocultar Física & Quiz Socrático" : "🧠 ¿Por qué funciona? (Física Natural + Quiz)"}</span>
                              </button>
                            </div>

                            {activeSocraticStepId === step.id && (() => {
                              const soc = getStepSocraticPhysics(step, sIdx);
                              const chosen = socraticSelectedAnswer[step.id];
                              const isCorrect = chosen === soc.correctIdx;
                              return (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-2 p-3.5 rounded-xl bg-card border border-emerald-500/40 space-y-2.5 text-xs animate-in fade-in duration-150 shadow-sm"
                                >
                                  <div className="flex items-center justify-between border-b border-border/60 pb-1.5 flex-wrap gap-1">
                                    <span className="font-mono-tech text-[10px] font-bold text-emerald-400 uppercase">
                                      🎓 Tutor MechAgent · {soc.pillarLabel}
                                    </span>
                                    <span className="font-mono-tech text-[9.5px] text-sky-400">
                                      {soc.formula}
                                    </span>
                                  </div>

                                  <div className="p-2.5 rounded-lg bg-muted/40 border border-border text-[11px] leading-relaxed">
                                    <strong className="text-foreground">Intuición Física Natural: </strong>
                                    <span className="text-muted-foreground">{soc.naturalAnalogy}</span>
                                  </div>

                                  <div className="space-y-1.5 pt-1">
                                    <p className="font-bold text-foreground text-[11px]">
                                      ❓ Pregunta Socrática (Comprueba tu comprensión mientras construyes):
                                    </p>
                                    <p className="text-muted-foreground text-[11px]">{soc.question}</p>
                                    <div className="space-y-1.5 pt-1">
                                      {soc.options.map((opt, oIdx) => {
                                        const isPicked = chosen === oIdx;
                                        return (
                                          <button
                                            key={oIdx}
                                            type="button"
                                            onClick={() => {
                                              setSocraticSelectedAnswer((prev) => ({ ...prev, [step.id]: oIdx }));
                                              if (oIdx === soc.correctIdx && chosen !== soc.correctIdx) {
                                                awardSkillMastery(soc.pillar, 15, soc.pillarLabel);
                                              }
                                            }}
                                            className={`w-full text-left p-2 rounded-lg border text-[10.5px] transition-all cursor-pointer flex items-center justify-between gap-2 ${
                                              isPicked
                                                ? oIdx === soc.correctIdx
                                                  ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300 font-bold"
                                                  : "bg-rose-500/15 border-rose-500/50 text-rose-300"
                                                : "bg-background border-border hover:border-primary/40 text-foreground"
                                            }`}
                                          >
                                            <span>{String.fromCharCode(65 + oIdx)}. {opt}</span>
                                            {isPicked && (
                                              <span className="font-mono-tech text-[9px] shrink-0">
                                                {oIdx === soc.correctIdx ? "✓ Correcto (+15% XP)" : "✕ Intenta otra"}
                                              </span>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    {chosen !== undefined && (
                                      <div className={`p-2 rounded-lg text-[10.5px] mt-1 ${isCorrect ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>
                                        {isCorrect ? soc.explanation : "Piensa en la relación física de arriba e intenta otra opción."}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Practical Step Breakdown */}
                            {(step.starterKit || step.actionFirst || step.doneCriteria) && (
                              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10.5px] font-sans pt-1.5 border-t border-border/40">
                                {step.starterKit && (
                                  <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/25 space-y-1">
                                    <span className="font-bold text-sky-400 font-mono-tech text-[9.5px] flex items-center gap-1 uppercase tracking-wider">
                                      1. Que necesitas a mano
                                    </span>
                                    <p className="text-foreground/90 text-[10px] leading-relaxed">
                                      {step.starterKit}
                                    </p>
                                  </div>
                                )}
                                {step.actionFirst && (
                                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/25 space-y-1">
                                    <span className="font-bold text-amber-400 font-mono-tech text-[9.5px] flex items-center gap-1 uppercase tracking-wider">
                                      2. Accion concreta / Paso 1
                                    </span>
                                    <p className="text-foreground/90 text-[10px] leading-relaxed">
                                      {step.actionFirst}
                                    </p>
                                  </div>
                                )}
                                {step.doneCriteria && (
                                  <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25 space-y-1">
                                    <span className="font-bold text-emerald-400 font-mono-tech text-[9.5px] flex items-center gap-1 uppercase tracking-wider">
                                      3. Como sabes que terminaste
                                    </span>
                                    <p className="text-foreground/90 text-[10px] leading-relaxed">
                                      {step.doneCriteria}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}

                            {step.criticalPoints && (
                              <div className="mt-1 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[10px] font-mono-tech flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
                                <span><strong>Punto Crítico de Seguridad:</strong> {step.criticalPoints}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bill of Materials (BOM) & Local PR Procurement Section */}
                  {activePhaseTab === "inventoryProcurement" && (
                    <div className="bg-background border border-border p-4 rounded-xl space-y-3 font-mono-tech">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
                        <div>
                          <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                            <Package className="w-3.5 h-3.5 text-primary" /> Lista de Materiales (BOM) & Control de Costos (Preestipulado vs Real)
                          </span>
                          <span className="text-[9.5px] text-muted-foreground">
                            Edita el costo real facturado directamente para calcular varianzas y ahorros automáticamente.
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px]">
                          <button
                            type="button"
                            onClick={() => setIsShoppingListOpen(true)}
                            className="px-2.5 py-1 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/25 font-bold transition-all cursor-pointer flex items-center gap-1.5 text-[9px] shadow-xs"
                            title="Abrir modo interactivo de lista de compras para llevar a la ferretería"
                          >
                            <ShoppingCart className="w-3 h-3 text-emerald-500" />
                            <span>🛒 Lista de Compras (Ferretería PR)</span>
                          </button>

                          <button
                            onClick={handleVerifyAbastoPR}
                            disabled={isVerifyingAbastoPR}
                            className="px-2.5 py-1 rounded bg-sky-500/10 border border-sky-500/30 text-sky-400 hover:bg-sky-500/20 font-bold transition-all cursor-pointer flex items-center gap-1 text-[9px] disabled:opacity-50"
                            title="Consultar precios y stock en tiempo real en Puerto Rico"
                          >
                            <Search className={`w-3 h-3 ${isVerifyingAbastoPR ? "animate-spin" : ""}`} />
                            <span>{isVerifyingAbastoPR ? "Verificando..." : "🔎 Cotizar / Abasto PR"}</span>
                          </button>
                          <span>
                            Est. Total: <strong className="text-foreground">${activeProject.estimatedBudgetUSD.toFixed(2)}</strong>
                          </span>
                          <span>
                            Real Total: <strong className="text-primary font-bold">${activeProjectActualCost.toFixed(2)}</strong>
                          </span>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-[10px]">
                          <thead>
                            <tr className="border-b border-border text-muted-foreground uppercase text-[8px] tracking-wider">
                              <th className="pb-2">Material / Pieza</th>
                              <th className="pb-2">Cant.</th>
                              <th className="pb-2">Preestipulado</th>
                              <th className="pb-2">Real Facturado</th>
                              <th className="pb-2">Varianza</th>
                              <th className="pb-2">Proveedor / Canal (PR)</th>
                              <th className="pb-2 text-center">Stock Taller</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border/40">
                            {activeProject.bom.map((item) => {
                              const est = item.estimatedPriceUSD || 0;
                              const hasAct = item.actualPriceUSD !== undefined && item.actualPriceUSD > 0;
                              const act = hasAct ? item.actualPriceUSD! : 0;
                              const diff = est - act;

                              return (
                                <tr key={item.id} className="hover:bg-muted/40 transition-colors">
                                  <td className="py-2.5 font-bold text-foreground max-w-[180px]">
                                    {item.name}
                                    {item.specs && <span className="block text-[8px] text-muted-foreground font-normal truncate">{item.specs}</span>}
                                  </td>
                                  <td className="py-2.5 text-muted-foreground whitespace-nowrap">{item.quantity} {item.unit}</td>
                                  <td className="py-2.5 font-bold text-foreground">${est.toFixed(2)}</td>
                                  <td className="py-2.5">
                                    <div className="flex items-center gap-1">
                                      <span className="text-muted-foreground text-[9px]">$</span>
                                      <input
                                        type="number"
                                        step="0.50"
                                        min="0"
                                        value={item.actualPriceUSD !== undefined && item.actualPriceUSD > 0 ? item.actualPriceUSD : ""}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                          const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                          updateBOMPrice(item.id, "actualPriceUSD", isNaN(val) ? 0 : val);
                                        }}
                                        className="w-20 px-2 py-0.5 rounded bg-muted/60 border border-border text-[9.5px] font-mono-tech text-foreground font-bold focus:border-primary focus:outline-none"
                                      />
                                    </div>
                                  </td>
                                  <td className="py-2.5 whitespace-nowrap">
                                    {hasAct ? (
                                      <span className={`px-1.5 py-0.5 rounded text-[8.5px] font-bold ${
                                        diff >= 0 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-500"
                                      }`}>
                                        {diff >= 0 ? `+$${diff.toFixed(2)}` : `-$${Math.abs(diff).toFixed(2)}`}
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground text-[8px] italic">Sin compras</span>
                                    )}
                                  </td>
                                  <td className="py-2.5 text-muted-foreground">
                                    <span className="px-1.5 py-0.5 rounded bg-muted border border-border text-[8px] inline-block max-w-[140px] truncate">
                                      {item.localSupplierPR}
                                    </span>
                                  </td>
                                  <td className="py-2.5 text-center">
                                    <button
                                      onClick={() => toggleBOMStock(item.id)}
                                      className={`px-2 py-0.5 rounded text-[8.5px] font-bold cursor-pointer transition-all border ${
                                        item.inStock
                                          ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
                                          : "bg-amber-500/10 border-amber-500/20 text-amber-500 hover:bg-amber-500/20"
                                      }`}
                                      title={item.inStock ? "Pieza disponible en taller" : "Pieza pendiente por adquirir"}
                                    >
                                      {item.inStock ? "✓ EN STOCK" : "+ FALTANTE"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Key Results & Lessons Learned for Completed Projects */}
                  {activeProject.status === "completed" && activeProject.lessonsLearned && (
                    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 space-y-2 font-mono-tech text-[10px]">
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Lecciones Aprendidas & Registro de Éxito
                      </span>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground font-sans text-[10px]">
                        {activeProject.lessonsLearned.map((lesson, idx) => (
                          <li key={idx}>{lesson}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* ========================================================================= */}
              {/* ========================================================================= */}
              {/* FLOATING / INLINE LIVE EXPENSE TRACKER (PREESTIPULADO VS REAL)            */}
              {/* ========================================================================= */}
              {quickExpenseOpen && (
                <div className="p-4 rounded-xl bg-card border-2 border-emerald-500/40 space-y-3 font-mono-tech animate-in fade-in duration-150 shadow-md">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5">
                    <div>
                      <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" /> Control Financiero en Vivo (Preestipulado vs. Gasto Real)
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Actualiza en cualquier momento lo que gastas en Home Depot, National Lumber o USPS y controla tu presupuesto real.
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="px-2.5 py-1 rounded bg-background border border-border">
                        Preestipulado: <strong>${activeProject.estimatedBudgetUSD.toFixed(2)}</strong>
                      </span>
                      <span className="px-2.5 py-1 rounded bg-background border border-emerald-500/40 text-emerald-400">
                        Real Gastado: <strong>${activeProjectActualCost.toFixed(2)}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuickExpenseOpen(false)}
                        className="px-2 py-1 rounded bg-muted hover:bg-muted/80 text-muted-foreground text-xs cursor-pointer"
                      >
                        ✕ Cerrar
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                    {activeProject.bom.map((item) => {
                      const est = item.estimatedPriceUSD || 0;
                      const act = item.actualPriceUSD || 0;
                      return (
                        <div key={item.id} className="p-2.5 rounded-lg bg-background border border-border flex flex-col justify-between gap-2">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-xs font-bold text-foreground line-clamp-1">{item.name}</span>
                            <button
                              type="button"
                              onClick={() => toggleBOMStock(item.id)}
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 cursor-pointer ${
                                item.inStock ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/15 text-amber-400"
                              }`}
                            >
                              {item.inStock ? "✓ En Taller" : "Pendiente"}
                            </button>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                            <span>Preestipulado: <strong className="text-foreground">${est.toFixed(2)}</strong></span>
                            <span>{item.localSupplierPR}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/50">
                            <span className="text-[10px] text-sky-400 font-bold">Gasto Real ($):</span>
                            <input
                              type="number"
                              step="0.50"
                              min="0"
                              value={act > 0 ? act : ""}
                              placeholder={est.toFixed(2)}
                              onChange={(e) => {
                                const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                updateBOMPrice(item.id, "actualPriceUSD", isNaN(val) ? 0 : val);
                              }}
                              className="w-24 px-2 py-1 rounded bg-card border border-border text-xs font-bold text-foreground focus:border-emerald-500 focus:outline-none text-right"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ========================================================================= */}
              {/* 2. 3D DESPIECE EXPLOSIVO, SECUENCIA DE MONTAJE & BLUEPRINTS ACOTADOS       */}
              {/* ========================================================================= */}
              {studioViewMode === "cad3d" && (
                <div className="space-y-5 animate-in fade-in duration-200 font-mono-tech">
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-muted/30 border border-sky-500/30 p-3.5 rounded-xl">
                    <div>
                      <h4 className="text-sm font-bold text-sky-400 flex items-center gap-2">
                        <Box className="w-4 h-4" /> Visor 3D de Despiece Explosivo, Secuencia de Montaje & Blueprints
                      </h4>
                      <p className="text-xs text-muted-foreground font-sans">
                        Inspecciona cómo se ensambla cada subsistema en 3D, sus tolerancias milimétricas y exporta el plano técnico acotado.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCadSubView("exploded3d")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                          cadSubView === "exploded3d"
                            ? "bg-sky-500 text-white shadow-xs"
                            : "bg-card border border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        🧊 Despiece & Montaje 3D
                      </button>
                      <button
                        type="button"
                        onClick={() => setCadSubView("blueprint2d")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                          cadSubView === "blueprint2d"
                            ? "bg-sky-500 text-white shadow-xs"
                            : "bg-card border border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        📐 Blueprint 2D Acotado
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsCinematicModalOpen(true)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-500/15 text-purple-300 border border-purple-500/40 hover:bg-purple-500/25 cursor-pointer"
                      >
                        🎬 Render Realista 3D
                      </button>
                    </div>
                  </div>

                  {(activeProject?.id === "proj-mesa-l-htx" || activeProject?.title?.toLowerCase().includes("mesa en l")) && (
                    <div className="bg-[#071018] border border-cyan-500/40 rounded-xl overflow-hidden shadow-lg">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-cyan-500/10 border-b border-cyan-500/30">
                        <span className="text-xs font-bold text-cyan-300">
                          🌐 SIMULADOR 3D INTERACTIVO EN VIVO — MESA COMPLETA (72&quot; × 72&quot; × 28&quot;) + ENGRANAJES 24V (CARPETA 05) + BAHÍA CELULAR HTX (CARPETA 07)
                        </span>
                        <a
                          href="/visor_mesa_l.html"
                          target="_blank"
                          rel="noreferrer"
                          className="px-2.5 py-1 rounded-lg text-xs font-bold bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 hover:bg-cyan-500/30"
                        >
                          ↗ Abrir en Pantalla Completa
                        </a>
                      </div>
                      <iframe
                        src="/visor_mesa_l.html"
                        title="Visor 3D Mesa en L Completa"
                        className="w-full h-[680px] border-0 bg-[#090d16]"
                      />
                    </div>
                  )}
                  {cadSubView === "exploded3d" ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                      {/* Left 7 cols: Interactive 3D Isometric Exploded Canvas */}
                      <div className="lg:col-span-7 bg-[#071018] border border-sky-500/40 rounded-xl p-4 flex flex-col justify-between space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="text-sky-400 font-bold">
                            VISTA ISOMÉTRICA 3D · EXPLOSIÓN: {explodeFactor}% · ETAPA MONTAJE: {assemblyStepIdx + 1}/5
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setExplodeFactor(0)}
                              className="px-2 py-1 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40 text-xs cursor-pointer"
                            >
                              Ensamblar (0%)
                            </button>
                            <button
                              type="button"
                              onClick={() => setExplodeFactor(85)}
                              className="px-2 py-1 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs cursor-pointer"
                            >
                              Despiece (85%)
                            </button>
                          </div>
                        </div>

                        {/* Interactive 3D Isometric SVG */}
                        <div className="relative h-72 w-full flex items-center justify-center rounded-lg border border-sky-500/20 bg-[#050b11] overflow-hidden">
                          <svg viewBox="0 0 500 300" className="w-full h-full">
                            {/* Isometric Grid */}
                            <g stroke="rgba(56,189,248,0.1)" strokeWidth="1">
                              <line x1="50" y1="250" x2="450" y2="250" />
                              <line x1="100" y1="280" x2="400" y2="120" />
                              <line x1="400" y1="280" x2="100" y2="120" />
                              <line x1="250" y1="20" x2="250" y2="285" strokeDasharray="4 4" stroke="rgba(56,189,248,0.35)" />
                            </g>

                            {projectSubsystems3D.map((sub, idx) => {
                              const isMounted = idx <= assemblyStepIdx;
                              const isSelected = idx === selectedPartIdx;
                              const baseY = 215 - idx * 32;
                              const offset = (idx - 2) * (explodeFactor * 0.42);
                              const yPos = baseY - offset;

                              if (!isMounted) return null;

                              return (
                                <g
                                  key={sub.step}
                                  onClick={() => setSelectedPartIdx(idx)}
                                  className="cursor-pointer transition-all duration-300"
                                >
                                  {/* Isometric 3D Slab / Module */}
                                  <polygon
                                    points={`250,${yPos - 24} 345,${yPos} 250,${yPos + 24} 155,${yPos}`}
                                    fill={isSelected ? `${sub.color}44` : `${sub.color}22`}
                                    stroke={sub.color}
                                    strokeWidth={isSelected ? "3" : "1.8"}
                                  />
                                  <polygon
                                    points={`155,${yPos} 250,${yPos + 24} 250,${yPos + 40} 155,${yPos + 16}`}
                                    fill={`${sub.color}33`}
                                    stroke={sub.color}
                                    strokeWidth="1.5"
                                  />
                                  <polygon
                                    points={`345,${yPos} 250,${yPos + 24} 250,${yPos + 40} 345,${yPos + 16}`}
                                    fill={`${sub.color}18`}
                                    stroke={sub.color}
                                    strokeWidth="1.5"
                                  />
                                  {/* Callout Line & Label */}
                                  <line
                                    x1="345"
                                    y1={yPos + 6}
                                    x2="375"
                                    y2={yPos + 6}
                                    stroke={sub.color}
                                    strokeWidth="1.5"
                                  />
                                  <text
                                    x="380"
                                    y={yPos + 10}
                                    fill={isSelected ? "#ffffff" : sub.color}
                                    fontSize="11"
                                    fontWeight={isSelected ? "bold" : "normal"}
                                    fontFamily="monospace"
                                  >
                                    #{sub.step} {sub.name.slice(3, 25)}
                                  </text>
                                </g>
                              );
                            })}
                          </svg>
                        </div>

                        {/* Sliders for Explosion & Assembly Step */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-sky-500/20 text-xs">
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Despiece Explosivo (Separación 3D):</span>
                              <strong className="text-sky-400">{explodeFactor}%</strong>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={explodeFactor}
                              onChange={(e) => setExplodeFactor(Number(e.target.value))}
                              className="w-full accent-sky-500 cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Secuencia de Montaje Paso a Paso:</span>
                              <strong className="text-emerald-400">Etapa {assemblyStepIdx + 1} de 5</strong>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="4"
                              value={assemblyStepIdx}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setAssemblyStepIdx(val);
                                setSelectedPartIdx(val);
                              }}
                              className="w-full accent-emerald-500 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right 5 cols: Active Part / Subsystem Mechanical Inspector */}
                      <div className="lg:col-span-5 bg-card border border-border rounded-xl p-4 flex flex-col justify-between space-y-3">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between border-b border-border pb-2">
                            <span className="text-xs font-bold text-primary uppercase">
                              Inspector de Subsistema (Haz clic en pieza 3D)
                            </span>
                            <span className="text-xs font-bold text-sky-400">
                              Pieza {selectedPartIdx + 1} / 5
                            </span>
                          </div>

                          {/* Subsystem selector pills */}
                          <div className="flex flex-wrap gap-1.5">
                            {projectSubsystems3D.map((s, idx) => (
                              <button
                                key={s.step}
                                type="button"
                                onClick={() => {
                                  setSelectedPartIdx(idx);
                                  if (assemblyStepIdx < idx) setAssemblyStepIdx(idx);
                                }}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold border cursor-pointer transition-all ${
                                  selectedPartIdx === idx
                                    ? "bg-sky-500/20 text-sky-300 border-sky-500/50"
                                    : "bg-background border-border text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                Pieza #{s.step}
                              </button>
                            ))}
                          </div>

                          {(() => {
                            const part = projectSubsystems3D[selectedPartIdx] || projectSubsystems3D[0];
                            return (
                              <div className="p-3.5 rounded-xl bg-background border border-border space-y-2.5 text-xs">
                                <h5 className="font-bold text-sm text-foreground" style={{ color: part.color }}>
                                  {part.name}
                                </h5>
                                <p className="text-muted-foreground font-sans leading-relaxed">
                                  {part.role}
                                </p>
                                <div className="grid grid-cols-1 gap-1.5 pt-2 border-t border-border/60">
                                  <div>
                                    <span className="text-muted-foreground">📏 Cotas & Tolerancia: </span>
                                    <strong className="text-foreground">{part.dims}</strong>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">🧱 Material Recomendado PR: </span>
                                    <strong className="text-emerald-400">{part.material}</strong>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">🔩 Fijación / Montaje: </span>
                                    <strong className="text-amber-400">{part.fastener}</strong>
                                  </div>
                                  <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/30 mt-1">
                                    <span className="text-sky-400 font-bold block">🔬 Por qué funciona físicamente:</span>
                                    <span className="text-foreground font-sans">{part.physics}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                          <button
                            type="button"
                            onClick={handleDownloadBlueprintSVG}
                            className="flex-1 px-3 py-2 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 border border-sky-500/40 text-xs font-bold cursor-pointer"
                          >
                            📐 Descargar Blueprint (.SVG)
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadFreeCADScript}
                            className="flex-1 px-3 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 text-xs font-bold cursor-pointer"
                          >
                            🐍 Script FreeCAD (.PY)
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Blueprint 2D Orthographic View */
                    <div className="bg-[#071018] border-2 border-sky-500/50 rounded-xl p-5 space-y-4 text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky-500/30 pb-3">
                        <div>
                          <span className="text-sky-400 font-bold text-sm block">
                            PLANO DE INGENIERÍA ORTOGONAL (ASME Y14.5 · UNIDADES: MM / FT)
                          </span>
                          <span className="text-slate-400">
                            Proyecto: {activeProject.title} · Tolerancia general ±0.15 mm (FDM/CNC)
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleDownloadBlueprintSVG}
                            className="px-3 py-1.5 rounded-lg bg-sky-500 text-white font-bold cursor-pointer"
                          >
                            ⬇️ Exportar Plano .SVG
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadFreeCADScript}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-bold cursor-pointer"
                          >
                            ⬇️ Abrir en FreeCAD (.py)
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-sky-500/30 rounded-lg p-3 bg-[#050b11]">
                          <span className="text-sky-400 font-bold block mb-2">VISTA FRONTAL Y SECCIÓN A-A</span>
                          <svg viewBox="0 0 360 220" className="w-full h-48">
                            <rect x="40" y="160" width="280" height="28" fill="rgba(56,189,248,0.12)" stroke="#38bdf8" strokeWidth="2" />
                            <rect x="110" y="75" width="140" height="85" fill="rgba(16,185,129,0.12)" stroke="#10b981" strokeWidth="2" />
                            <circle cx="180" cy="118" r="28" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5 3" />
                            <line x1="180" y1="30" x2="180" y2="200" stroke="#ef4444" strokeWidth="1" strokeDasharray="6 3" />
                            <line x1="40" y1="205" x2="320" y2="205" stroke="#e2e8f0" strokeWidth="1.5" />
                            <text x="135" y="218" fill="#e2e8f0" fontSize="11" fontFamily="monospace">180.0 ± 0.15 mm</text>
                            <text x="260" y="115" fill="#10b981" fontSize="11" fontFamily="monospace">H = 88.0 mm</text>
                          </svg>
                        </div>

                        <div className="border border-sky-500/30 rounded-lg p-3 bg-[#050b11] space-y-2">
                          <span className="text-sky-400 font-bold block">TABLA DE SUBSISTEMAS Y ESPECIFICACIONES</span>
                          {projectSubsystems3D.map((s) => (
                            <div key={s.step} className="p-2 rounded border border-sky-500/20 flex items-center justify-between gap-2">
                              <div>
                                <span className="font-bold text-slate-100 block">{s.name}</span>
                                <span className="text-slate-400">{s.material} · {s.fastener}</span>
                              </div>
                              <span className="text-sky-400 font-bold shrink-0">{s.dims.split("·")[0]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ========================================================================= */}
              {/* 3. ENTRENADOR VISUAL DE CONEXIONES DE HARDWARE EN 3D & PINOUT             */}
              {/* ========================================================================= */}
              {studioViewMode === "wiring3d" && (
                <div className="space-y-5 animate-in fade-in duration-200 font-mono-tech">
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-muted/30 border border-amber-500/30 p-3.5 rounded-xl">
                    <div>
                      <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                        <Zap className="w-4 h-4" /> Entrenador Visual de Conexiones de Hardware en 3D
                      </h4>
                      <p className="text-xs text-muted-foreground font-sans">
                        Aprende cómo conectar potencia, tierra común, reguladores, microcontroladores y motores paso a paso sin quemar componentes.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIsSimulatingCurrent(!isSimulatingCurrent)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer border ${
                          isSimulatingCurrent
                            ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
                            : "bg-card border-border text-muted-foreground"
                        }`}
                      >
                        {isSimulatingCurrent ? "⚡ Flujo de Corriente Activo" : "⏸ Simulación Pausada"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveWireIdx(-1)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer border ${
                          activeWireIdx === -1
                            ? "bg-amber-500 text-black border-amber-400"
                            : "bg-card border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Ver Todos los Cables (6)
                      </button>
                    </div>
                  </div>

                  {/* 3D Isometric Hardware Bench SVG */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-7 bg-[#071018] border border-amber-500/40 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-amber-400 font-bold">BANCADA 3D DE INTERCONEXIÓN ELÉCTRICA</span>
                        <span className="text-slate-400">Haz clic en cualquier cable (1-6) para aprender su función</span>
                      </div>

                      <div className="h-64 w-full rounded-lg border border-slate-800 bg-[#050b11] p-2">
                        <svg viewBox="0 0 540 240" className="w-full h-full">
                          {/* Module 1: Battery LiFePO4 + Fuse */}
                          <rect x="20" y="35" width="115" height="70" rx="8" fill="#1e293b" stroke="#ef4444" strokeWidth="2" />
                          <text x="30" y="58" fill="#f8fafc" fontSize="11" fontWeight="bold">1. BATERÍA LiFePO4</text>
                          <text x="30" y="75" fill="#fca5a5" fontSize="10">12V/24V + Fusible 15A</text>
                          <text x="30" y="92" fill="#94a3b8" fontSize="9">[+VCC]          [-GND]</text>

                          {/* Module 2: Buck Converter DC-DC */}
                          <rect x="20" y="140" width="115" height="65" rx="8" fill="#1e293b" stroke="#f97316" strokeWidth="2" />
                          <text x="30" y="162" fill="#f8fafc" fontSize="11" fontWeight="bold">2. BUCK LM2596</text>
                          <text x="30" y="178" fill="#fdba74" fontSize="10">Baja 12V → 5.0V Lógicos</text>
                          <text x="30" y="194" fill="#94a3b8" fontSize="9">[OUT 5V]        [GND]</text>

                          {/* Module 3: ESP32-S3 Brain */}
                          <rect x="205" y="85" width="130" height="85" rx="8" fill="#0f172a" stroke="#38bdf8" strokeWidth="2.5" />
                          <text x="218" y="108" fill="#38bdf8" fontSize="12" fontWeight="bold">3. ESP32-S3 MCU</text>
                          <text x="218" y="125" fill="#e2e8f0" fontSize="10">Cerebro WiFi / Control</text>
                          <text x="218" y="142" fill="#fde047" fontSize="9">GPIO 5 (PWM) · I2C 21/22</text>
                          <text x="218" y="157" fill="#94a3b8" fontSize="9">VIN 5V · GND Común</text>

                          {/* Module 4: Driver MOSFET / Stepper + Motor / Sensor */}
                          <rect x="395" y="35" width="125" height="75" rx="8" fill="#1e293b" stroke="#eab308" strokeWidth="2" />
                          <text x="405" y="58" fill="#f8fafc" fontSize="11" fontWeight="bold">4. DRIVER POTENCIA</text>
                          <text x="405" y="74" fill="#fde047" fontSize="10">MOSFET / Puente-H</text>
                          <text x="405" y="92" fill="#94a3b8" fontSize="9">VIN+ · GND · PWM IN</text>

                          <rect x="395" y="140" width="125" height="65" rx="8" fill="#1e293b" stroke="#10b981" strokeWidth="2" />
                          <text x="405" y="162" fill="#f8fafc" fontSize="11" fontWeight="bold">5. MOTOR & I2C</text>
                          <text x="405" y="178" fill="#6ee7b7" fontSize="10">Actuador + Diodo Flyback</text>
                          <text x="405" y="194" fill="#38bdf8" fontSize="9">OLED / Sensor INA226</text>

                          {/* 6 Interactive 3D Wires */}
                          {/* Wire 1: +VCC High Power */}
                          {(activeWireIdx === -1 || activeWireIdx === 0) && (
                            <path
                              d="M 135 55 L 395 55"
                              fill="none"
                              stroke="#ef4444"
                              strokeWidth={activeWireIdx === 0 ? "4" : "2.5"}
                              strokeDasharray={isSimulatingCurrent ? "6 4" : undefined}
                            />
                          )}
                          {/* Wire 2: Star Common GND */}
                          {(activeWireIdx === -1 || activeWireIdx === 1) && (
                            <path
                              d="M 135 90 L 175 90 L 175 155 L 205 155 M 175 90 L 395 90"
                              fill="none"
                              stroke="#94a3b8"
                              strokeWidth={activeWireIdx === 1 ? "4" : "2.5"}
                            />
                          )}
                          {/* Wire 3: 5V Clean Logic */}
                          {(activeWireIdx === -1 || activeWireIdx === 2) && (
                            <path
                              d="M 135 165 L 205 148"
                              fill="none"
                              stroke="#f97316"
                              strokeWidth={activeWireIdx === 2 ? "4" : "2.5"}
                              strokeDasharray={isSimulatingCurrent ? "5 3" : undefined}
                            />
                          )}
                          {/* Wire 4: PWM / STEP Control */}
                          {(activeWireIdx === -1 || activeWireIdx === 3) && (
                            <path
                              d="M 335 120 L 395 82"
                              fill="none"
                              stroke="#eab308"
                              strokeWidth={activeWireIdx === 3 ? "4" : "2.5"}
                              strokeDasharray={isSimulatingCurrent ? "4 3" : undefined}
                            />
                          )}
                          {/* Wire 5: I2C Bus */}
                          {(activeWireIdx === -1 || activeWireIdx === 4) && (
                            <path
                              d="M 335 145 L 395 182"
                              fill="none"
                              stroke="#38bdf8"
                              strokeWidth={activeWireIdx === 4 ? "4" : "2.5"}
                              strokeDasharray={isSimulatingCurrent ? "4 4" : undefined}
                            />
                          )}
                          {/* Wire 6: Actuator Out + Flyback */}
                          {(activeWireIdx === -1 || activeWireIdx === 5) && (
                            <path
                              d="M 455 110 L 455 140"
                              fill="none"
                              stroke="#10b981"
                              strokeWidth={activeWireIdx === 5 ? "4.5" : "3"}
                              strokeDasharray={isSimulatingCurrent ? "6 3" : undefined}
                            />
                          )}
                        </svg>
                      </div>

                      {/* Step-by-Step Cable Selector Buttons */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {projectHardwareWires3D.map((w, idx) => (
                          <button
                            key={w.id}
                            type="button"
                            onClick={() => setActiveWireIdx(idx)}
                            className={`p-2 rounded-lg border text-left text-xs cursor-pointer transition-all ${
                              activeWireIdx === idx
                                ? "bg-amber-500/20 border-amber-400 text-white font-bold"
                                : "bg-card/70 border-border text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: w.color }} />
                            Cable {idx + 1}: {w.title.split(": ")[1]?.slice(0, 18)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Right 5 cols: Active Wire Electrical Lesson */}
                    <div className="lg:col-span-5 bg-card border border-border rounded-xl p-4 flex flex-col justify-between space-y-3">
                      {(() => {
                        const wire = projectHardwareWires3D[activeWireIdx >= 0 ? activeWireIdx : 0];
                        return (
                          <div className="space-y-3 text-xs">
                            <div className="flex items-center justify-between border-b border-border pb-2">
                              <span className="font-bold text-amber-400 uppercase">
                                Lección de Conexión #{activeWireIdx >= 0 ? activeWireIdx + 1 : 1} de 6
                              </span>
                              <span className="px-2 py-0.5 rounded bg-muted text-foreground font-bold">
                                {wire.gauge}
                              </span>
                            </div>

                            <h5 className="text-sm font-bold text-foreground" style={{ color: wire.color }}>
                              {wire.title}
                            </h5>

                            <div className="p-2.5 rounded-lg bg-background border border-border">
                              <span className="text-muted-foreground block text-[10px] uppercase">Ruta de Conexión Física:</span>
                              <strong className="text-foreground">{wire.from}</strong>
                              <span className="block text-sky-400 mt-1">Nivel Eléctrico: {wire.voltage}</span>
                            </div>

                            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-1 font-sans">
                              <span className="font-mono-tech text-[10px] font-bold text-emerald-400 uppercase block">
                                🧠 ¿Por qué se conecta así? (Física Eléctrica)
                              </span>
                              <p className="text-foreground leading-relaxed">{wire.whyItMatters}</p>
                            </div>

                            <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 space-y-1 font-sans">
                              <span className="font-mono-tech text-[10px] font-bold text-rose-400 uppercase block">
                                ⚠️ Error Crítico a Evitar en el Taller
                              </span>
                              <p className="text-foreground leading-relaxed">{wire.mistakeWarning}</p>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex justify-between gap-2 pt-2 border-t border-border">
                        <button
                          type="button"
                          onClick={() => setActiveWireIdx((prev) => (prev <= 0 ? 5 : prev - 1))}
                          className="px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 text-xs font-bold cursor-pointer"
                        >
                          ← Cable Anterior
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveWireIdx((prev) => (prev >= 5 ? 0 : prev + 1));
                            awardSkillMastery("mecatronica", 5, "Entrenamiento de Cableado 3D");
                          }}
                          className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-bold cursor-pointer"
                        >
                          Siguiente Cable →
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Interactive Pinout & Firmware Block */}
                  <div className="pt-2">
                    <InteractivePinoutDiagram compact={true} title={`Pinout & Firmware en Vivo — ${activeProject.title}`} />
                  </div>
                </div>
              )}

              {/* ========================================================================= */}
              {/* 4. UNIVERSIDAD PRÁCTICA DE INGENIERÍA + IA (MECHAGENT · VENTO · SKILLFALL) */}
              {/* ========================================================================= */}
              {studioViewMode === "university" && (
                <div className="space-y-5 animate-in fade-in duration-200 font-mono-tech">
                  <div className="bg-muted/30 border border-emerald-500/30 p-4 rounded-xl flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                        <Brain className="w-4 h-4" /> La Nueva Universidad Práctica de Ingeniería Mecánica + IA (MechAgent · Vento · SkillFall)
                      </h4>
                      <p className="text-xs text-muted-foreground font-sans">
                        Experimenta con las fuerzas reales de tu proyecto en vivo y desbloquea maestría natural en los 5 pilares de ingeniería.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left 7 cols: MechAgent + Vento AI Live Physics Simulator */}
                    <div className="lg:col-span-7 bg-card border border-border rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between border-b border-border pb-2">
                        <span className="text-xs font-bold text-sky-400 uppercase">
                          ⚙️ Simulador Físico en Vivo (MechAgent + Vento Force Flow)
                        </span>
                        <span className="text-xs text-muted-foreground">Proyecto: {activeProject.title.slice(0, 28)}</span>
                      </div>

                      {(() => {
                        const matSpecs = {
                          petg_cf: { name: "PETG Fibra Carbono (Impresión 3D)", yieldMPa: 52 },
                          alu_6061: { name: "Aluminio 6061-T6 Estructural", yieldMPa: 276 },
                          inox_316: { name: "Acero Inoxidable 316 Marino PR", yieldMPa: 290 },
                          concreto_4000: { name: "Hormigón Armado 4000 PSI + Varilla #5", yieldMPa: 85 }
                        }[mechMaterial];

                        const forceN = mechLoadKg * 9.81;
                        const armM = mechArmMm / 1000;
                        const requiredTorqueNm = forceN * armM;
                        const baseMotorTorqueNm = 1.4;
                        const deliveredTorqueNm = baseMotorTorqueNm * mechGearRatio * 0.88;
                        const bendingStressMPa = Number(((requiredTorqueNm * 1000) / 1450).toFixed(1));
                        const fos = Number((matSpecs.yieldMPa / Math.max(1, bendingStressMPa)).toFixed(2));
                        const torqueOk = deliveredTorqueNm >= requiredTorqueNm;

                        return (
                          <div className="space-y-4 text-xs">
                            {/* Material Selector */}
                            <div>
                              <span className="text-muted-foreground block mb-1.5">Material Estructural Seleccionado:</span>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                {[
                                  { id: "petg_cf", label: "PETG-CF (52 MPa)" },
                                  { id: "alu_6061", label: "Aluminio 6061 (276 MPa)" },
                                  { id: "inox_316", label: "Inox 316 PR (290 MPa)" },
                                  { id: "concreto_4000", label: "Hormigón Armado" }
                                ].map((m) => (
                                  <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMechMaterial(m.id as any)}
                                    className={`px-2 py-1.5 rounded-lg border text-[10.5px] font-bold cursor-pointer ${
                                      mechMaterial === m.id
                                        ? "bg-sky-500/20 text-sky-300 border-sky-500/50"
                                        : "bg-background border-border text-muted-foreground"
                                    }`}
                                  >
                                    {m.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Interactive Sliders */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-background p-3 rounded-xl border border-border">
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span>Reducción (i):</span>
                                  <strong className="text-sky-400">{mechGearRatio}:1</strong>
                                </div>
                                <input
                                  type="range"
                                  min="1"
                                  max="30"
                                  value={mechGearRatio}
                                  onChange={(e) => setMechGearRatio(Number(e.target.value))}
                                  className="w-full accent-sky-500 cursor-pointer"
                                />
                              </div>

                              <div>
                                <div className="flex justify-between mb-1">
                                  <span>Carga (Masa):</span>
                                  <strong className="text-amber-400">{mechLoadKg} kg ({forceN.toFixed(0)} N)</strong>
                                </div>
                                <input
                                  type="range"
                                  min="2"
                                  max="80"
                                  value={mechLoadKg}
                                  onChange={(e) => setMechLoadKg(Number(e.target.value))}
                                  className="w-full accent-amber-500 cursor-pointer"
                                />
                              </div>

                              <div>
                                <div className="flex justify-between mb-1">
                                  <span>Brazo Palanca:</span>
                                  <strong className="text-purple-400">{mechArmMm} mm</strong>
                                </div>
                                <input
                                  type="range"
                                  min="50"
                                  max="500"
                                  step="10"
                                  value={mechArmMm}
                                  onChange={(e) => setMechArmMm(Number(e.target.value))}
                                  className="w-full accent-purple-500 cursor-pointer"
                                />
                              </div>
                            </div>

                            {/* Telemetry Gauges */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                              <div className="p-2.5 rounded-lg bg-background border border-border">
                                <span className="text-[10px] text-muted-foreground block">Torque Requerido</span>
                                <strong className="text-sm text-foreground">{requiredTorqueNm.toFixed(1)} N·m</strong>
                              </div>
                              <div className="p-2.5 rounded-lg bg-background border border-border">
                                <span className="text-[10px] text-muted-foreground block">Torque con {mechGearRatio}:1</span>
                                <strong className={`text-sm ${torqueOk ? "text-emerald-400" : "text-rose-400"}`}>
                                  {deliveredTorqueNm.toFixed(1)} N·m
                                </strong>
                              </div>
                              <div className="p-2.5 rounded-lg bg-background border border-border">
                                <span className="text-[10px] text-muted-foreground block">Esfuerzo Flector (σ)</span>
                                <strong className="text-sm text-amber-400">{bendingStressMPa} MPa</strong>
                              </div>
                              <div className="p-2.5 rounded-lg bg-background border border-border">
                                <span className="text-[10px] text-muted-foreground block">Factor Seguridad</span>
                                <strong className={`text-sm ${fos >= 2.0 ? "text-emerald-400" : fos >= 1.2 ? "text-amber-400" : "text-rose-400"}`}>
                                  FoS = {fos}
                                </strong>
                              </div>
                            </div>

                            {/* Live Engineering Verdict */}
                            <div className={`p-3 rounded-xl border font-sans leading-relaxed ${
                              fos >= 2.0 && torqueOk
                                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                                : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                            }`}>
                              <strong>Diagnóstico del Profesor IA: </strong>
                              {fos >= 2.0 && torqueOk
                                ? `✓ Tu configuración con reducción ${mechGearRatio}:1 en ${matSpecs.name} entrega ${deliveredTorqueNm.toFixed(1)} N·m (supera los ${requiredTorqueNm.toFixed(1)} N·m requeridos) y mantiene un Factor de Seguridad de ${fos} (Límite elástico: ${matSpecs.yieldMPa} MPa).`
                                : !torqueOk
                                ? `⚠️ El par requerido (${requiredTorqueNm.toFixed(1)} N·m) supera el par entregado (${deliveredTorqueNm.toFixed(1)} N·m). Sube la relación de reducción (engranaje) o acorta el brazo de palanca.`
                                : `⚠️ El esfuerzo (${bendingStressMPa} MPa) está cerca del límite de ${matSpecs.name}. Cambia a Aluminio 6061 / Inox 316 o aumenta la sección transversal.`}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Right 5 cols: SkillFall AI Mastery Tree */}
                    <div className="lg:col-span-5 bg-card border border-border rounded-xl p-4 flex flex-col justify-between space-y-3">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between border-b border-border pb-2">
                          <span className="text-xs font-bold text-emerald-400 uppercase">
                            🌳 Árbol de Maestría Práctica (SkillFall AI)
                          </span>
                          <span className="text-xs text-muted-foreground">Aprende Haciendo</span>
                        </div>

                        <p className="text-xs text-muted-foreground font-sans">
                          Tu nivel sube automáticamente al resolver los quizzes socráticos de cada paso, practicar el cableado 3D y deconstruir mecanismos:
                        </p>

                        <div className="space-y-2.5 text-xs">
                          {[
                            { key: "estatica", label: "1. Estática, Torque & Resistencia de Materiales", color: "bg-sky-500" },
                            { key: "cinematica", label: "2. Cinemática, Engranajes & Tolerancias CAD", color: "bg-emerald-500" },
                            { key: "mecatronica", label: "3. Mecatrónica, Sensores & Ruteo Hardware 3D", color: "bg-amber-500" },
                            { key: "termofluidos", label: "4. Termofluidos, Hidráulica Venturi & Energía Solar", color: "bg-purple-500" },
                            { key: "arquitectura_pr", label: "5. Arquitectura Autosuficiente & Huracán PR (175 mph)", color: "bg-rose-500" }
                          ].map((skill) => {
                            const val = skillMastery[skill.key] || 45;
                            return (
                              <div key={skill.key} className="space-y-1">
                                <div className="flex justify-between text-[11px]">
                                  <span className="font-bold text-foreground">{skill.label}</span>
                                  <strong className="text-emerald-400">{val}%</strong>
                                </div>
                                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full ${skill.color} transition-all duration-300`} style={{ width: `${val}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          openChatWithPrometheus(
                            `Prometeo, actúa como mi profesor socrático de Ingeniería Mecánica e IA (estilo MechAgent + SkillFall). Explícame el principio físico de "${activeProject.title}" y hazme una pregunta práctica de diseño para poner a prueba mi intuición mecánica.`
                          )
                        }
                        className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold cursor-pointer"
                      >
                        🎓 Abrir Clase Socrática en Vivo con Prometeo
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ========================================================================= */}
              {/* 5. MOTOR DE INGENIERÍA INVERSA ("¿CÓMO LO HICIERON? → ANALIZAR Y RECREAR") */}
              {/* ========================================================================= */}
              {studioViewMode === "reverse" && (
                <div className="space-y-5 animate-in fade-in duration-200 font-mono-tech">
                  <div className="bg-muted/30 border border-rose-500/30 p-4 rounded-xl space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h4 className="text-sm font-bold text-rose-400 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" /> Motor de Ingeniería Inversa ("¿Cómo lo hicieron? → Analizar y Recrear")
                        </h4>
                        <p className="text-xs text-muted-foreground font-sans">
                          Escribe cualquier máquina, mecanismo o estructura que hayas visto, entiende su secreto físico y conviértelo en un proyecto real para recrearlo en tu taller.
                        </p>
                      </div>
                    </div>

                    {/* Custom Reverse Engineering Input */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={customReverseQuery}
                        onChange={(e) => setCustomReverseQuery(e.target.value)}
                        placeholder="Escribe qué quieres deconstruir y recrear (ej. Diferencial coaxial, Seguidor solar, Brazo cicloidad, Techo pluvial)..."
                        className="flex-1 px-3 py-2 rounded-xl bg-background border border-border text-xs text-foreground focus:border-rose-500 focus:outline-none font-sans"
                      />
                      <button
                        type="button"
                        onClick={handleAnalyzeCustomReverse}
                        className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold cursor-pointer shrink-0"
                      >
                        🔍 Analizar Cómo Funciona
                      </button>
                    </div>

                    {/* Preset Pills */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {REVERSE_ENGINEERING_CATALOG.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setCustomReverseResult(null);
                            setSelectedReverseIdx(idx);
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold border cursor-pointer transition-all ${
                            !customReverseResult && selectedReverseIdx === idx
                              ? "bg-rose-500/20 text-rose-300 border-rose-500/50"
                              : "bg-card border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {item.title.split(" (")[0]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active Reverse Engineering Breakdown Card */}
                  {(() => {
                    const rev = customReverseResult || REVERSE_ENGINEERING_CATALOG[selectedReverseIdx];
                    const savings = rev.commercialPriceUSD - rev.diyPricePR;
                    return (
                      <div className="bg-card border border-border rounded-xl p-5 space-y-4 text-xs">
                        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
                          <div>
                            <span className="text-[10px] text-rose-400 font-bold uppercase block">
                              Deconstrucción en Primeros Principios · {rev.category}
                            </span>
                            <h5 className="text-base font-bold text-foreground mt-0.5">{rev.title}</h5>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-1 rounded bg-background border border-border text-muted-foreground">
                              Comercial: <strong>${rev.commercialPriceUSD}</strong>
                            </span>
                            <span className="px-2.5 py-1 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-bold">
                              Recrear en Taller PR: ${rev.diyPricePR} (Ahorras ${savings})
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-sans">
                          <div className="p-3.5 rounded-xl bg-background border border-sky-500/30 space-y-1">
                            <span className="font-mono-tech text-[10px] font-bold text-sky-400 uppercase block">
                              1. El Secreto Físico / Por qué funciona tan bien
                            </span>
                            <p className="text-foreground leading-relaxed">{rev.secretPrinciple}</p>
                          </div>

                          <div className="p-3.5 rounded-xl bg-background border border-amber-500/30 space-y-1">
                            <span className="font-mono-tech text-[10px] font-bold text-amber-400 uppercase block">
                              2. Cinemática & Mecanismo Interno Paso a Paso
                            </span>
                            <p className="text-foreground leading-relaxed">{rev.kinematicExplanation}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <span className="font-bold text-foreground uppercase text-[10px] block">
                            3. Subsistemas para Recrearlo en Puerto Rico (BOM Preestipulado)
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {rev.subsystems.map((sub, i) => (
                              <div key={i} className="p-2.5 rounded-lg bg-background border border-border flex items-center justify-between gap-2">
                                <div>
                                  <strong className="text-foreground block">{sub.name}</strong>
                                  <span className="text-[10px] text-muted-foreground font-sans">{sub.spec} · {sub.supplierPR}</span>
                                </div>
                                <strong className="text-emerald-400 shrink-0">${sub.cost.toFixed(2)}</strong>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
                          <span className="text-muted-foreground font-sans">
                            Al presionar "Recrear en mi Taller", se crea como proyecto activo con sus 6 fases, Despiece 3D, Cableado y Control de Gastos.
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRecreateFromReverse(rev)}
                            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold cursor-pointer shadow-md flex items-center gap-2"
                          >
                            <span>🚀 Recrear en mi Taller (Generar Proyecto + BOM + 3D)</span>
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* 2. BITÁCORA TÉCNICA DE AVANCES, NOTAS DE CAMPO & ECO */}
              {/* ========================================================================= */}
              {studioViewMode === "journal" && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  {/* New Log Creator Form */}
                  <div className="p-5 rounded-2xl bg-card/60 border border-border/80 space-y-4 shadow-sm">
                    <div className="flex items-center justify-between border-b border-border/40 pb-2.5">
                      <h4 className="font-serif-display text-sm font-bold text-foreground flex items-center gap-2">
                        <Plus className="w-4 h-4 text-primary" /> Registrar Nueva Entrada en la Bitácora
                      </h4>
                      <span className="text-[9px] text-muted-foreground">
                        Proyecto: {activeProject.title}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[9px] uppercase text-muted-foreground font-bold block">Título del Avance o Suceso</label>
                        <input
                          type="text"
                          value={newLogTitle}
                          onChange={(e) => setNewLogTitle(e.target.value)}
                          placeholder="Ej. Ajuste de holgura radial a 0.15mm en impresión PETG..."
                          className="w-full bg-void border border-border p-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-sans"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[9px] uppercase text-muted-foreground font-bold block">Categoría</label>
                        <select
                          value={newLogCategory}
                          onChange={(e) => setNewLogCategory(e.target.value as any)}
                          className="w-full bg-void border border-border p-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary"
                        >
                          <option value="progreso">🚀 Avance / Fabricación</option>
                          <option value="medicion">📏 Medición / Calibración</option>
                          <option value="problema">⚠️ Obstáculo / Fallo</option>
                          <option value="leccion">💡 Lección Aprendida</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[9px] uppercase text-muted-foreground font-bold block">Detalle Técnico & Observaciones</label>
                      <textarea
                        rows={3}
                        value={newLogContent}
                        onChange={(e) => setNewLogContent(e.target.value)}
                        placeholder="Describe qué cambios realizaste, qué herramientas utilizaste y los resultados obtenidos..."
                        className="w-full bg-void border border-border p-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-sans leading-relaxed"
                      />
                    </div>

                    {/* Photo Attachment & Smart Sync Row */}
                    <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-border/40 text-[11px] font-mono-tech">
                      <div className="flex items-center gap-3">
                        <label className="px-2.5 py-1.5 rounded-lg bg-card border border-border hover:bg-muted text-foreground flex items-center gap-1.5 cursor-pointer transition-colors shadow-2xs">
                          <Camera className="w-3.5 h-3.5 text-primary" />
                          <span>{logPhotoUrl ? "📷 Cambiar Foto" : "📷 Adjuntar / Tomar Foto"}</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  setLogPhotoUrl(reader.result as string);
                                  toast.success("✓ Foto cargada a la entrada de bitácora.");
                                };
                                reader.readAsDataURL(f);
                              }
                            }}
                          />
                        </label>

                        {logPhotoUrl && (
                          <div className="flex items-center gap-1.5 p-1 rounded-lg bg-primary/10 border border-primary/20">
                            <img src={logPhotoUrl} alt="Preview avance" className="w-7 h-7 object-cover rounded" />
                            <button
                              type="button"
                              onClick={() => setLogPhotoUrl(null)}
                              className="p-1 text-red-400 hover:text-red-300 text-xs font-bold"
                              title="Quitar foto"
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        <label className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={smartUpdateProject}
                            onChange={(e) => setSmartUpdateProject(e.target.checked)}
                            className="rounded border-border accent-primary w-3.5 h-3.5 cursor-pointer"
                          />
                          <span className="text-[10px]">
                            🧠 Sincronizar automáticamente fases, pasos o repuestos con Prometeo
                          </span>
                        </label>
                      </div>

                      <button
                        onClick={handleAddProjectLog}
                        disabled={!newLogTitle.trim() || !newLogContent.trim()}
                        className="px-4 py-2 bg-primary text-primary-foreground font-bold text-xs rounded-xl shadow hover:scale-105 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                      >
                        <Bookmark className="w-3.5 h-3.5" />
                        <span>Guardar en Bitácora</span>
                      </button>
                    </div>
                  </div>

                  {/* Timeline of Logs */}
                  <div className="space-y-3">
                    <h5 className="font-mono-tech text-[10px] uppercase tracking-wider text-primary font-bold">
                      Historial de Bitácora ({projectLogs.length} registros)
                    </h5>

                    {projectLogs.length > 0 ? (
                      <div className="space-y-3">
                        {projectLogs.map((log: any) => (
                          <div key={log.id} className="p-4 rounded-xl bg-void/40 border border-border/70 space-y-2 group hover:border-primary/40 transition-all">
                            <div className="flex items-center justify-between flex-wrap gap-2 border-b border-border/30 pb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-[8px] uppercase font-bold ${
                                  log.category === "problema"
                                    ? "bg-red-500/15 text-red-500 border border-red-500/30"
                                    : log.category === "medicion"
                                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                                    : log.category === "leccion"
                                    ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                                    : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                                }`}>
                                  {log.category}
                                </span>
                                <h5 className="font-serif-display text-sm font-bold text-foreground">
                                  {log.title}
                                </h5>
                              </div>
                              <span className="text-[9px] text-muted-foreground">
                                {log.timestamp}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                              {log.content}
                            </p>
                            {log.photoUrl && (
                              <div className="pt-2">
                                <img
                                  src={log.photoUrl}
                                  alt={log.title}
                                  onClick={() => setViewingPhotoUrl(log.photoUrl)}
                                  className="max-h-44 rounded-xl object-cover border border-border/80 shadow-xs hover:scale-101 transition-transform cursor-pointer"
                                  title="Haz clic para ampliar la foto del avance"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center bg-void/30 border border-dashed border-border/60 rounded-xl space-y-2">
                        <p className="text-xs text-muted-foreground font-sans">
                          Aún no hay registros en la bitácora de este proyecto.
                        </p>
                        <span className="text-[9px] text-muted-foreground font-mono-tech">
                          Usa el formulario superior para documentar calibraciones, pruebas de ensamblaje o notas de taller.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center space-y-4 shadow-sm font-mono-tech">
              <div className="w-14 h-14 rounded-2xl bg-muted/60 border border-border flex items-center justify-center mx-auto text-muted-foreground shadow-sm">
                <FolderKanban className="w-7 h-7 text-primary/70" />
              </div>
              <div className="space-y-1.5">
                <h4 className="font-serif-display text-xl font-bold text-foreground">
                  No hay proyectos seleccionados
                </h4>
                <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed font-sans">
                  {projectsList.length === 0
                    ? "Has eliminado los proyectos del taller. Puedes crear una nueva idea personalizada usando el botón a continuación."
                    : "Selecciona un proyecto de la lista izquierda o ajusta los filtros para comenzar a diseñar."}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                <button
                  onClick={() => setIsCreatorOpen(true)}
                  className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-mono-tech text-xs uppercase font-bold flex items-center gap-1.5 shadow hover:opacity-90 transition-all cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Crear Nueva Idea / Proyecto</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* New Project / Idea Creator Modal */}
      {isCreatorOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 space-y-4 shadow-2xl animate-in fade-in zoom-in-95 font-mono-tech">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-serif-display text-lg font-bold text-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Crear Nuevo Proyecto / Idea
              </h3>
              <button
                onClick={() => setIsCreatorOpen(false)}
                className="text-muted-foreground hover:text-foreground text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-[10px] text-muted-foreground uppercase mb-1 font-bold">Título del Proyecto / Invento</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Ej. Brazo Robótico 4-DOF / Filtro Solar..."
                  className="w-full p-2.5 bg-background border border-border rounded-lg text-foreground focus:border-primary focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-muted-foreground uppercase mb-1 font-bold">Disciplina</label>
                  <select
                    value={newDiscipline}
                    onChange={(e) => setNewDiscipline(e.target.value as any)}
                    className="w-full p-2 bg-background border border-border rounded-lg text-foreground"
                  >
                    <option value="Mecánica & CAD">Mecánica & CAD</option>
                    <option value="Electrónica & IoT">Electrónica & IoT</option>
                    <option value="Energía & Solar">Energía & Solar</option>
                    <option value="Química & Materiales">Química & Materiales</option>
                    <option value="Robótica">Robótica</option>
                    <option value="Estructuras & Resiliencia PR">Estructuras & Resiliencia PR</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] text-muted-foreground uppercase mb-1 font-bold">Estado Inicial</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as any)}
                    className="w-full p-2 bg-background border border-border rounded-lg text-foreground"
                  >
                    <option value="in-progress">🔄 En Proceso</option>
                    <option value="idea-future">💡 Idea Futura</option>
                    <option value="completed">🚀 Completado</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] text-muted-foreground uppercase mb-1 font-bold">Descripción del Objetivo</label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="¿Qué hace el dispositivo y cuál es su meta principal?"
                  rows={2}
                  className="w-full p-2 bg-background border border-border rounded-lg text-foreground focus:border-primary focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-muted-foreground uppercase mb-1 font-bold">Presupuesto Est. ($ USD)</label>
                  <input
                    type="number"
                    value={newBudget}
                    onChange={(e) => setNewBudget(Number(e.target.value))}
                    className="w-full p-2 bg-background border border-border rounded-lg text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              <Button variant="outline" size="sm" onClick={() => setIsCreatorOpen(false)}>
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateProjectAI}
                disabled={!newTitle.trim() || isAiGenerating}
                className="bg-primary text-primary-foreground font-bold"
              >
                {isAiGenerating ? "Generando Fases..." : "Crear Proyecto con 6 Fases"}
              </Button>
            </div>
          </div>
        </div>
      )}



      {/* Shopping List Modal (Ferreterías de Puerto Rico) */}
      {isShoppingListOpen && activeProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-void/85 backdrop-blur-md font-mono-tech animate-in fade-in duration-150">
          <div className="w-full max-w-2xl bg-card border border-emerald-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 bg-void/90 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-500">
                  <ShoppingCart className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-serif-display text-sm sm:text-base font-bold text-foreground">
                    Lista de Compras de Taller (Ferretería PR)
                  </h3>
                  <p className="text-[10px] text-muted-foreground font-sans">
                    Proyecto: {activeProject.title}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsShoppingListOpen(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Store Filter Pills */}
            <div className="p-3 bg-card border-b border-border flex items-center gap-1.5 overflow-x-auto no-scrollbar text-xs">
              <span className="text-[9px] uppercase font-bold text-muted-foreground mr-1">Tienda:</span>
              {[
                { id: "all", label: "Todas las Tiendas" },
                { id: "National Lumber", label: "National Lumber" },
                { id: "Home Depot PR", label: "Home Depot PR" },
                { id: "Ace Hardware", label: "Ferreterías Ace" },
                { id: "Maderas 3C", label: "Maderas 3C" },
                { id: "USPS", label: "Mouser / Digi-Key / USPS" }
              ].map((st) => (
                <button
                  key={st.id}
                  type="button"
                  onClick={() => setShoppingSelectedStore(st.id)}
                  className={`px-2.5 py-1 rounded-lg font-bold text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    shoppingSelectedStore === st.id
                      ? "bg-emerald-500 text-white shadow-xs"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {st.label}
                </button>
              ))}
            </div>

            {/* Checklist items */}
            <div className="p-4 overflow-y-auto flex-1 space-y-2.5">
              {activeProject.bom
                .filter((it) => shoppingSelectedStore === "all" || it.localSupplierPR?.toLowerCase().includes(shoppingSelectedStore.toLowerCase()))
                .map((it) => {
                  const isChecked = shoppingCheckedItems[it.id] ?? it.inStock;
                  return (
                    <div
                      key={it.id}
                      onClick={() => setShoppingCheckedItems((prev) => ({ ...prev, [it.id]: !isChecked }))}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                        isChecked
                          ? "bg-emerald-500/10 border-emerald-500/40 text-foreground"
                          : "bg-void/40 border-border hover:border-emerald-500/30"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <button
                          type="button"
                          className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${
                            isChecked ? "bg-emerald-500 text-white" : "border border-muted-foreground/40"
                          }`}
                        >
                          {isChecked && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                        </button>
                        <div className="min-w-0">
                          <h5 className={`text-xs font-bold ${isChecked ? "line-through text-muted-foreground" : "text-foreground"}`}>
                            {it.name}
                          </h5>
                          <span className="text-[9.5px] text-muted-foreground font-sans block">
                            Cantidad: {it.quantity} {it.unit} · Proveedor: <strong className="text-foreground">{it.localSupplierPR}</strong>
                          </span>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-xs font-bold text-foreground">
                          ${(it.estimatedPriceUSD || 0).toFixed(2)}
                        </span>
                        {it.inStock && (
                          <span className="block text-[8.5px] text-emerald-500 font-bold">Ya en Taller</span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Actions Footer */}
            <div className="p-4 bg-void/90 border-t border-border flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs">
                <span className="text-muted-foreground">Comprados: </span>
                <strong className="text-emerald-500">
                  {Object.values(shoppingCheckedItems).filter(Boolean).length} / {activeProject.bom.length}
                </strong>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const lines = activeProject.bom.map((it) => {
                      const check = (shoppingCheckedItems[it.id] ?? it.inStock) ? "[X]" : "[ ]";
                      return `${check} ${it.quantity}x ${it.name} (~$${it.estimatedPriceUSD.toFixed(2)}) - ${it.localSupplierPR}`;
                    });
                    const msg = [
                      `🛒 LISTA DE COMPRAS EN FERRETERÍA PR`,
                      `Proyecto: ${activeProject.title}`,
                      `-----------------------------------`,
                      ...lines,
                      `Total Est: $${activeProject.estimatedBudgetUSD.toFixed(2)} USD`
                    ].join("\n");
                    navigator.clipboard.writeText(msg);
                    toast.success("✓ Lista formateada para WhatsApp copiada al portapapeles.");
                  }}
                  className="px-3 py-1.5 rounded-lg bg-card border border-border hover:bg-muted text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5 text-primary" />
                  <span>Copiar para WhatsApp</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setProjectsList((prevList) =>
                      prevList.map((p) => {
                        if (p.id !== activeProject.id) return p;
                        const updatedBOM = p.bom.map((it) => ({
                          ...it,
                          inStock: shoppingCheckedItems[it.id] !== undefined ? shoppingCheckedItems[it.id] : it.inStock
                        }));
                        return { ...p, bom: updatedBOM };
                      })
                    );
                    setIsShoppingListOpen(false);
                    toast.success("✓ Stock de materiales del proyecto sincronizado exitosamente.");
                  }}
                  className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Sincronizar con Stock de Taller</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Size Photo Viewer Modal */}
      {viewingPhotoUrl && (
        <div
          onClick={() => setViewingPhotoUrl(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md cursor-pointer animate-in fade-in duration-150"
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <img src={viewingPhotoUrl} alt="Foto ampliada avance" className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl border border-border" />
            <button
              onClick={() => setViewingPhotoUrl(null)}
              className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white hover:bg-black/90 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Prototype 3D Cinematic Render Modal */}
      {activeProject && (
        <PrototypeCinematicStudio
          isOpen={isCinematicModalOpen}
          onClose={() => setIsCinematicModalOpen(false)}
          projectTitle={activeProject.title}
          projectDescription={activeProject.description}
          ecosystem={activeProject.ecosystem}
          discipline={activeProject.discipline}
          bom={activeProject.bom}
        />
      )}
    </section>
  );
};
